import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, catchError, map, of, tap, throwError } from 'rxjs';
import {
  buildSolarRentSignatureDocumentId,
  buildSolarRentSignatureDocumentPreviewUrl,
  createSolarRentFinalSignatureDocumentConfig,
  createSolarRentDefaultForm,
  getSolarRentBatteryBrand,
  getSolarRentBatteryModel,
  getSolarRentInverterLabel,
  getSolarRentPackageObservations,
  getSolarRentMonitoringLabel,
  normalizeSolarRentOccupationStatusValue,
  normalizeSolarRentPeriodicPaymentModeValue,
  normalizeSolarRentPaymentFrequencyValue,
  SOLAR_RENT_PERIODIC_PAYMENT_MODE_VALUES,
  normalizeSolarRentTenantCivilityValue,
  SOLAR_RENT_BINARY_CHOICE_VALUES,
  SOLAR_RENT_DEFAULTS,
  SOLAR_RENT_SINGLE_SIGNATURE_FOR_ALL,
  SOLAR_RENT_PAYMENT_FREQUENCY_VALUES,
  type SolarRentBinaryChoiceValue,
  type SolarRentPaymentFrequencyValue,
  type SolarRentSignatureDocumentConfig,
  type SolarRentSignatureDocumentTemplatesPendingRequest,
  type SolarRentProductData,
  SOLAR_RENT_UPLOAD_DOCUMENT_CONFIG,
  SOLAR_RENT_DOCUMENT_STATUS_META_KEYS,
  type SolarRentDocumentStatusEntry,
  SOLAR_RENT_DOCUMENT_STATUS_BASELINE_ROWS,
  matchesSolarRentDocumentStatusBaselineRow,
} from './solar-rent-wizard.config';
import { SolarRentWizardAutofillService } from './solar-rent-wizard-autofill.service';
import { environment } from '../../../../../environments/environment';

export interface SolarRentOption {
  key: string;
  label: string;
}

export interface SolarRentDocumentDefinition {
  key: string;
  label: string;
}

export interface SolarRentContractCategoryPublicationResponse {
  success: boolean;
  already_sent: boolean;
  contract_id?: string | number | null;
  category_key?: string | null;
  category_label?: string | null;
  dashboard_url?: string | null;
  workflow_result?: Record<string, unknown> | null;
}

interface SolarRentContractTypeDefinition {
  id: number | null;
  name: string;
  code: string;
  label: string;
  productType: string;
  electricalEnvironment: string;
  productData: SolarRentProductData | null;
}

interface SolarRentProductTypeOption {
  id: number;
  code: string;
  name: string;
  productData: SolarRentProductData | null;
  active: boolean;
}

interface SolarRentElectricalEnvironmentOption {
  id: number;
  code: string;
  name: string;
  active: boolean;
}

const PRODUCT_LINKED_DOCUMENT_KEYS = ['single_line_diagram', 'equipment_datasheets'] as const;
type ProductLinkedDocumentKey = (typeof PRODUCT_LINKED_DOCUMENT_KEYS)[number];

export interface SolarRentStoredFile {
  name: string;
  url: string;
  file: File;
}

interface SolarRentPersistedStoredFile {
  name: string;
  url: string;
  mime_type?: string | null;
}

interface SolarRentUploadedSignatureAssetResponse {
  path?: unknown;
  url?: unknown;
  name?: unknown;
  mime_type?: unknown;
}

export interface SolarRentSignedDocumentSnapshot {
  title: string;
  fileName: string;
  contractId: string;
  fileReference: string;
  tenantName: string;
  tenantIdentityNumber: string;
  tenantAddress: string;
  siteAddress: string;
  generatedAt: string;
  signedDate: string;
  qrCodeData: string;
  qrCodeImageUrl: string;
  nicImageUrls: string[];
  signatureImageUrl: string;
}

type SolarRentPartyKey = 'landlord' | 'technician';

interface SolarRentDefaultSettingItem {
  section?: unknown;
  section_name?: unknown;
  value?: unknown;
}

interface SolarRentDeferredDocumentScopeSnapshot {
  documentFiles: Record<string, SolarRentStoredFile[]>;
  documentProductSelections: Partial<Record<ProductLinkedDocumentKey, string>>;
  otherDetails: string;
  documentsResetSinceTypeChange: boolean;
  documentsChanged: boolean;
  hasManualDocumentEdits: boolean;
}

interface SolarRentChecklistDocumentDefaultsScope {
  value: Record<string, any>;
  signature: string;
}

interface SolarRentPvMaterialCatalogItem {
  brand: string;
  model: string;
  unitPower: string;
  serialNumber: string;
}

interface SolarRentBatteryMaterialCatalogItem {
  brand: string;
  model: string;
  capacity: string;
  serialNumber: string;
}

interface SolarRentInverterMaterialCatalogItem {
  brand: string;
  model: string;
  nominalPower: string;
  serialNumber: string;
  location: string;
  type: 'hybrid' | 'other';
  otherTypeDetails: string;
}

const LEGACY_FORM_KEY_MAP: Record<string, string> = {
  reference_contrat: 'contractReference',
  reference_dossier: 'fileReference',
  client_locataire: 'tenantClient',
  document_language: 'documentLanguage',
  date_signature: 'signatureDate',
  date_installation: 'installationDate',
  date_mise_en_service: 'commissioningDate',
  bailleur: 'landlord',
  technicien: 'technician',
  locataire: 'tenant',
  denomination: 'legalName',
  companyName: 'legalName',
  legal_name: 'legalName',
  forme_sociale: 'legalForm',
  legal_form: 'legalForm',
  platform_application: 'platformApplication',
  adresse: 'address',
  represente_par: 'representedBy',
  represented_by: 'representedBy',
  qualite: 'title',
  civilite: 'civility',
  nom: 'name',
  nic_brn: 'identityNumber',
  telephone: 'phone',
  adresse_complete: 'fullAddress',
  type_bien: 'propertyType',
  statut_occupation: 'occupationStatus',
  gamme: 'range',
  ref_commerciale: 'commercialReference',
  env_sp: 'singlePhaseEnvironment',
  env_tp: 'threePhaseEnvironment',
  config_ref: 'referenceConfig',
  puissance_pv_prevue: 'plannedPvPower',
  capacite_batterie_prevue: 'plannedBatteryCapacity',
  onduleur_prevu: 'plannedInverter',
  monitoring_prevu_oui: 'plannedMonitoringYes',
  monitoring_prevu_non: 'plannedMonitoringNo',
  config_installee: 'installedConfig',
  synthese: 'summary',
  pv_prevu: 'plannedPv',
  pv_installe: 'installedPv',
  pv_conforme: 'pvCompliant',
  pv_adapte: 'pvAdapted',
  batt_prevu: 'plannedBattery',
  batt_installe: 'installedBattery',
  batt_conforme: 'batteryCompliant',
  batt_adapte: 'batteryAdapted',
  nb_modules_prevu: 'plannedModuleCount',
  nb_modules_installe: 'installedModuleCount',
  nb_modules_conforme: 'moduleCountCompliant',
  nb_modules_adapte: 'moduleCountAdapted',
  onduleur_installe: 'installedInverter',
  onduleur_conforme: 'inverterCompliant',
  onduleur_adapte: 'inverterAdapted',
  monitoring_prevu: 'plannedMonitoring',
  monitoring_installe: 'installedMonitoring',
  monitoring_conforme: 'monitoringCompliant',
  monitoring_adapte: 'monitoringAdapted',
  env_prevu: 'plannedEnvironment',
  env_installe: 'installedEnvironment',
  env_conforme: 'environmentCompliant',
  env_adapte: 'environmentAdapted',
  commentaire_general: 'generalComment',
  modules_pv: 'pvModules',
  marque: 'brand',
  modele: 'model',
  puissance_unitaire: 'unitPower',
  nombre_modules: 'moduleCount',
  puissance_totale: 'totalPower',
  numeros_serie: 'serialNumbers',
  onduleur: 'inverter',
  puissance_nominale: 'nominalPower',
  type_hybride: 'hybridType',
  type_autre: 'otherType',
  type_autre_precision: 'otherTypeDetails',
  numero_serie: 'serialNumber',
  emplacement: 'location',
  batterie: 'battery',
  capacite_nominale: 'nominalCapacity',
  capacite_utile: 'usableCapacity',
  nombre_unites: 'unitCount',
  protections_ac: 'acProtections',
  protections_dc: 'dcProtections',
  coffret_coupure: 'disconnectionBox',
  parafoudre: 'surgeProtection',
  cablage_principal: 'mainWiring',
  connectiques: 'connectors',
  interfaces_communication: 'communicationInterfaces',
  elements_fixation: 'mountingElements',
  signaletique: 'signage',
  autre: 'other',
  installe_oui: 'installedYes',
  installe_non: 'installedNo',
  connectivite_wifi: 'connectivityWifi',
  connectivite_4g: 'connectivity4g',
  connectivite_ethernet: 'connectivityEthernet',
  connectivite_autre: 'connectivityOther',
  connectivite_autre_precision: 'connectivityOtherDetails',
  compte_cree_oui: 'accountCreatedYes',
  compte_cree_non: 'accountCreatedNo',
  acces_remis_oui: 'accessProvidedYes',
  acces_remis_non: 'accessProvidedNo',
  test_remontee_oui: 'reportingTestYes',
  test_remontee_non: 'reportingTestNo',
  test_remontee_na: 'reportingTestNotApplicable',
  implantation: 'siteLayout',
  toiture_principale: 'mainRoof',
  toiture_secondaire: 'secondaryRoof',
  structure_dediee: 'dedicatedStructure',
  other_details: 'otherDetails',
  autre_precision: 'otherDetails',
  inclinaison: 'tilt',
  ombrages_oui: 'shadingYes',
  ombrages_non: 'shadingNo',
  equipements: 'equipment',
  emplacement_batterie: 'batteryLocation',
  emplacement_onduleur: 'inverterLocation',
  emplacement_coffrets: 'enclosureLocation',
  maintenance_satisfaisante: 'maintenanceSatisfactory',
  maintenance_a_surveiller: 'maintenanceNeedsMonitoring',
  maintenance_reserve: 'maintenanceReserved',
  ecarts: 'deviations',
  non: 'none',
  oui_mineurs: 'minor',
  oui_substantiels: 'substantial',
  mineurs: 'minor',
  substantiels: 'substantial',
  reserves: 'reservations',
  sans_reserve: 'withoutReservation',
  avec_mineures: 'withMinorReservations',
  avec_substantielles: 'withSubstantialReservations',
  mineures: 'minorReservations',
  substantielles: 'substantialReservations',
  actions: 'actions',
  mise_en_service_maintenue: 'commissioningMaintained',
  mise_en_service_partielle: 'partialCommissioning',
  mise_en_service_reportee: 'commissioningPostponed',
  avenant_requis: 'amendmentRequired',
  observations_complementaires: 'additionalObservations',
  bailleur_technicien: 'landlordTechnician',
  site_concerne: 'siteConcerned',
  cet_bis: 'cetBis',
  energy_framework_grid: 'energyFrameworkGrid',
  document_status: 'documentStatus',
};

const SOLAR_RENT_PARTY_SECTION_KEYS: Record<string, SolarRentPartyKey> = {
  bailleur_loueur: 'landlord',
  prestataire_technicien_installateur: 'technician',
};

const SOLAR_RENT_TECHNICAL_DEFAULT_SETTING_SECTION_KEYS = {
  pvModules: 'modules_photovoltaiques',
  battery: 'batterie_stockage_installee',
  inverter: 'onduleur_installe',
} as const;

const SOLAR_RENT_PARTY_DEFAULT_SETTINGS: Record<SolarRentPartyKey, Record<string, string>> = {
  landlord: {
    legal_name: 'Solar Rent LTD',
    legal_form: 'Domestic company limited by shares',
    address: 'Royal Road, Plaine Lauzun, Port-Louis, Maurice',
    phone: '',
    email: '',
    platformApplication: '',
    businessRegistrationNumber: 'C25226881',
    represented_by: 'Richard Biron',
    title: 'Directeur général',
  },
  technician: {
    legal_name: 'Solar Center LTD',
    legal_form: 'Domestic company limited by shares',
    address: 'Royal Road, Plaine Lauzun, Port-Louis, Maurice',
    phone: '',
    email: '',
    platformApplication: '',
    businessRegistrationNumber: 'C11100437',
    represented_by: 'Richard Biron',
    title: 'Directeur général',
  },
};

const SOLAR_RENT_SIGNATURE_TEXT_DEFAULT_SETTINGS: Record<SolarRentPartyKey, { name: string; title: string }> = {
  landlord: {
    name: SOLAR_RENT_DEFAULTS.form.contractDetails.signatures.landlord.name,
    title: SOLAR_RENT_DEFAULTS.form.contractDetails.signatures.landlord.title,
  },
  technician: {
    name: SOLAR_RENT_DEFAULTS.form.contractDetails.signatures.technician.name,
    title: SOLAR_RENT_DEFAULTS.form.contractDetails.signatures.technician.title,
  },
};

export interface SolarRentSignatureDocumentState {
  signatureMode: 'draw' | 'upload';
  nicFiles: SolarRentStoredFile[];
  nicRectoVersoConfirmed: boolean;
  signatureUploadFile: SolarRentStoredFile | null;
  drawnSignatureDataUrl: string;
  signatureDate: string;
  signedSnapshot: SolarRentSignedDocumentSnapshot | null;
  signedDocumentFile: SolarRentStoredFile | null;
}

interface SolarRentPersistedDocumentSignatureState {
  acknowledged: boolean;
  completed: boolean;
  signatureDate: string;
  signedSnapshot: SolarRentSignedDocumentSnapshot | null;
  signatureMode?: 'draw' | 'upload';
  drawnSignatureDataUrl?: string;
  signatureUploadFile?: SolarRentPersistedStoredFile | null;
  nicFiles?: SolarRentPersistedStoredFile[];
  nicRectoVersoConfirmed?: boolean;
  signedDocumentFile?: SolarRentPersistedStoredFile | null;
}

@Injectable({ providedIn: 'root' })
export class SolarRentWizardStateService {
  private readonly observationSignatureStateChangesSubject = new Subject<void>();
  readonly observationSignatureStateChanges$ = this.observationSignatureStateChangesSubject.asObservable();

  loadedContractId: string | null = null;
  private solarProjectLeadProductPayload: Record<string, any> | null = null;
  private persistedPvModuleCount: string | null = null;
  private readonly solarProjectLeadReadonlyValues = new Map<string, string>();
  loadedContractTypesBaseUrl: string | null = null;
  loadedProductTypesBaseUrl: string | null = null;
  loadedElectricalEnvironmentsBaseUrl: string | null = null;
  loadedDefaultSettingsBaseUrl: string | null = null;
  loadedSignatureDocumentTemplatesKey: string | null = null;
  solarRentAdvisor = {
    name: '',
    email: '',
    phone_number: '',
  };
  isLoadingContract = false;
  isLoadingContractTypes = false;
  isLoadingProductTypes = false;
  isLoadingElectricalEnvironments = false;
  isLoadingDefaultSettings = false;
  isLoadingSignatureDocumentTemplates = false;
  signatureDocumentTemplatesError = '';
  private pendingSignatureDocumentTemplatesRequest: SolarRentSignatureDocumentTemplatesPendingRequest | null = null;
  apiBaseUrl: string = '';
  isSavingPurposeStep = false;
  isFinalizingContract = false;
  isContractFinalized = false;
  loadContractError = '';
  savePurposeStepError = '';
  finalizeContractError = '';
  private documentsResetSinceTypeChange = false;
  private documentsChanged = false;
  private hasManualDocumentEdits = false;
  private pendingDocumentSelectionScopeChange = false;
  private documentChecklistDefaults: Record<string, any> | null = null;
  pvMaterialsCatalog: SolarRentPvMaterialCatalogItem[] = [];
  batteryMaterialsCatalog: SolarRentBatteryMaterialCatalogItem[] = [];
  inverterMaterialsCatalog: SolarRentInverterMaterialCatalogItem[] = [];
  customerServiceContact: { email: string; phone: string } = { email: '', phone: '' };
  private dismissedChecklistDefaultDocumentScopes: Partial<Record<ProductLinkedDocumentKey, string[]>> = {};
  private managerObservationSignatureDefaultsInitialized: Partial<Record<SolarRentPartyKey, boolean>> = {};
  private lastSavedPurposeStepPayload = '';
  private siteLocationBeforeEdit: {
    address: ReturnType<typeof createSolarRentDefaultForm>['address'];
    latitude: string;
    longitude: string;
  } | null = null;
  private pendingInitialPurposeStepSnapshotAfterTemplateLoad = false;
  private persistedDocumentSignatureStates: Record<string, SolarRentPersistedDocumentSignatureState> = {};
  private persistedSignatureDocumentGroupVisibility: Record<string, boolean> = {};
  private cachedProductTypeSelectOptions: Array<{ value: string; label: string }> = [];
  private cachedProductTypeSelectOptionsKey = '';
  private cachedElectricalEnvironmentSelectOptions: Array<{ value: string; label: string }> = [];
  private cachedElectricalEnvironmentSelectOptionsKey = '';
  private cachedSharedDocumentProductSelectOptions: Array<{ value: string; label: string }> = [];
  private cachedSharedDocumentProductSelectOptionsKey = '';

  srhRangeOptions: SolarRentOption[] = [];
  srhCommercialReferenceOptions: SolarRentOption[] = [];

  contractTypeOptions: string[] = [];
  private contractTypeDefinitions: SolarRentContractTypeDefinition[] = [];
  private productTypeOptions: SolarRentProductTypeOption[] = [];
  private electricalEnvironmentOptions: SolarRentElectricalEnvironmentOption[] = [];
  projectProductReference = '';
  projectProductGamme = '';
  productTypeLoadError = '';
  signatureDocumentDefinitions: SolarRentSignatureDocumentConfig[] = [];
  documentStatusFallbackTemplates: { key: string; code: string; label: string }[] = [];
  private lastSignatureDocumentBaseUrl: string | null = null;
  private lastSignatureDocumentToken: string | null = null;

  readonly contractDocumentDefinitions: SolarRentDocumentDefinition[] =
    SOLAR_RENT_UPLOAD_DOCUMENT_CONFIG.map(({ key, label }) => ({ key, label }));

  readonly documentFiles: Record<string, SolarRentStoredFile[]> = {};
  readonly documentProductSelections: Record<string, string> = {};
  readonly acknowledgedSignatureDocuments: Record<string, boolean> = {};
  readonly signatureDocuments: Record<string, SolarRentSignatureDocumentState> = {};

  readonly form = createSolarRentDefaultForm();

  get srhRangeOptionKeys(): string[] {
    return this.srhRangeOptions.map(({ key }) => key);
  }

  get srhCommercialReferenceOptionKeys(): string[] {
    return this.srhCommercialReferenceOptions.map(({ key }) => key);
  }

  get hasUnsavedPurposeStepChanges(): boolean {
    if (this.loadedContractId === null || this.isLoadingContract) {
      return false;
    }

    return this.serializePurposeStepPayload(this.buildPurposeStepPayload()) !== this.lastSavedPurposeStepPayload;
  }

  beginSiteAddressEdit(): void {
    if (this.siteLocationBeforeEdit !== null) {
      return;
    }

    this.siteLocationBeforeEdit = {
      address: { ...this.form.address },
      latitude: this.form.latitude,
      longitude: this.form.longitude,
    };
  }

  savePreviousSiteLocationOnNavigation(): void {
    const previousLocation = this.siteLocationBeforeEdit;
    this.siteLocationBeforeEdit = null;

    if (!previousLocation) {
      return;
    }

    if (!this.isSiteAddressEqual(previousLocation.address)) {
      this.form.contractDetails.parties.site.previousAddress = previousLocation.address;
    }

    if (!this.isSiteCoordinatesEqual(previousLocation)) {
      this.form.contractDetails.parties.site.previousCoordinates = {
        latitude: previousLocation.latitude,
        longitude: previousLocation.longitude,
      };
    }
  }

  hasGpsChangesSinceSiteLocationEntry(): boolean {
    return !!this.siteLocationBeforeEdit && (
      this.siteLocationBeforeEdit.latitude !== this.form.latitude
      || this.siteLocationBeforeEdit.longitude !== this.form.longitude
    );
  }

  resetGpsToSiteLocationEntry(): void {
    if (!this.siteLocationBeforeEdit) {
      return;
    }

    this.form.latitude = this.siteLocationBeforeEdit.latitude;
    this.form.longitude = this.siteLocationBeforeEdit.longitude;
  }

  private isSiteAddressEqual(previousAddress: NonNullable<typeof this.siteLocationBeforeEdit>['address']): boolean {
    return Object.keys(previousAddress).every((key) =>
      previousAddress[key as keyof typeof previousAddress]
        === this.form.address[key as keyof typeof this.form.address],
    );
  }

  private isSiteCoordinatesEqual(previousLocation: NonNullable<typeof this.siteLocationBeforeEdit>): boolean {
    return previousLocation.latitude === this.form.latitude
      && previousLocation.longitude === this.form.longitude;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly autofillService: SolarRentWizardAutofillService,
  ) {
    for (const definition of this.contractDocumentDefinitions) {
      this.documentFiles[definition.key] = [];
    }
    for (const key of PRODUCT_LINKED_DOCUMENT_KEYS) {
      this.documentProductSelections[key] = '';
    }
    this.setSignatureDocumentDefinitions([]);

    this.syncDerivedConfiguration();
  }

  autofill(): void {
    if (this.isContractFinalized) {
      return;
    }

    this.autofillService.fill(this);
    console.log('[SolarRentWizard] Autofilled data', {
      form: this.form,
      documentFiles: this.documentFiles,
      signatureDocuments: this.signatureDocuments,
    });
  }

  get siteCompleteAddress(): string {
    const primaryParts = [
      this.form.address.number,
      this.form.address.street,
      this.form.address.city,
      this.form.address.postcode,
    ]
      .map((value) => value.trim())
      .filter(Boolean);

    if (primaryParts.length === 0) {
      return '';
    }

    const country = this.form.address.country.trim();
    return [...primaryParts, ...(country ? [country] : [])].join(', ');
  }

  get selectedProductData(): SolarRentProductData | null {
    const resolvedProductTypeCode = this.resolveSelectedProductTypeCode(this.form.contractType)
      || this.getDefaultProductTypeCode();

    return this.findContractTypeDefinition(this.form.contractType)?.productData
      ?? this.resolveProductTypeProductData(resolvedProductTypeCode)
      ?? null;
  }

  get selectedProductLabel(): string {
    return this.getContractTypeLabel(this.form.contractType);
  }

  get selectedProductTypeCode(): string {
    const resolved = this.resolveSelectedProductTypeCode(this.form.contractType);
    if (resolved !== '') {
      return resolved;
    }

    return this.getDefaultProductTypeCode();
  }

  get isContractProductLocked(): boolean {
    return this.loadedContractId !== '' && this.projectProductReference !== '';
  }

  get isSolarRentProductReadonly(): boolean {
    return this.solarProjectLeadProductPayload !== null || this.isContractProductLocked;
  }

  isSolarRentProductFieldReadonly(fieldKey: string, value: unknown): boolean {
    const normalizedValue = this.normalizeSolarRentProductValue(value);

    return normalizedValue !== null && this.solarProjectLeadReadonlyValues.get(fieldKey) === normalizedValue;
  }

  isAnySolarRentProductFieldReadonly(...fieldKeys: string[]): boolean {
    return fieldKeys.some((fieldKey) => this.solarProjectLeadReadonlyValues.has(fieldKey));
  }

  get availableProductTypeCodes(): string[] {
    if (this.productTypeLoadError !== '') {
      return [];
    }

    const options = this.productTypeOptions
      .filter((option) => option.code !== '' && option.active !== false)
      .map((option) => option.code);

    if (options.length > 0) {
      return options;
    }

    const fallback = this.contractTypeDefinitions
      .map(({ productType }) => this.toText(productType))
      .filter((code) => code !== '')
      .filter((code, index, values) => values.indexOf(code) === index);

    return fallback;
  }

  get productTypeSelectOptions(): Array<{ value: string; label: string }> {
    const cacheKey = this.buildProductTypeSelectOptionsCacheKey();

    if (cacheKey === this.cachedProductTypeSelectOptionsKey) {
      return this.cachedProductTypeSelectOptions;
    }

    this.cachedProductTypeSelectOptionsKey = cacheKey;
    this.cachedProductTypeSelectOptions = this.buildAvailableProductTypeEntries().map((entry) => ({
      value: entry.code,
      label: entry.name || entry.code,
    }));

    return this.cachedProductTypeSelectOptions;
  }

  get electricalEnvironmentSelectOptions(): Array<{ value: string; label: string }> {
    const cacheKey = this.buildElectricalEnvironmentSelectOptionsCacheKey();

    if (cacheKey === this.cachedElectricalEnvironmentSelectOptionsKey) {
      return this.cachedElectricalEnvironmentSelectOptions;
    }

    this.cachedElectricalEnvironmentSelectOptionsKey = cacheKey;
    this.cachedElectricalEnvironmentSelectOptions = this.buildAvailableElectricalEnvironmentEntries().map((entry) => ({
      value: entry.code,
      label: entry.name || entry.code,
    }));

    return this.cachedElectricalEnvironmentSelectOptions;
  }

  isElectricalEnvironmentSelected(code: string): boolean {
    return this.normalizeElectricalEnvironmentCode(code) === this.getSelectedEnvironmentCode();
  }

  setSelectedElectricalEnvironmentCode(code: string, checked: boolean): void {
    if (this.isContractFinalized || this.isContractProductLocked) {
      return;
    }

    if (!checked) {
      return;
    }

    const normalizedCode = this.normalizeElectricalEnvironmentCode(code);
    if (normalizedCode === '') {
      return;
    }

    this.setElectricalEnvironment(
      normalizedCode === 'TP' ? 'threePhaseEnvironment' : 'singlePhaseEnvironment',
      true,
    );
  }

  getProductTypeLabel(code: string): string {
    const normalized = this.toText(code);
    if (normalized === '') {
      return '';
    }

    const resolved = this.productTypeOptions.find((option) => option.code === normalized)?.name;
    if (resolved) {
      return resolved;
    }

    const spaced = normalized.replace(/_/g, ' ').trim();
    return spaced !== '' ? spaced : normalized;
  }

  setSelectedProductTypeCode(productTypeCode: string): void {
    if (this.isContractFinalized || this.isContractProductLocked) {
      return;
    }

    const normalizedProductTypeCode = this.toText(productTypeCode).toUpperCase();
    const environmentCode = this.getSelectedEnvironmentCode();
    const resolvedContractType = this.findContractTypeByProductTypeAndEnvironment(
      normalizedProductTypeCode,
      environmentCode,
    );

    if (resolvedContractType) {
      this.setProductType(resolvedContractType.name);
      return;
    }

    this.setProductType(normalizedProductTypeCode);
  }

  get selectedReferenceRangeLabel(): string {
    return this.firstText(
      this.form.contractDetails.referenceConfig.range,
      this.getSelectedRangeOption()?.label,
      this.selectedProductData?.label,
      this.findProductTypeOption(this.selectedProductTypeCode)?.name,
    );
  }

  get selectedReferenceRangeKey(): string {
    return this.getSelectedRangeOption()?.key ?? this.selectedProductData?.rangeKey ?? '';
  }

  get referenceRangeSelectOptions(): Array<{ value: string; label: string }> {
    const fallbackKey = this.selectedReferenceRangeKey;
    const fallbackLabel = this.selectedReferenceRangeLabel;
    const options = this.srhRangeOptions.map(({ key, label }) => ({ value: key, label }));

    if (
      fallbackKey !== ''
      && fallbackLabel !== ''
      && !options.some((option) => option.value === fallbackKey)
    ) {
      return [{ value: fallbackKey, label: fallbackLabel }, ...options];
    }

    return options;
  }

  get selectedReferenceCommercialLabel(): string {
    return this.firstText(
      this.form.contractDetails.referenceConfig.commercialReference,
      this.getSelectedCommercialReferenceOption()?.label,
      this.buildCommercialReferenceKey(this.selectedProductData, this.getSelectedEnvironmentCode()),
    ).toUpperCase();
  }

  get selectedReferenceCommercialKey(): string {
    return this.getSelectedCommercialReferenceOption()?.key
      ?? this.buildCommercialReferenceKey(this.selectedProductData, this.getSelectedEnvironmentCode());
  }

  get referenceCommercialSelectOptions(): Array<{ value: string; label: string }> {
    const fallbackKey = this.selectedReferenceCommercialKey;
    const fallbackLabel = this.selectedReferenceCommercialLabel;
    const options = this.srhCommercialReferenceOptions.map(({ key, label }) => ({ value: key, label }));

    if (
      fallbackKey !== ''
      && fallbackLabel !== ''
      && !options.some((option) => option.value === fallbackKey)
    ) {
      return [{ value: fallbackKey, label: fallbackLabel }, ...options];
    }

    return options;
  }

  get selectedReferenceMonitoringLabel(): string {
    if (this.form.contractDetails.referenceConfig.plannedMonitoringYes) {
      return 'Oui';
    }

    if (this.form.contractDetails.referenceConfig.plannedMonitoringNo) {
      return 'Non';
    }

    if (this.selectedProductData?.monitoringIncluded === true) {
      return 'Oui';
    }

    if (this.selectedProductData?.monitoringIncluded === false) {
      return 'Non';
    }

    return '';
  }

  get selectedReferencePlannedPvPower(): string {
    return this.firstText(
      this.form.contractDetails.referenceConfig.plannedPvPower,
      this.selectedProductData?.totalPvPower,
    );
  }

  get selectedReferencePlannedBatteryCapacity(): string {
    return this.firstText(
      this.form.contractDetails.referenceConfig.plannedBatteryCapacity,
      this.selectedProductData?.batteryCapacity,
    );
  }

  get selectedReferencePlannedInverterLabel(): string {
    return this.firstText(
      this.form.contractDetails.referenceConfig.plannedInverter,
      getSolarRentInverterLabel(this.selectedProductData),
    );
  }

  setReferenceRangeSelection(value: string, checked = true): void {
    if (this.isContractFinalized) {
      return;
    }

    this.form.contractDetails.referenceConfig.range = checked ? this.toText(value) : '';
  }

  setReferenceCommercialSelection(value: string, checked = true): void {
    if (this.isContractFinalized) {
      return;
    }

    this.form.contractDetails.referenceConfig.commercialReference = checked ? this.toText(value) : '';
  }

  setReferenceMonitoringSelection(selectedKey: 'plannedMonitoringYes' | 'plannedMonitoringNo', checked: boolean): void {
    if (this.isContractFinalized) {
      return;
    }

    this.setExclusiveSelection(
      this.form.contractDetails.referenceConfig,
      selectedKey,
      ['plannedMonitoringYes', 'plannedMonitoringNo'],
      checked,
    );
  }

  setReferenceAssociatedInfrastructureSelection(
    selectedValue: 'yes' | 'no' | 'technical_annex',
    checked: boolean,
  ): void {
    if (this.isContractFinalized || !checked) {
      return;
    }

    this.form.contractDetails.referenceConfig.associatedInfrastructurePlanned =
      selectedValue === 'yes' ? true : selectedValue === 'no' ? false : null;
    this.form.contractDetails.referenceConfig.associatedInfrastructureAsPerTechnicalAnnex =
      selectedValue === 'technical_annex';
  }

  setProductType(contractType: string): void {
    if (this.isContractFinalized || this.isContractProductLocked) {
      return;
    }

    this.updateDocumentSelectionScope(() => {
      this.form.contractType = this.normalizeContractTypeValue(this.toText(contractType));
      this.syncDerivedConfiguration();
      this.loadCurrentSignatureDocumentTemplates();
    });
  }

  getContractTypeLabel(contractType: string): string {
    return this.findContractTypeDefinition(contractType)?.label ?? this.toText(contractType);
  }

  getContractTypeDefinition(contractType: string): SolarRentContractTypeDefinition | null {
    return this.findContractTypeDefinition(contractType);
  }

  getCommercialReferenceKeyForProduct(contractType: string, environmentCode: 'SP' | 'TP'): string {
    const productData = this.findContractTypeDefinition(contractType)?.productData;
    return this.buildCommercialReferenceKey(productData, environmentCode);
  }

  savePurposeStep(contractId: string, baseUrl: string, token?: string | null): Observable<Record<string, any> | null> {
    if (this.isContractFinalized) {
      return of(null);
    }

    const normalizedContractId = String(contractId).trim();
    const normalizedBaseUrl = this.resolveContractsApiBaseUrl(baseUrl);
    const deferredDocumentScopeSnapshot = this.applyPendingDocumentSelectionScopeChange();
    const payload = this.buildPurposeStepPayload();

    this.apiBaseUrl = normalizedBaseUrl || this.apiBaseUrl;
    this.isSavingPurposeStep = true;
    this.savePurposeStepError = '';

    const headers = this.buildAuthorizationHeaders(token);

    return this.http
      .put<{ data?: Record<string, any> } | Record<string, any>>(
        `${normalizedBaseUrl}/contracts/${normalizedContractId}`,
        payload,
        { headers },
      )
      .pipe(
        tap({
          next: (response) => {
            const contract = this.unwrapContractResponse(response);

            if (contract) {
              this.applyContract(contract);
              this.loadedContractId = normalizedContractId;
            }

            this.pendingDocumentSelectionScopeChange = false;
            this.documentsResetSinceTypeChange = false;
            this.documentsChanged = false;
            this.hasManualDocumentEdits = false;
            this.captureCurrentPurposeStepPayload();
            this.isSavingPurposeStep = false;
          },
          error: (error) => {
            this.restoreDeferredDocumentSelectionScopeChange(deferredDocumentScopeSnapshot);
            console.error('[SolarRentWizardStateService] Failed to save purpose step', error);
            this.savePurposeStepError = 'Unable to save contract details.';
            this.isSavingPurposeStep = false;
          },
        }),
      );
  }

  finalizeContract(contractId: string, baseUrl: string, token?: string | null): Observable<Record<string, any> | null> {
    const normalizedContractId = String(contractId).trim();
    const normalizedBaseUrl = this.resolveContractsApiBaseUrl(baseUrl);

    if (!normalizedContractId || !normalizedBaseUrl) {
      return of(null);
    }

    this.apiBaseUrl = normalizedBaseUrl || this.apiBaseUrl;
    this.isFinalizingContract = true;
    this.finalizeContractError = '';

    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;

    return this.http
      .post<{ data?: Record<string, any> } | Record<string, any>>(
        `${normalizedBaseUrl}/contracts/${normalizedContractId}/finalize`,
        {},
        { headers },
      )
      .pipe(
        tap({
          next: (response) => {
            const contract = this.unwrapContractResponse(response);

            if (contract) {
              this.applyContract(contract);
              this.loadedContractId = normalizedContractId;
            }

            this.isFinalizingContract = false;
          },
          error: (error) => {
            console.error('[SolarRentWizardStateService] Failed to finalize contract', error);
            this.finalizeContractError = 'Unable to finalize contract.';
            this.isFinalizingContract = false;
          },
        }),
      );
  }

  setElectricalEnvironment(
    selectedKey: 'singlePhaseEnvironment' | 'threePhaseEnvironment',
    checked: boolean,
  ): void {
    if (!checked) {
      return;
    }

    this.updateDocumentSelectionScope(() => {
      this.setExclusiveSelection(
        this.form.contractDetails.parties.site,
        selectedKey,
        ['singlePhaseEnvironment', 'threePhaseEnvironment'],
        true,
      );

      const environmentCode = this.getSelectedEnvironmentCode();
      const selectedProductType = this.selectedProductTypeCode;
      const resolvedContractType = selectedProductType
        ? this.findContractTypeByProductTypeAndEnvironment(selectedProductType, environmentCode)
        : null;

      if (resolvedContractType) {
        this.form.contractType = this.normalizeContractTypeValue(resolvedContractType.name);
      }

      this.syncDerivedConfiguration();
    });
  }

  loadContract(contractId: string, baseUrl: string, token?: string | null): void {
    const normalizedContractId = String(contractId).trim();
    const normalizedBaseUrl = this.resolveContractsApiBaseUrl(baseUrl);

    if (
      !normalizedContractId ||
      !normalizedBaseUrl ||
      this.loadedContractId === normalizedContractId ||
      this.isLoadingContract
    ) {
      return;
    }

    this.apiBaseUrl = normalizedBaseUrl;
    this.isLoadingContract = true;
    this.loadContractError = '';
    this.solarProjectLeadProductPayload = null;
    this.persistedPvModuleCount = null;
    this.solarRentAdvisor = { name: '', email: '', phone_number: '' };
    this.solarProjectLeadReadonlyValues.clear();
    this.pendingInitialPurposeStepSnapshotAfterTemplateLoad = false;

    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;

    this.http
      .get<{ data?: Record<string, any> } | Record<string, any>>(
        `${normalizedBaseUrl}/contracts/${normalizedContractId}`,
        { headers },
      )
      .subscribe({
        next: (response) => {
          const contract = this.unwrapContractResponse(response);

          if (contract) {
            this.applyContract(contract);
            this.loadedContractId = normalizedContractId;
            this.loadSolarProjectLead(normalizedContractId, baseUrl, token);
            this.loadLinkedApplicationWizardData(contract, normalizedContractId, baseUrl, token);
          }

          this.isLoadingContract = false;

          if (this.pendingInitialPurposeStepSnapshotAfterTemplateLoad) {
            return;
          }

          this.captureCurrentPurposeStepPayload();
        },
        error: (error) => {
          console.error('[SolarRentWizardStateService] Failed to load contract', error);
          this.loadContractError = 'Unable to load contract data.';
          this.pendingInitialPurposeStepSnapshotAfterTemplateLoad = false;
          this.isLoadingContract = false;
        },
      });
  }

  private loadSolarProjectLead(
    contractId: string,
    baseUrl: string,
    token?: string | null,
  ): void {
    const coreBaseUrl = this.resolveCoreApiBaseUrl(baseUrl);

    if (!coreBaseUrl) {
      return;
    }

    this.http
      .get<Record<string, any> | null>(
        `${coreBaseUrl}/contracts/${encodeURIComponent(contractId)}/solar-project-lead`,
        { headers: this.buildAuthorizationHeaders(token) },
      )
      .subscribe({
        next: (response) => {
          let payload: unknown = response;

          for (let depth = 0; depth < 3; depth += 1) {
            if (!this.isRecord(payload) || !this.isRecord(payload['data'])) {
              break;
            }

            payload = payload['data'];
          }

          if (!this.isRecord(payload) || !this.isRecord(payload['product'])) {
            console.warn('[SolarRentWizard] Solar Project Lead response has no product payload', response);
            return;
          }

          console.log('[TOTO]', payload);

          const location = this.isRecord(payload['location']) ? payload['location'] : null;
          const advisor = location && this.isRecord(location['advisor']) ? location['advisor'] : null;
          const advisorName = advisor ? this.buildUserDisplayName(advisor) : '';
          this.solarRentAdvisor = {
            name: advisorName,
            email: advisor ? this.toText(advisor['email']) : '',
            phone_number: advisor ? this.toText(advisor['phone_number']) : '',
          };

          this.solarProjectLeadProductPayload = payload['product'];
          this.applySolarProjectLeadProduct(payload['product']);
        },
        error: (error) => {
          console.warn('[SolarRentWizard] Unable to load Solar Project Lead product', error);
        },
      });
  }

  private applySolarProjectLeadProduct(product: Record<string, any>): void {
    this.solarProjectLeadReadonlyValues.clear();
    const components = this.isRecord(product['components']) ? product['components'] : {};
    const panels = this.arrayRecords(components['panels'] ?? components['panel'] ?? product['panels'] ?? product['panel']);
    const batteries = this.arrayRecords(components['battery'] ?? components['batteries'] ?? product['batteries'] ?? product['battery']);
    const inverters = this.arrayRecords(components['inverter'] ?? components['inverters'] ?? product['inverters'] ?? product['inverter']);
    const productPanelPower = this.productPowerText(product['panel_power'] ?? product['panelPower']);
    const productBatteryPower = this.productPowerText(product['battery_power'] ?? product['batteryPower']);
    const productInverterPower = this.productPowerText(product['inverter_power'] ?? product['inverterPower']);
    const declaredPanelQuantity = this.toNumber(product['panel_count'] ?? product['panelCount']) ?? 0;
    const declaredBatteryQuantity = this.toNumber(product['battery_unit_count'] ?? product['batteryUnitCount']) ?? 0;
    const panelQuantity = panels.reduce((total, panel) => total + this.productQuantity(panel), 0)
      || Math.max(0, Math.round(declaredPanelQuantity));
    const batteryQuantity = batteries.reduce((total, battery) => total + this.productQuantity(battery), 0)
      || Math.max(0, Math.round(declaredBatteryQuantity))
      || (productBatteryPower !== '' ? 1 : 0);
    const fallbackPanels = panels.length > 0 || panelQuantity <= 0
      ? panels
      : [{ brand: product['brand'], quantity: panelQuantity }];
    const fallbackBatteries = batteries.length > 0 || batteryQuantity <= 0
      ? batteries
      : [{ brand: product['brand'], quantity: batteryQuantity }];
    const panelUnitPower = this.productPanelUnitPower(productPanelPower, fallbackPanels, panelQuantity);
    const panelRows = this.productPanelRows(fallbackPanels, productPanelPower, panelQuantity);
    const batteryRows = this.productBatteryRows(fallbackBatteries, productBatteryPower, batteryQuantity);
    const firstInverter = inverters[0] ?? {};
    const firstBattery = fallbackBatteries[0] ?? {};
    const batteryBrand = this.firstText(firstBattery['brand'], product['brand']);
    const batteryModel = this.firstText(firstBattery['model']);
    const inverterBrand = this.firstText(firstInverter['brand'], product['brand']);
    const inverterModel = this.firstText(firstInverter['model']);
    const pvBrand = this.firstText(fallbackPanels[0]?.['brand']);
    const pvModel = this.firstText(fallbackPanels[0]?.['model']);
    const moduleCount = this.persistedPvModuleCount && this.persistedPvModuleCount.trim() !== ''
      ? this.persistedPvModuleCount
      : this.toText(panelQuantity || product['panel_count']);

    this.rememberSolarRentProductField('installedConfig.plannedPv', productPanelPower);
    this.rememberSolarRentProductField('installedConfig.installedPv', productPanelPower);
    this.rememberSolarRentProductField('installedConfig.plannedBattery', productBatteryPower);
    this.rememberSolarRentProductField('installedConfig.installedBattery', productBatteryPower);
    this.rememberSolarRentProductField('installedConfig.plannedModuleCount', panelQuantity || product['panel_count']);
    this.rememberSolarRentProductField('installedConfig.installedModuleCount', panelQuantity || product['panel_count']);
    this.rememberSolarRentProductField('installedConfig.plannedInverter', productInverterPower);
    this.rememberSolarRentProductField('installedConfig.installedInverter', productInverterPower);
    this.rememberSolarRentProductField('pvModules.brand', pvBrand);
    this.rememberSolarRentProductField('pvModules.model', pvModel);
    this.rememberSolarRentProductField('pvModules.unitPower', panelUnitPower);
    this.rememberSolarRentProductField('pvModules.moduleCount', panelQuantity || product['panel_count']);
    this.rememberSolarRentProductField('pvModules.totalPower', productPanelPower);
    Object.entries(panelRows).forEach(([rowKey, row]) => {
      ['brand', 'model', 'unitPower'].forEach((field) => {
        this.rememberSolarRentProductField(`pvModules.row.${rowKey}.${field}`, row[field]);
      });
    });
    this.rememberSolarRentProductField('battery.brand', batteryBrand);
    this.rememberSolarRentProductField('battery.model', batteryModel);
    this.rememberSolarRentProductField('battery.nominalCapacity', productBatteryPower);
    this.rememberSolarRentProductField('battery.usableCapacity', productBatteryPower);
    if (batteries.length > 0 || declaredBatteryQuantity > 0) {
      this.rememberSolarRentProductField('battery.unitCount', batteryQuantity || product['battery_unit_count'] || product['batteryUnitCount']);
    }
    Object.entries(batteryRows).forEach(([rowKey, row]) => {
      ['brand', 'model', 'capacity'].forEach((field) => {
        this.rememberSolarRentProductField(`battery.row.${rowKey}.${field}`, row[field]);
      });
    });
    this.rememberSolarRentProductField('inverter.brand', inverterBrand);
    this.rememberSolarRentProductField('inverter.model', inverterModel);
    this.rememberSolarRentProductField('inverter.nominalPower', productInverterPower);

    this.form.contractDetails.installedConfig.summary.plannedPv = productPanelPower;
    this.form.contractDetails.installedConfig.summary.installedPv = productPanelPower;
    this.form.contractDetails.installedConfig.summary.plannedBattery = productBatteryPower;
    this.form.contractDetails.installedConfig.summary.installedBattery = productBatteryPower;
    this.form.contractDetails.installedConfig.summary.plannedModuleCount = this.toText(panelQuantity || product['panel_count']);
    this.form.contractDetails.installedConfig.summary.installedModuleCount = this.toText(panelQuantity || product['panel_count']);
    this.form.contractDetails.installedConfig.summary.plannedInverter = productInverterPower;
    this.form.contractDetails.installedConfig.summary.installedInverter = productInverterPower;

    this.form.contractDetails.pvModules.brand = pvBrand;
    this.form.contractDetails.pvModules.model = pvModel;
    this.form.contractDetails.pvModules.unitPower = panelUnitPower;
    this.form.contractDetails.pvModules.moduleCount = moduleCount;
    this.form.contractDetails.pvModules.totalPower = productPanelPower;
    this.form.contractDetails.pvModules.moduleTableRows = panelRows;

    this.form.contractDetails.battery.brand = batteryBrand;
    this.form.contractDetails.battery.model = batteryModel;
    this.form.contractDetails.battery.nominalCapacity = productBatteryPower;
    this.form.contractDetails.battery.usableCapacity = productBatteryPower;
    this.form.contractDetails.battery.unitCount = this.toText(batteryQuantity || product['battery_unit_count'] || product['batteryUnitCount']);
    this.form.contractDetails.battery.batteryTableRows = batteryRows;

    this.form.contractDetails.inverter.brand = inverterBrand;
    this.form.contractDetails.inverter.model = inverterModel;
    this.form.contractDetails.inverter.nominalPower = productInverterPower;
    if (inverters.length > 0) {
      this.form.contractDetails.inverter.hybridType = true;
      this.form.contractDetails.inverter.otherType = false;
      this.form.contractDetails.inverter.otherTypeDetails = '';
    }

    this.form.contractDetails.installedConfig.summary.pvCompliant = true;
    this.form.contractDetails.installedConfig.summary.pvAdapted = false;
    this.form.contractDetails.installedConfig.summary.pvOrdinaryAdaptation = false;
    this.form.contractDetails.installedConfig.summary.pvSubstantialAdaptation = false;
    this.form.contractDetails.installedConfig.summary.batteryCompliant = true;
    this.form.contractDetails.installedConfig.summary.batteryAdapted = false;
    this.form.contractDetails.installedConfig.summary.batteryOrdinaryAdaptation = false;
    this.form.contractDetails.installedConfig.summary.batterySubstantialAdaptation = false;
    this.form.contractDetails.installedConfig.summary.moduleCountCompliant = true;
    this.form.contractDetails.installedConfig.summary.moduleCountAdapted = false;
    this.form.contractDetails.installedConfig.summary.moduleCountOrdinaryAdaptation = false;
    this.form.contractDetails.installedConfig.summary.moduleCountSubstantialAdaptation = false;
    this.form.contractDetails.installedConfig.summary.inverterCompliant = true;
    this.form.contractDetails.installedConfig.summary.inverterAdapted = false;
    this.form.contractDetails.installedConfig.summary.inverterOrdinaryAdaptation = false;
    this.form.contractDetails.installedConfig.summary.inverterSubstantialAdaptation = false;
  }

  private arrayRecords(value: unknown): Array<Record<string, any>> {
    return Array.isArray(value) ? value.filter((entry): entry is Record<string, any> => this.isRecord(entry)) : [];
  }

  private rememberSolarRentProductField(fieldKey: string, value: unknown): void {
    const normalizedValue = this.normalizeSolarRentProductValue(value);
    if (normalizedValue !== null) {
      this.solarProjectLeadReadonlyValues.set(fieldKey, normalizedValue);
    }
  }

  private normalizeSolarRentProductValue(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      const normalizedValue = value.trim();
      return normalizedValue === '' ? null : normalizedValue;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return null;
  }

  private productQuantity(component: Record<string, any>): number {
    const quantity = this.toNumber(component['quantity'] ?? component['qty']) ?? 1;
    return Math.max(1, Math.round(quantity));
  }

  private productPowerText(value: unknown): string {
    const power = this.toNumber(value);

    if (power === null || power <= 0) {
      return '';
    }

    return Number.isInteger(power) ? String(power) : String(power).replace(/0+$/, '').replace(/\.$/, '');
  }

  private productPanelUnitPower(
    productPanelPower: string,
    panels: Array<Record<string, any>>,
    panelQuantity: number,
  ): string {
    const firstPanelPower = this.toNumber(panels[0]?.['power']);

    if (firstPanelPower !== null && firstPanelPower > 100) {
      return this.productPowerText(firstPanelPower);
    }

    const totalPower = this.toNumber(productPanelPower);
    if (totalPower !== null && panelQuantity > 0) {
      const totalWatts = totalPower <= 100 ? totalPower * 1000 : totalPower;
      return this.productPowerText(totalWatts / panelQuantity);
    }

    return firstPanelPower === null ? '' : this.productPowerText(firstPanelPower * 1000);
  }

  private productPanelRows(
    panels: Array<Record<string, any>>,
    productPanelPower: string,
    panelQuantity: number,
  ): Record<string, Record<string, string>> {
    const rows: Record<string, Record<string, string>> = {};
    let rowNumber = 1;

    for (const panel of panels) {
      const quantity = this.productQuantity(panel);
      const unitPower = this.productPanelUnitPower(productPanelPower, [panel], panelQuantity);

      for (let index = 0; index < quantity; index += 1) {
        rows[String(rowNumber)] = {
          brand: this.toText(panel['brand']),
          model: this.toText(panel['model']),
          unitPower,
          serialNumber: '',
        };
        rowNumber += 1;
      }
    }

    return rows;
  }

  private productBatteryRows(
    batteries: Array<Record<string, any>>,
    productBatteryPower: string,
    batteryQuantity: number,
  ): Record<string, Record<string, string>> {
    const rows: Record<string, Record<string, string>> = {};
    let rowNumber = 1;

    for (const battery of batteries) {
      const quantity = this.productQuantity(battery);
      const capacity = this.productBatteryUnitCapacity(productBatteryPower, battery, batteryQuantity);

      for (let index = 0; index < quantity; index += 1) {
        rows[String(rowNumber)] = {
          brand: this.toText(battery['brand']),
          model: this.toText(battery['model']),
          capacity,
          serialNumber: '',
        };
        rowNumber += 1;
      }
    }

    return rows;
  }

  private productBatteryUnitCapacity(
    productBatteryPower: string,
    battery: Record<string, any>,
    batteryQuantity: number,
  ): string {
    const batteryPower = this.toNumber(battery['power']);

    if (batteryPower !== null && batteryPower > 0) {
      return this.productPowerText(batteryPower);
    }

    const totalPower = this.toNumber(productBatteryPower);

    return totalPower !== null && batteryQuantity > 0
      ? this.productPowerText(totalPower / batteryQuantity)
      : '';
  }

  private loadLinkedApplicationWizardData(
    contract: Record<string, any>,
    contractId: string,
    baseUrl: string,
    token?: string | null,
  ): void {
    const applicationId = this.toNumber(contract['application_id']) ?? 0;

    if (applicationId <= 0) {
      console.log('[SolarRentWizard] No linked CNCS application on this contract — skipping CEB prefill');
      return;
    }

    // The endpoint lives on solar-rent-core, not the FastAPI service — use resolveCoreApiBaseUrl
    const coreBaseUrl = this.resolveCoreApiBaseUrl(baseUrl);

    if (!coreBaseUrl) {
      console.warn('[SolarRentWizard] Could not resolve solar-rent-core base URL for CEB prefill', { baseUrl });
      return;
    }

    console.log('[SolarRentWizard] Loading linked CNCS application wizard data', { contractId, applicationId, coreBaseUrl });

    const headers = this.buildAuthorizationHeaders(token);

    this.http
      .get<Record<string, any> | null>(
        `${coreBaseUrl}/contracts/${contractId}/linked-application/wizard-data`,
        { headers },
      )
      .subscribe({
        next: (response) => {
          console.log('[SolarRentWizard] Linked wizard-data response', response);
          this.applyLinkedApplicationCebPrefill(response);
        },
        error: (error) => {
          console.warn('[SolarRentWizard] Unable to load linked application wizard data', error);
        },
      });
  }

  private applyLinkedApplicationCebPrefill(response: unknown): void {
    if (!response) {
      console.warn('[SolarRentWizard] Empty response from linked wizard-data endpoint');
      return;
    }

    // Unwrap outer {status, application_id, data: <wizardData>} envelope
    const innerPayload = this.isRecord(response) && (this.isRecord(response['data']) || response['status'] !== undefined)
      ? (this.isRecord(response['data']) ? response['data'] : response)
      : response;

    const wizardData = this.unwrapWizardDataPayload(innerPayload);

    if (!this.isRecord(wizardData)) {
      console.warn('[SolarRentWizard] Could not resolve wizard data sections from response', response);
      return;
    }

    // Map CEB-specific data
    const cebMapped = this.mapWizardDataToCebScheme(wizardData);
    console.log('[SolarRentWizard] Mapped CEB Information values', cebMapped);
    this.fillMissingValues(this.form.contractDetails.cebInformation, cebMapped);

    // Map Site Location data
    const siteMapped = this.mapWizardDataToSiteLocation(wizardData);
    console.log('[SolarRentWizard] Mapped Site Location values', siteMapped);
    if (siteMapped.latitude && this.form.latitude.trim() === '') {
      this.form.latitude = siteMapped.latitude;
    }
    if (siteMapped.longitude && this.form.longitude.trim() === '') {
      this.form.longitude = siteMapped.longitude;
    }
    if (
      siteMapped.address?.['number']
      || siteMapped.address?.['street']
      || siteMapped.address?.['city']
      || siteMapped.address?.['postcode']
      || siteMapped.address?.['country']
    ) {
      this.fillMissingValues(this.form.address, siteMapped.address);
    }
    if (siteMapped.site) {
      this.fillMissingValues(this.form.contractDetails.parties.site, siteMapped.site);
    }

    console.log('[SolarRentWizard] CEB Information after prefill', this.form.contractDetails.cebInformation);
    console.log('[SolarRentWizard] Site Location after prefill', {
      latitude: this.form.latitude,
      longitude: this.form.longitude,
      address: this.form.address,
      site: this.form.contractDetails.parties.site,
    });
  }

  private unwrapWizardDataPayload(payload: unknown): unknown {
    if (!this.isRecord(payload)) {
      return payload;
    }

    const nestedData = payload['data'];
    if (this.isRecord(nestedData) && this.hasWizardDataSections(nestedData)) {
      return nestedData;
    }

    if (this.hasWizardDataSections(payload)) {
      return payload;
    }

    return nestedData ?? payload;
  }

  private hasWizardDataSections(value: Record<string, any>): boolean {
    const keys = ['applicant', 'installer', 'projectSite', 'interconnection', 'facilityDetail', 'bess', 'documents', 'application'];
    return keys.some((key) => key in value);
  }

  private mapWizardDataToCebScheme(wizardData: Record<string, any>): Record<string, string> {
    // Tariff: prefer first non-empty contractAccount tariff, then applicant's cebTarifCode
    const contractAccountTariff = this.extractContractAccountTariff(wizardData);
    const cebTarifCode = this.firstText(
      this.findValueAtPaths(wizardData, [
        ['applicant', 'data', 'cebTarifCode'],
        ['applicant', 'cebTarifCode'],
      ]),
      this.findFirstNestedTextByKeys(wizardData, ['cebTarifCode', 'cebTariCode', 'tariffCode', 'tariff_code']),
    );
    const tariff = this.firstText(contractAccountTariff, cebTarifCode);

    // Application type: drives the "applicable scheme"
    const applicationType = this.firstText(
      this.findValueAtPaths(wizardData, [
        ['application', 'application_type'],
        ['application', 'data', 'application_type'],
        ['application', 'type'],
      ]),
      this.findFirstNestedTextByKeys(wizardData, ['application_type', 'applicationType']),
    );

    // Tariff category: prefer explicit field, fall back to deriving from tariff code or application category
    const cebApplicationCategory = this.firstText(
      this.findValueAtPaths(wizardData, [
        ['applicant', 'data', 'cebApplicationCategory'],
        ['applicant', 'cebApplicationCategory'],
      ]),
      this.findFirstNestedTextByKeys(wizardData, ['cebApplicationCategory', 'ceb_application_category']),
    );
    const mappedCategory = this.firstText(
      this.findFirstNestedTextByKeys(wizardData, ['tariffCategory', 'tariff_category']),
      this.deriveTariffCategoryFromTariff(tariff),
      cebApplicationCategory,
    );

    // Reference date: prefer application updated_at, then created_at
    const mappedReferenceDate = this.normalizeDateForInput(
      this.firstText(
        this.findValueAtPaths(wizardData, [
          ['application', 'updated_at'],
          ['application', 'created_at'],
          ['application', 'data', 'updated_at'],
          ['application', 'data', 'created_at'],
          ['application', 'submitted_at'],
          ['application', 'data', 'submitted_at'],
          ['application', 'reference_date'],
          ['application', 'data', 'reference_date'],
        ]),
      ),
    );

    // kWh value: look for explicit field, or use declared load from first contract account
    const declaredLoad = this.extractFirstContractAccountDeclaredLoad(wizardData);
    const mappedKwh = this.firstText(
      this.findFirstNestedTextByKeys(wizardData, [
        'cebReferenceKwhValue',
        'ceb_reference_kwh_value',
        'referenceKwhValue',
        'reference_kwh_value',
        'annualKwhConsumption',
        'annual_kwh_consumption',
        'kwhConsumption',
        'kwh_consumption',
      ]),
      declaredLoad,
    );

    // Applicable scheme: derive from application_type
    const mappedApplicableScheme = this.firstText(
      this.findFirstNestedTextByKeys(wizardData, ['applicableScheme', 'applicable_scheme']),
      this.resolveApplicableSchemeFromApplicationType(applicationType),
    );

    // Business Partner Number (BP Number): extract from applicant data
    const cebBusinessPartnerNumber = this.firstText(
      this.findValueAtPaths(wizardData, [
        ['applicant', 'data', 'cebBusinessPartnerNumber'],
        ['applicant', 'cebBusinessPartnerNumber'],
        ['applicant', 'data', 'businessPartnerNumber'],
        ['applicant', 'businessPartnerNumber'],
      ]),
      this.findFirstNestedTextByKeys(wizardData, [
        'cebBusinessPartnerNumber',
        'ceb_business_partner_number',
        'businessPartnerNumber',
        'business_partner_number',
        'bpNumber',
        'bp_number',
      ]),
    );

    // Contract Account Numbers: extract from projectSite contractAccounts array
    const contractAccountNumbers = this.extractContractAccountNumbers(wizardData);

    return {
      cebReferenceDate: mappedReferenceDate,
      cebReferenceTariff: tariff,
      cebTariffCategory: mappedCategory,
      cebReferenceKwhValue: mappedKwh,
      cebApplicableScheme: mappedApplicableScheme,
      cebBusinessPartnerNumber,
      cebContractAccountNumbers: contractAccountNumbers,
    };
  }

  private mapWizardDataToSiteLocation(wizardData: Record<string, any>): {
    latitude: string;
    longitude: string;
    address: Record<string, string>;
    site: Record<string, string>;
  } {
    // Extract GPS coordinates and address from projectSite.data payload used by CNCS wizards
    const projectSiteData = this.getProjectSiteData(wizardData);

    let latitude = '';
    let longitude = '';

    if (projectSiteData && this.isRecord(projectSiteData)) {
      const coordsData = projectSiteData;
      if (this.isRecord(coordsData)) {
        latitude = this.firstText(
          this.findValueAtPaths(coordsData, [
            ['latitude'],
            ['lat'],
            ['gpsCoordinates', 'latitude'],
            ['gps_coordinates', 'latitude'],
            ['gps', 'latitude'],
            ['coordinates', 'latitude'],
          ]),
          this.findFirstNestedTextByKeys(coordsData, ['latitude', 'lat']),
        );

        longitude = this.firstText(
          this.findValueAtPaths(coordsData, [
            ['longitude'],
            ['lng'],
            ['lon'],
            ['gpsCoordinates', 'longitude'],
            ['gps_coordinates', 'longitude'],
            ['gps', 'longitude'],
            ['coordinates', 'longitude'],
          ]),
          this.findFirstNestedTextByKeys(coordsData, ['longitude', 'lng', 'lon']),
        );

        if (latitude === '' || longitude === '') {
          const parsedFromGpsCoordinates = this.parseGpsCoordinates(
            this.firstText(
              coordsData['gpsCoordinates'],
              coordsData['gps_coordinates'],
              coordsData['gpsCoordinate'],
              coordsData['gps_coordinate'],
            ),
          );
          latitude = this.firstText(latitude, parsedFromGpsCoordinates.latitude);
          longitude = this.firstText(longitude, parsedFromGpsCoordinates.longitude);
        }
      }
    }

    // Extract address components from projectSite
    const address: Record<string, string> = {
      number: '',
      street: '',
      city: '',
      postcode: '',
      country: '',
    };

    if (projectSiteData && this.isRecord(projectSiteData)) {
      const addressData = projectSiteData;
      if (this.isRecord(addressData)) {
        address['number'] = this.firstText(
          this.findValueAtPaths(addressData, [
            ['siteAddressNumber'],
            ['site_address_number'],
            ['address', 'number'],
            ['addressNumber'],
            ['number'],
          ]),
          this.findFirstNestedTextByKeys(addressData, ['siteAddressNumber', 'site_address_number', 'addressNumber', 'address_number', 'number']),
        );

        address['street'] = this.firstText(
          this.findValueAtPaths(addressData, [
            ['siteAddressStreet'],
            ['site_address_street'],
            ['address', 'street'],
            ['street'],
            ['streetName'],
            ['addressStreet'],
          ]),
          this.findFirstNestedTextByKeys(addressData, ['siteAddressStreet', 'site_address_street', 'street', 'streetName', 'street_name', 'addressStreet']),
        );

        address['city'] = this.firstText(
          this.findValueAtPaths(addressData, [
            ['siteAddressCity'],
            ['site_address_city'],
            ['address', 'city'],
            ['city'],
            ['locationCity'],
          ]),
          this.findFirstNestedTextByKeys(addressData, ['siteAddressCity', 'site_address_city', 'city', 'locationCity', 'location_city']),
        );

        address['postcode'] = this.firstText(
          this.findValueAtPaths(addressData, [
            ['siteAddressPostcode'],
            ['site_address_postcode'],
            ['address', 'postcode'],
            ['postcode'],
            ['zipCode'],
            ['postalCode'],
          ]),
          this.findFirstNestedTextByKeys(addressData, ['siteAddressPostcode', 'site_address_postcode', 'postcode', 'zipCode', 'zip_code', 'postalCode']),
        );

        address['country'] = this.firstText(
          this.findValueAtPaths(addressData, [
            ['siteAddressCountry'],
            ['site_address_country'],
            ['address', 'country'],
            ['country'],
            ['countryName'],
          ]),
          this.findFirstNestedTextByKeys(addressData, ['siteAddressCountry', 'site_address_country', 'country', 'countryName', 'country_name']),
        );
      }
    }

    // Extract site-related information from projectSite
    const site: Record<string, string> = {};

    if (projectSiteData && this.isRecord(projectSiteData)) {
      const siteData = projectSiteData;
      if (this.isRecord(siteData)) {
        // Property Type
        const propertyType = this.firstText(
          this.findValueAtPaths(siteData, [
            ['propertyType'],
            ['property_type'],
            ['siteType'],
            ['site_type'],
          ]),
          this.findFirstNestedTextByKeys(siteData, ['propertyType', 'property_type', 'siteType']),
        );
        if (propertyType) {
          site['propertyType'] = propertyType;
        }

        const occupationStatus = this.firstText(
          this.findValueAtPaths(siteData, [
            ['siteLocationType'],
            ['site_location_type'],
            ['occupationStatus'],
            ['occupation_status'],
          ]),
          this.findFirstNestedTextByKeys(siteData, ['siteLocationType', 'site_location_type', 'occupationStatus', 'occupation_status']),
        );
        if (occupationStatus) {
          site['occupationStatus'] = occupationStatus;
        }

        // Roof Condition
        const roofCondition = this.firstText(
          this.findValueAtPaths(siteData, [
            ['roofCondition'],
            ['roof_condition'],
            ['roofType'],
            ['roof_type'],
          ]),
          this.findFirstNestedTextByKeys(siteData, ['roofCondition', 'roof_condition', 'roofType']),
        );
        if (roofCondition) {
          site['roofCondition'] = roofCondition;
        }

        // Installation Site / General Installation Mode
        const installationMode = this.firstText(
          this.findValueAtPaths(siteData, [
            ['generalInstallationMode'],
            ['general_installation_mode'],
            ['installationMode'],
            ['installation_mode'],
            ['installationSite'],
            ['installation_site'],
          ]),
          this.findFirstNestedTextByKeys(siteData, [
            'generalInstallationMode',
            'general_installation_mode',
            'installationMode',
            'installationSite',
          ]),
        );
        if (installationMode) {
          site['generalInstallationMode'] = installationMode;
        }

        // Structure Type
        const structureType = this.firstText(
          this.findValueAtPaths(siteData, [
            ['structureType'],
            ['structure_type'],
            ['supportStructure'],
            ['support_structure'],
          ]),
          this.findFirstNestedTextByKeys(siteData, ['structureType', 'structure_type', 'supportStructure']),
        );
        if (structureType) {
          site['structureType'] = structureType;
        }
      }
    }

    return { latitude, longitude, address, site };
  }

  private extractContractAccountTariff(source: unknown): string {
    const accountCollection = this.getContractAccountsCollection(source);

    if (!Array.isArray(accountCollection)) {
      return '';
    }

    const selectedAccount = this.getSelectedContractAccount(accountCollection);
    if (selectedAccount && this.isRecord(selectedAccount)) {
      return this.firstText(selectedAccount['tariff'], selectedAccount['tariffCode'], selectedAccount['tariff_code']);
    }

    for (const account of accountCollection) {
      if (!this.isRecord(account)) {
        continue;
      }

      const tariff = this.firstText(account['tariff'], account['tariffCode'], account['tariff_code']);
      if (tariff !== '') {
        return tariff;
      }
    }

    return '';
  }

  private extractFirstContractAccountDeclaredLoad(source: unknown): string {
    const accountCollection = this.getContractAccountsCollection(source);

    if (!Array.isArray(accountCollection)) {
      return '';
    }

    const selectedAccount = this.getSelectedContractAccount(accountCollection);
    if (selectedAccount && this.isRecord(selectedAccount)) {
      const selectedLoad = this.firstText(selectedAccount['declaredLoad'], selectedAccount['declared_load'], selectedAccount['load']);
      if (selectedLoad !== '') {
        return selectedLoad;
      }
    }

    for (const account of accountCollection) {
      if (!this.isRecord(account)) {
        continue;
      }

      const load = this.firstText(account['declaredLoad'], account['declared_load'], account['load']);
      if (load !== '') {
        return load;
      }
    }

    return '';
  }

  private extractContractAccountNumbers(source: unknown): string {
    const accountCollection = this.getContractAccountsCollection(source);

    if (!Array.isArray(accountCollection)) {
      return '';
    }

    const selectedAccount = this.getExplicitlySelectedContractAccount(accountCollection);
    if (selectedAccount && this.isRecord(selectedAccount)) {
      return this.firstText(
        selectedAccount['accountNumber'],
        selectedAccount['account_number'],
        selectedAccount['number'],
        selectedAccount['id'],
      );
    }

    return '';
  }

  private getProjectSiteData(source: unknown): Record<string, any> | null {
    if (!this.isRecord(source)) {
      return null;
    }

    const projectSite = this.findFirstNestedValueByKeys(source, ['projectSite', 'project_site']);
    if (!this.isRecord(projectSite)) {
      return null;
    }

    const candidate = this.isRecord(projectSite['data']) ? projectSite['data'] : projectSite;
    return this.isRecord(candidate) ? candidate : null;
  }

  private getContractAccountsCollection(source: unknown): unknown[] {
    if (!this.isRecord(source)) {
      return [];
    }

    const candidates: unknown[] = [
      this.findValueAtPath(source, ['projectSite', 'data', 'contractAccounts']),
      this.findValueAtPath(source, ['project_site', 'data', 'contractAccounts']),
      this.findValueAtPath(source, ['projectSite', 'contractAccounts']),
      this.findValueAtPath(source, ['project_site', 'contractAccounts']),
      this.findValueAtPath(source, ['projectDetails', 'contractAccounts']),
      this.findValueAtPath(source, ['project_details', 'contractAccounts']),
      this.findValueAtPath(source, ['project', 'contractAccounts']),
      this.findValueAtPath(source, ['selectedContractAccounts']),
      this.findValueAtPath(source, ['contractAccounts']),
      this.findValueAtPath(source, ['contract_accounts']),
    ];

    const nested = this.findFirstNestedValueByKeys(source, ['selectedContractAccounts', 'contractAccounts', 'contract_accounts']);
    candidates.push(nested);

    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) {
        return candidate;
      }
    }

    return [];
  }

  private getSelectedContractAccount(accountCollection: unknown[]): Record<string, any> | null {
    const explicitSelection = this.getExplicitlySelectedContractAccount(accountCollection);
    if (explicitSelection) {
      return explicitSelection;
    }

    const firstRecord = accountCollection.find((account) => this.isRecord(account));
    return firstRecord && this.isRecord(firstRecord) ? firstRecord : null;
  }

  private getExplicitlySelectedContractAccount(accountCollection: unknown[]): Record<string, any> | null {
    for (const account of accountCollection) {
      if (!this.isRecord(account)) {
        continue;
      }

      if (account['isSelected'] === true || account['selected'] === true || account['is_selected'] === true) {
        return account;
      }
    }

    return null;
  }

  private parseGpsCoordinates(value: string): { latitude: string; longitude: string } {
    const raw = this.toText(value);
    if (raw === '') {
      return { latitude: '', longitude: '' };
    }

    const normalized = raw.replace(/[;]/g, ',').trim();
    const parts = normalized.split(',').map((item) => item.trim()).filter((item) => item !== '');

    if (parts.length < 2) {
      return { latitude: '', longitude: '' };
    }

    return {
      latitude: parts[0],
      longitude: parts[1],
    };
  }

  private findValueAtPath(source: unknown, path: string[]): unknown {
    if (!this.isRecord(source)) {
      return null;
    }

    let current: unknown = source;
    for (const key of path) {
      if (!this.isRecord(current) || !(key in current)) {
        return null;
      }

      current = current[key];
    }

    return current;
  }

  private deriveTariffCategoryFromTariff(tariff: string): string {
    const normalizedTariff = this.toText(tariff).toUpperCase();

    if (normalizedTariff === '') {
      return '';
    }

    if (/^225[A-Z]*$/.test(normalizedTariff)) {
      return 'Business';
    }

    if (/^110[A-Z]*$/.test(normalizedTariff)) {
      return 'Domestic';
    }

    return '';
  }

  private resolveApplicableSchemeFromApplicationType(applicationType: string): string {
    const normalizedType = this.toText(applicationType).toUpperCase();

    if (normalizedType === '') {
      return '';
    }

    if (normalizedType.includes('CNCS')) {
      return 'CNCS';
    }

    if (normalizedType.includes('SSDG')) {
      return 'SSDG';
    }

    if (normalizedType.includes('MSDG')) {
      return 'MSDG';
    }

    return applicationType;
  }

  private normalizeDateForInput(value: string): string {
    const normalized = this.toText(value);

    if (normalized === '') {
      return '';
    }

    const isoDateMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDateMatch) {
      return isoDateMatch[1];
    }

    const timestamp = Date.parse(normalized);
    if (Number.isNaN(timestamp)) {
      return '';
    }

    const parsedDate = new Date(timestamp);
    const year = String(parsedDate.getFullYear()).padStart(4, '0');
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private findValueAtPaths(source: unknown, paths: string[][]): string {
    if (!this.isRecord(source)) {
      return '';
    }

    for (const path of paths) {
      let current: unknown = source;

      for (const key of path) {
        if (!this.isRecord(current) || !(key in current)) {
          current = null;
          break;
        }

        current = current[key];
      }

      const resolved = this.toText(current);
      if (resolved !== '') {
        return resolved;
      }
    }

    return '';
  }

  private findFirstNestedTextByKeys(source: unknown, keys: string[]): string {
    const value = this.findFirstNestedValueByKeys(source, keys);
    return this.toText(value);
  }

  private findFirstNestedValueByKeys(source: unknown, keys: string[]): unknown {
    const expectedKeys = new Set(keys.map((key) => key.toLowerCase()));
    const queue: unknown[] = [source];
    const visited = new Set<unknown>();

    while (queue.length > 0) {
      const current = queue.shift();

      if (current === null || current === undefined) {
        continue;
      }

      if (visited.has(current)) {
        continue;
      }

      if (this.isRecord(current)) {
        visited.add(current);

        for (const [key, value] of Object.entries(current)) {
          if (expectedKeys.has(key.toLowerCase())) {
            return value;
          }

          if (this.isRecord(value) || Array.isArray(value)) {
            queue.push(value);
          }
        }

        continue;
      }

      if (Array.isArray(current)) {
        visited.add(current);
        for (const item of current) {
          if (this.isRecord(item) || Array.isArray(item)) {
            queue.push(item);
          }
        }
      }
    }

    return null;
  }

  loadContractTypeOptions(baseUrl: string, token?: string | null): void {
    this.lastSignatureDocumentBaseUrl = baseUrl;
    this.lastSignatureDocumentToken = token ?? null;
    const requestBaseUrl = this.resolveContractsApiBaseUrl(baseUrl);
    const requestCacheKey = requestBaseUrl;

    this.apiBaseUrl = requestBaseUrl || this.apiBaseUrl;
    this.loadProductTypeOptions(requestBaseUrl, token);
    this.loadElectricalEnvironmentOptions(requestBaseUrl, token);

    const skipReason = requestBaseUrl === ''
      ? 'empty_api_url'
      : this.loadedContractTypesBaseUrl === requestCacheKey
        ? 'already_loaded_for_base_url'
        : this.isLoadingContractTypes
          ? 'request_in_flight'
          : '';

    if (skipReason !== '') {
      console.log('[SolarRentWizardStateService] Skipping contract type request', {
        callerBaseUrl: baseUrl,
        requestBaseUrl,
        requestCacheKey,
        loadedContractTypesBaseUrl: this.loadedContractTypesBaseUrl,
        isLoadingContractTypes: this.isLoadingContractTypes,
        skipReason,
      });
      return;
    }

    this.isLoadingContractTypes = true;
    this.apiBaseUrl = requestBaseUrl || this.apiBaseUrl;

    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;
    const params = new HttpParams({
      fromObject: {
        offset: '0',
        limit: '100',
        relation: '["product_type_item","electrical_environment_item"]',
        where: '[]',
        where_relation: '[]',
        base_columns: '[]',
      },
    });
    const endpoint = `${requestBaseUrl}/products/`;
    const requestUrl = `${endpoint}?${params.toString()}`;

    console.log('[SolarRentWizardStateService] Loading contract type options', {
      callerBaseUrl: baseUrl,
      requestUrl,
      hasToken: Boolean(token),
    });

    this.http
      .get<unknown>(endpoint, {
        headers,
        params,
      })
      .subscribe({
        next: (response) => {
          const definitions = this.unwrapCollectionResponse(response)
            .map((value) => this.extractContractTypeDefinition(value))
            .filter((value): value is SolarRentContractTypeDefinition => value !== null);

          console.log('[SolarRentWizardStateService] Contract type response received', {
            requestUrl,
            response,
            parsedOptions: definitions,
          });

          this.setContractTypeDefinitions(definitions);
          this.loadedContractTypesBaseUrl = requestCacheKey;
          this.isLoadingContractTypes = false;
          this.loadSignatureDocumentTemplates(baseUrl, token, this.form.contractType);
        },
        error: (error) => {
          console.error('[SolarRentWizardStateService] Failed to load contract type options', {
            requestUrl,
            error,
          });

          this.isLoadingContractTypes = false;
        },
      });
  }

  private loadProductTypeOptions(requestBaseUrl: string, token?: string | null): void {
    const resolvedBaseUrl = String(requestBaseUrl).trim().replace(/\/+$/, '');
    const skipReason = !resolvedBaseUrl
      ? 'empty_api_url'
      : this.loadedProductTypesBaseUrl === resolvedBaseUrl
        ? 'already_loaded_for_base_url'
        : this.isLoadingProductTypes
          ? 'request_in_flight'
          : '';

    if (skipReason !== '') {
      return;
    }

    this.isLoadingProductTypes = true;
    this.productTypeLoadError = '';

    const headers = this.buildAuthorizationHeaders(token);
    const endpoint = `${resolvedBaseUrl}/products/product-types`;

    console.log('[SolarRentWizardStateService] Loading product type options', {
      endpoint,
      hasToken: Boolean(token),
    });

    this.http
      .get<SolarRentProductTypeOption[] | { data?: unknown }>(endpoint, { headers })
      .subscribe({
        next: (response) => {
          this.productTypeOptions = this.mapUniqueCodeOptions(
            response,
            (value): SolarRentProductTypeOption => ({
              id: this.toNumber(value['id']) ?? 0,
              code: this.toText(value['code']).toUpperCase(),
              name: this.toText(value['name']),
              productData: this.extractProductData(value['product_data']),
              active: Boolean(value['active']),
            }),
          );

          if (this.contractTypeDefinitions.length > 0) {
            this.setContractTypeDefinitions(this.contractTypeDefinitions);
          }

          this.loadedProductTypesBaseUrl = resolvedBaseUrl;
          this.isLoadingProductTypes = false;
        },
        error: (error) => {
          console.error('[SolarRentWizardStateService] Failed to load product type options', {
            endpoint,
            error,
          });
          this.productTypeLoadError = 'Unable to load product types.';
          this.isLoadingProductTypes = false;
        },
      });
  }

  private loadElectricalEnvironmentOptions(requestBaseUrl: string, token?: string | null): void {
    const resolvedBaseUrl = String(requestBaseUrl).trim().replace(/\/+$/, '');
    const skipReason = !resolvedBaseUrl
      ? 'empty_api_url'
      : this.loadedElectricalEnvironmentsBaseUrl === resolvedBaseUrl
        ? 'already_loaded_for_base_url'
        : this.isLoadingElectricalEnvironments
          ? 'request_in_flight'
          : '';

    if (skipReason !== '') {
      return;
    }

    this.isLoadingElectricalEnvironments = true;

    const headers = this.buildAuthorizationHeaders(token);
    const endpoint = `${resolvedBaseUrl}/products/electrical-environments`;

    this.http
      .get<SolarRentElectricalEnvironmentOption[] | { data?: unknown }>(endpoint, { headers })
      .subscribe({
        next: (response) => {
          this.electricalEnvironmentOptions = this.mapUniqueCodeOptions(
            response,
            (value): SolarRentElectricalEnvironmentOption => ({
              id: this.toNumber(value['id']) ?? 0,
              code: this.normalizeElectricalEnvironmentCode(this.toText(value['code'])),
              name: this.toText(value['name']),
              active: Boolean(value['active']),
            }),
          );

          this.loadedElectricalEnvironmentsBaseUrl = resolvedBaseUrl;
          this.isLoadingElectricalEnvironments = false;
        },
        error: () => {
          this.isLoadingElectricalEnvironments = false;
        },
      });
  }

  loadSignatureDocumentTemplates(
    baseUrl: string,
    token?: string | null,
    contractType?: string | null,
  ): void {
    const requestedContractType = this.toText(contractType ?? this.form.contractType);
    const selectedProductType = this.resolveSelectedProductTypeCode(requestedContractType);
    const selectedProductName = selectedProductType === ''
      ? this.resolveSelectedProductName(requestedContractType)
      : '';
    const selectedTemplateFilter = selectedProductType || selectedProductName;

    if (selectedTemplateFilter === '') {
      this.setSignatureDocumentDefinitions([]);
      this.loadedSignatureDocumentTemplatesKey = null;
      this.signatureDocumentTemplatesError = '';
      return;
    }

    const requestBaseUrl = this.resolveContractsApiBaseUrl(baseUrl);
    const requestKey = `${requestBaseUrl}|${selectedTemplateFilter}`;

    this.lastSignatureDocumentBaseUrl = baseUrl;
    this.lastSignatureDocumentToken = token ?? null;

    if (!requestBaseUrl || this.loadedSignatureDocumentTemplatesKey === requestKey) {
      return;
    }

    const pendingRequest: SolarRentSignatureDocumentTemplatesPendingRequest = {
      requestKey,
      requestBaseUrl,
      selectedProductType,
      selectedProductName,
      baseUrl,
      token: token ?? null,
    };

    if (this.isLoadingSignatureDocumentTemplates) {
      this.pendingSignatureDocumentTemplatesRequest = pendingRequest;
      return;
    }

    this.pendingSignatureDocumentTemplatesRequest = pendingRequest;
    this.signatureDocumentTemplatesError = '';
    this.performSignatureDocumentTemplatesRequest();
  }

  private performSignatureDocumentTemplatesRequest(): void {
    const pendingRequest = this.pendingSignatureDocumentTemplatesRequest;
    if (!pendingRequest) {
      return;
    }

    this.pendingSignatureDocumentTemplatesRequest = null;
    this.isLoadingSignatureDocumentTemplates = true;
    this.signatureDocumentTemplatesError = '';

    const { requestKey, requestBaseUrl, selectedProductType, selectedProductName, baseUrl, token } = pendingRequest;

    if (this.isLoadingContract) {
      this.pendingInitialPurposeStepSnapshotAfterTemplateLoad = true;
    }

    const headers = this.buildAuthorizationHeaders(token);
    const endpoint = `${requestBaseUrl}/contract_document_templates/active`;
    const params = new HttpParams({
      fromObject: {
        ...(selectedProductType ? { product_type: selectedProductType } : {}),
        ...(!selectedProductType && selectedProductName ? { product: selectedProductName } : {}),
      },
    });
    const requestUrl = `${endpoint}?${params.toString()}`;

    console.log('[SolarRentWizardStateService] Loading signature document templates', {
      callerBaseUrl: baseUrl,
      requestUrl,
      productType: selectedProductType,
      product: selectedProductName,
    });

    this.http
      .get<{ data?: unknown }>(endpoint, { headers, params })
      .subscribe({
        next: (response) => {
          const definitions = this.unwrapCollectionResponse(response)
            .map((value, index) => this.extractSignatureDocumentDefinition(value, index + 1))
            .filter((value): value is SolarRentSignatureDocumentConfig => value !== null);

          console.log('[SolarRentWizardStateService] Signature document template response received', {
            requestUrl,
            response,
            parsedDefinitions: definitions,
          });

          this.setSignatureDocumentDefinitions(definitions);
          this.loadedSignatureDocumentTemplatesKey = requestKey;
          this.isLoadingSignatureDocumentTemplates = false;
          this.finalizePendingPurposeStepSnapshotAfterTemplateLoad();
          this.dispatchNextSignatureDocumentTemplatesRequest(requestKey);
        },
        error: (error) => {
          console.error('[SolarRentWizardStateService] Failed to load signature document templates', {
            requestUrl,
            error,
          });
          this.isLoadingSignatureDocumentTemplates = false;
          this.signatureDocumentTemplatesError =
            'Unable to load contract document templates. Please retry or reload the page.';
          this.finalizePendingPurposeStepSnapshotAfterTemplateLoad();
          this.dispatchNextSignatureDocumentTemplatesRequest(requestKey);
        },
      });
  }

  private dispatchNextSignatureDocumentTemplatesRequest(completedKey: string): void {
    const pendingRequest = this.pendingSignatureDocumentTemplatesRequest;
    if (!pendingRequest) {
      return;
    }

    this.pendingSignatureDocumentTemplatesRequest = null;

    if (pendingRequest.requestKey === completedKey) {
      return;
    }

    this.performSignatureDocumentTemplatesRequest();
  }

  retrySignatureDocumentTemplates(): void {
    if (!this.lastSignatureDocumentBaseUrl) {
      return;
    }

    this.loadedSignatureDocumentTemplatesKey = null;
    this.signatureDocumentTemplatesError = '';
    this.loadSignatureDocumentTemplates(
      this.lastSignatureDocumentBaseUrl,
      this.lastSignatureDocumentToken,
      this.form.contractType,
    );
  }

  loadDefaultSettings(baseUrl: string, token?: string | null): void {
    const normalizedBaseUrl = String(baseUrl).trim().replace(/\/+$/, '');

    if (
      !normalizedBaseUrl ||
      this.loadedDefaultSettingsBaseUrl === normalizedBaseUrl ||
      this.isLoadingDefaultSettings
    ) {
      return;
    }

    this.isLoadingDefaultSettings = true;

    const headers = this.buildAuthorizationHeaders(token);

    this.http
      .get<{ data?: unknown }>(`${normalizedBaseUrl}/api/manager-settings`, {
        headers,
        params: { application_type: 'SOLAR_RENT' },
      })
      .subscribe({
        next: (response) => {
          this.applyDefaultSettings(response);
          this.loadedDefaultSettingsBaseUrl = normalizedBaseUrl;
          this.isLoadingDefaultSettings = false;
        },
        error: (error) => {
          console.error('[SolarRentWizardStateService] Failed to load default settings', error);
          this.applyDefaultSettings(null);
          this.isLoadingDefaultSettings = false;
        },
      });
  }

  prepareNewContractContext(): void {
    this.loadedContractId = null;
    this.isContractFinalized = false;
    this.persistedPvModuleCount = null;
    this.solarRentAdvisor = { name: '', email: '', phone_number: '' };
    this.loadContractError = '';
    this.pendingInitialPurposeStepSnapshotAfterTemplateLoad = false;

    const defaults = createSolarRentDefaultForm();
    this.mergeInto(this.form.contractDetails.parties.landlord, defaults.contractDetails.parties.landlord);
    this.mergeInto(this.form.contractDetails.parties.technician, defaults.contractDetails.parties.technician);
    this.mergeInto(this.form.contractDetails.referenceConfig, defaults.contractDetails.referenceConfig);
  }

  setDocumentFile(key: string, file: File | null): void {
    if (this.isContractFinalized) {
      return;
    }

    this.documentFiles[key] = this.appendFileEntry(this.documentFiles[key], file);

    if (this.isProductLinkedDocumentKey(key) && this.toText(this.documentProductSelections[key]) === '') {
      this.documentProductSelections[key] = this.resolveDefaultDocumentProductSelection();
    }

    this.documentsChanged = true;
    this.hasManualDocumentEdits = true;
  }

  removeDocumentFile(key: string, index?: number): void {
    if (this.isContractFinalized) {
      return;
    }

    this.documentFiles[key] = this.removeFileEntry(this.documentFiles[key], index);

    if (this.isProductLinkedDocumentKey(key) && (this.documentFiles[key] ?? []).length === 0) {
      this.rememberDismissedChecklistDefaultDocument(key);
    }

    this.documentsChanged = true;
    this.hasManualDocumentEdits = true;
  }

  isSharedDocumentProductDropdownAnchor(key: string): boolean {
    return this.getSharedDocumentProductAnchorKey() === key;
  }

  getSharedDocumentProductSelection(): string {
    const selections = PRODUCT_LINKED_DOCUMENT_KEYS
      .map((key) => this.toText(this.documentProductSelections[key]))
      .filter((value) => value !== '');

    return selections[0] ?? '';
  }

  setSharedDocumentProductSelection(value: string): void {
    if (this.isContractFinalized) {
      return;
    }

    const normalizedValue = this.toText(value);

    for (const key of PRODUCT_LINKED_DOCUMENT_KEYS) {
      this.documentProductSelections[key] = normalizedValue;
      this.applySelectedChecklistDefaultDocument(key, normalizedValue);
    }

    this.documentsChanged = true;
  }

  get sharedDocumentProductSelectOptions(): Array<{ value: string; label: string }> {
    const cacheKey = this.buildSharedDocumentProductSelectOptionsCacheKey();

    if (cacheKey === this.cachedSharedDocumentProductSelectOptionsKey) {
      return this.cachedSharedDocumentProductSelectOptions;
    }

    this.cachedSharedDocumentProductSelectOptionsKey = cacheKey;
    this.cachedSharedDocumentProductSelectOptions = this.buildSharedDocumentProductOptions().map((option) => ({
      value: option.key,
      label: option.label,
    }));

    return this.cachedSharedDocumentProductSelectOptions;
  }

  private buildSharedDocumentProductOptions(): SolarRentOption[] {
    const options = this.getAvailableDocumentProductDefinitions()
      .map((definition) => ({
        key: definition.name,
        label: definition.name || definition.label || definition.code,
      }))
      .filter(
        (option, index, values) =>
          option.key !== '' && values.findIndex((entry) => entry.key === option.key) === index,
      );

    const selectedValues = PRODUCT_LINKED_DOCUMENT_KEYS
      .map((key) => this.toText(this.documentProductSelections[key]))
      .filter((value, index, values) => value !== '' && values.indexOf(value) === index);

    for (const selectedValue of selectedValues) {
      if (!options.some((option) => option.key === selectedValue)) {
        options.push({
          key: selectedValue,
          label: selectedValue,
        });
      }
    }

    return options;
  }

  setSignatureNicFile(key: string, file: File | null): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }
    entry.nicFiles = this.appendFileEntry(entry.nicFiles, file);
    entry.nicRectoVersoConfirmed = false;
    entry.signedSnapshot = null;
    entry.signedDocumentFile = null;
  }

  removeSignatureNicFile(key: string, index?: number): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }
    entry.nicFiles = this.removeFileEntry(entry.nicFiles, index);
    entry.nicRectoVersoConfirmed = false;
    entry.signedSnapshot = null;
    entry.signedDocumentFile = null;
  }

  setSignatureNicConfirmed(key: string, confirmed: boolean): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }

    entry.nicRectoVersoConfirmed = confirmed && entry.nicFiles.length > 0;
  }

  setSignatureMode(key: string, signatureMode: 'draw' | 'upload'): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }
    entry.signatureMode = signatureMode;
    entry.signedSnapshot = null;
    entry.signedDocumentFile = null;
  }

  setSignatureUploadFile(key: string, file: File | null): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }
    entry.signatureUploadFile = this.replaceFileEntry(entry.signatureUploadFile, file);
    entry.signedSnapshot = null;
    entry.signedDocumentFile = null;
  }

  setUploadedSignatureFile(key: string, uploadedFile: SolarRentStoredFile | null): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }

    if (entry.signatureUploadFile?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(entry.signatureUploadFile.url);
    }

    entry.signatureUploadFile = uploadedFile;
    entry.signedSnapshot = null;
    entry.signedDocumentFile = null;
  }

  addUploadedSignatureNicFile(key: string, uploadedFile: SolarRentStoredFile | null): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry || !uploadedFile) {
      return;
    }

    entry.nicFiles = [...entry.nicFiles, uploadedFile];
    entry.nicRectoVersoConfirmed = false;
    entry.signedSnapshot = null;
    entry.signedDocumentFile = null;
  }

  setSignedDocumentFile(key: string, uploadedFile: SolarRentStoredFile | null): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }
    entry.signedDocumentFile = uploadedFile;
  }

  uploadSignatureAsset(
    contractId: string,
    assetType: 'signature' | 'nic' | 'document',
    file: File,
    token?: string | null,
  ): Observable<SolarRentStoredFile | null> {
    if (this.isContractFinalized) {
      return of(null);
    }

    const normalizedContractId = String(contractId).trim();
    const requestBaseUrl = this.apiBaseUrl || this.resolveContractsApiBaseUrl(this.lastSignatureDocumentBaseUrl ?? '');

    if (!normalizedContractId || !requestBaseUrl || !file) {
      return of(null);
    }

    this.apiBaseUrl = requestBaseUrl || this.apiBaseUrl;

    let headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();

    if (this.hasValidSigningOtpToken()) {
      headers = headers.set('X-Signing-Otp-Token', this.signingOtpToken as string);
    }

    const formData = new FormData();
    formData.append('asset_file', file);

    return this.http
      .post<Record<string, unknown>>(
        `${requestBaseUrl}/contracts/${normalizedContractId}/signature-assets/upload`,
        formData,
        {
          headers,
          params: new HttpParams({ fromObject: { asset_type: assetType } }),
        },
      )
      .pipe(map((response) => this.createRemoteStoredFileEntry(response, file)));
  }

  setUploadedDocumentFile(key: string, uploadedFile: SolarRentStoredFile | null): void {
    if (!(key in this.documentFiles)) {
      return;
    }

    this.documentFiles[key] = uploadedFile
      ? this.replacePendingDocumentUpload(this.documentFiles[key], uploadedFile)
      : this.replaceFileEntries(this.documentFiles[key], []);
  }

  uploadContractDocument(
    contractId: string,
    documentKey: string,
    file: File,
    token?: string | null,
    baseUrl?: string | null,
  ): Observable<SolarRentStoredFile | null> {
    const normalizedContractId = String(contractId).trim();
    const normalizedDocumentKey = this.toText(documentKey);
    const requestBaseUrl = this.resolveContractsApiBaseUrl(baseUrl ?? this.apiBaseUrl);

    if (!normalizedContractId || !normalizedDocumentKey || !requestBaseUrl || !file) {
      return of(null);
    }

    this.apiBaseUrl = requestBaseUrl || this.apiBaseUrl;

    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;
    const formData = new FormData();
    formData.append('document_file', file);

    return this.http
      .post<{ data?: Record<string, unknown> } | Record<string, unknown>>(
        `${requestBaseUrl}/contracts/${normalizedContractId}/documents/${encodeURIComponent(normalizedDocumentKey)}/upload`,
        formData,
        { headers },
      )
      .pipe(
        map((response) => {
          const contract = this.unwrapContractResponse(response);
          if (!this.isRecord(contract)) {
            return null;
          }

          const contractDetails = this.isRecord(contract['contract_details_data'])
            ? contract['contract_details_data']
            : {};
          const documents = this.isRecord(contractDetails['documents'])
            ? contractDetails['documents']
            : {};

          return this.createLatestRemoteStoredFileEntry(documents[normalizedDocumentKey], file);
        }),
      );
  }

  deleteContractDocument(
    contractId: string,
    documentKey: string,
    fileUrl?: string | null,
    token?: string | null,
    baseUrl?: string | null,
  ): Observable<boolean> {
    const normalizedContractId = String(contractId).trim();
    const normalizedDocumentKey = this.toText(documentKey);
    const requestBaseUrl = this.resolveContractsApiBaseUrl(baseUrl ?? this.apiBaseUrl);

    if (!normalizedContractId || !normalizedDocumentKey || !requestBaseUrl) {
      return of(false);
    }

    this.apiBaseUrl = requestBaseUrl || this.apiBaseUrl;

    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;

    return this.http
      .delete(
        `${requestBaseUrl}/contracts/${normalizedContractId}/documents/${encodeURIComponent(normalizedDocumentKey)}`,
        {
          headers,
          params: this.toText(fileUrl) !== ''
            ? new HttpParams({ fromObject: { file_url: this.toText(fileUrl) } })
            : undefined,
        },
      )
      .pipe(map(() => true));
  }

  publishContractCategory(
    contractId: string,
    categoryKey: string,
    categoryLabel: string,
    step?: number | null,
    stepSlug?: string | null,
    token?: string | null,
    baseUrl?: string | null,
  ): Observable<SolarRentContractCategoryPublicationResponse | null> {
    const normalizedContractId = String(contractId).trim();
    const normalizedCategoryKey = this.toText(categoryKey);
    const normalizedCategoryLabel = this.toText(categoryLabel);
    const requestBaseUrl = this.resolveCoreApiBaseUrl(baseUrl ?? this.apiBaseUrl);

    if (!normalizedContractId || !normalizedCategoryKey || !normalizedCategoryLabel || !requestBaseUrl) {
      return of(null);
    }

    this.apiBaseUrl = requestBaseUrl || this.apiBaseUrl;

    const headers = this.buildAuthorizationHeaders(token);
    const body: Record<string, string | number> = {
      category_key: normalizedCategoryKey,
      category_label: normalizedCategoryLabel,
    };

    if (Number.isFinite(step) && (step ?? 0) > 0) {
      body['step'] = Number(step);
    }

    const normalizedSlug = this.toText(stepSlug);
    if (normalizedSlug !== '') {
      body['step_slug'] = normalizedSlug;
    }

    return this.http.post<SolarRentContractCategoryPublicationResponse>(
      `${requestBaseUrl}/contracts/${normalizedContractId}/publish-category`,
      body,
      { headers },
    );
  }

  resolveCurrentStatusLeadId(
    token?: string | null,
    baseUrl?: string | null,
    contractId?: string | null,
  ): Observable<number | null> {
    const requestBaseUrl = this.resolveCoreApiBaseUrl(baseUrl ?? this.apiBaseUrl);

    if (!requestBaseUrl) {
      return of(null);
    }

    const headers = this.buildAuthorizationHeaders(token);
    const normalizedContractId = this.toText(contractId);
    const endpoint = normalizedContractId !== ''
      ? `/contracts/${encodeURIComponent(normalizedContractId)}/solar-project-lead`
      : '/solar-project-leads/status-lead';

    return this.http
      .get<Record<string, any> | null>(
        `${requestBaseUrl}${endpoint}`,
        { headers },
      )
      .pipe(
        map((response) => {
          let payload: unknown = response;

          for (let depth = 0; depth < 3; depth += 1) {
            if (!this.isRecord(payload) || !this.isRecord(payload['data'])) {
              break;
            }

            payload = payload['data'];
          }

          const leadId = this.isRecord(payload)
            ? this.toNumber(payload['lead_id'] ?? payload['id'])
            : null;

          return leadId !== null && leadId > 0 ? leadId : null;
        }),
        catchError((error) => {
          if (normalizedContractId !== '') {
            console.warn('[SolarRentWizard] Unable to resolve the lead linked to the contract', error);
            return this.resolveCurrentStatusLeadId(token, baseUrl);
          }

          return throwError(() => error);
        }),
      );
  }

  /**
   * Ask solar-rent-core to complete the "contract_signatures" stage of the
   * solar project lead once the CET document has been signed for the given
   * contract. Resolves CNCS_CORE_URL/api/contract-signatures/completed and
   * is idempotent on the core side.
   */
  completeContractSignaturesStage(
    contractId: string,
    signedAt?: string | null,
    token?: string | null,
    coreBaseUrl?: string | null,
  ): Observable<Record<string, any> | null> {
    const normalizedContractId = String(contractId).trim();
    const contractIdValue = Number(normalizedContractId);
    const discoveredCoreBaseUrl = String(environment.cncsCoreUrl ?? '')
      .trim()
      .replace(/\/+$/, '');
    const requestBaseUrl = this.resolveCoreApiBaseUrl(coreBaseUrl ?? discoveredCoreBaseUrl);

    if (!normalizedContractId || !Number.isFinite(contractIdValue) || contractIdValue <= 0 || !requestBaseUrl) {
      return of(null);
    }

    const headers = this.buildAuthorizationHeaders(token);
    const body: Record<string, string | number> = { contract_id: contractIdValue };

    if (this.toText(signedAt) !== '') {
      body['signed_at'] = this.toText(signedAt);
    }

    return this.http.post<Record<string, any> | null>(
      `${requestBaseUrl}/solar-project-leads/contract-signatures/completed`,
      body,
      { headers },
    );
  }

  /**
   * Ask solar-rent-core to email the client once every contract document carries a
   * signature. Core is responsible for de-duplicating, so this is safe to call again.
   */
  notifyAllContractDocumentsSigned(
    contractId: string,
    token?: string | null,
    coreBaseUrl?: string | null,
  ): Observable<Record<string, any> | null> {
    const normalizedContractId = String(contractId).trim();
    const contractIdValue = Number(normalizedContractId);
    const discoveredCoreBaseUrl = String(environment.cncsCoreUrl ?? '')
      .trim()
      .replace(/\/+$/, '');
    const requestBaseUrl = this.resolveCoreApiBaseUrl(coreBaseUrl ?? discoveredCoreBaseUrl);

    console.log(
      '[SolarRentAllSignedNotify] state.notifyAllContractDocumentsSigned',
      {
        contractId: normalizedContractId,
        resolvedBaseUrl: requestBaseUrl,
        hasToken: !!token,
      },
    );

    if (!normalizedContractId || !Number.isFinite(contractIdValue) || contractIdValue <= 0 || !requestBaseUrl) {
      console.warn(
        '[SolarRentAllSignedNotify] aborted before HTTP call',
        {
          contractId: normalizedContractId,
          contractIdValue,
          requestBaseUrl,
        },
      );
      return of(null);
    }

    const url = `${requestBaseUrl}/contracts/${contractIdValue}/all-documents-signed`;

    console.log('[SolarRentAllSignedNotify] POST', url);

    return this.http
      .post<Record<string, any> | null>(url, {}, { headers: this.buildAuthorizationHeaders(token) })
      .pipe(
        tap((response) => {
          console.log('[SolarRentAllSignedNotify] response', response);
        }),
        catchError((error) => {
          console.error(
            '[SolarRentAllSignedNotify] HTTP error',
            {
              url,
              status: error?.status ?? null,
              body: error?.error ?? null,
            },
          );
          return throwError(() => error);
        }),
      );
  }

  signingOtpToken: string | null = null;
  private signingOtpTokenExpiresAtMs: number | null = null;

  hasValidSigningOtpToken(): boolean {
    if (!this.signingOtpToken || !this.signingOtpTokenExpiresAtMs) {
      return false;
    }

    // 30 s skew so a request never starts with a token about to expire.
    return this.signingOtpTokenExpiresAtMs - 30_000 > Date.now();
  }

  clearSigningOtpToken(): void {
    this.signingOtpToken = null;
    this.signingOtpTokenExpiresAtMs = null;
  }

  sendSigningOtp(
    contractId: string,
    phone: string,
    token?: string | null,
  ): Observable<Record<string, any> | null> {
    const normalizedContractId = String(contractId).trim();
    const contractIdValue = Number(normalizedContractId);
    const discoveredCoreBaseUrl = String(environment.cncsCoreUrl ?? '')
      .trim()
      .replace(/\/+$/, '');
    const requestBaseUrl = this.resolveCoreApiBaseUrl(discoveredCoreBaseUrl);

    if (!normalizedContractId || !Number.isFinite(contractIdValue) || contractIdValue <= 0 || !requestBaseUrl || !phone.trim()) {
      return of(null);
    }

    const headers = this.buildAuthorizationHeaders(token);

    return this.http.post<Record<string, any> | null>(
      `${requestBaseUrl}/signing-otp/send`,
      { contract_id: contractIdValue, phone: phone.trim() },
      { headers },
    );
  }

  verifySigningOtp(
    contractId: string,
    phone: string,
    code: string,
    token?: string | null,
  ): Observable<Record<string, any> | null> {
    const normalizedContractId = String(contractId).trim();
    const contractIdValue = Number(normalizedContractId);
    const discoveredCoreBaseUrl = String(environment.cncsCoreUrl ?? '')
      .trim()
      .replace(/\/+$/, '');
    const requestBaseUrl = this.resolveCoreApiBaseUrl(discoveredCoreBaseUrl);

    if (!normalizedContractId || !Number.isFinite(contractIdValue) || contractIdValue <= 0 || !requestBaseUrl) {
      return of(null);
    }

    const headers = this.buildAuthorizationHeaders(token);

    return this.http.post<Record<string, any> | null>(
      `${requestBaseUrl}/signing-otp/verify`,
      { contract_id: contractIdValue, phone: phone.trim(), code: code.trim() },
      { headers },
    ).pipe(
      tap((response) => {
        const otpToken = this.toText(response?.['token']);

        if (!otpToken) {
          return;
        }

        this.signingOtpToken = otpToken;
        const expiresAt = this.toText(response?.['expires_at']);
        this.signingOtpTokenExpiresAtMs = expiresAt ? Date.parse(expiresAt) : Date.now() + 30 * 60 * 1000;
      }),
    );
  }

  setDrawnSignature(key: string, signatureDataUrl: string): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }
    entry.drawnSignatureDataUrl = signatureDataUrl;
    entry.signedSnapshot = null;
    entry.signedDocumentFile = null;
  }

  clearDrawnSignature(key: string): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }
    entry.drawnSignatureDataUrl = '';
    entry.signedSnapshot = null;
    entry.signedDocumentFile = null;
  }

  setSignatureDate(key: string, signatureDate: string): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }
    entry.signatureDate = signatureDate;
    entry.signedSnapshot = null;
    entry.signedDocumentFile = null;
  }

  clearSignatureDate(key: string): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }

    entry.signatureDate = '';
    entry.signedSnapshot = null;
    entry.signedDocumentFile = null;
  }

  signDocument(key: string, snapshot: SolarRentSignedDocumentSnapshot): void {
    if (this.isContractFinalized) {
      return;
    }

    const entry = this.signatureDocuments[key];
    if (!entry) {
      return;
    }
    entry.signedSnapshot = snapshot;
    entry.signedDocumentFile = null;
  }

  /**
   * In single signature mode one client signature closes every document at once, so
   * the contract QR code is shared by all of them. It is stamped onto the snapshots
   * that already exist instead of re-signing, which would drop their signed PDFs.
   */
  attachSharedQrCodeToSignedDocuments(qrCodeData: string, qrCodeImageUrl: string): void {
    // Deliberately not gated on isContractFinalized: stamping the shared QR onto
    // already-signed snapshots is metadata only, it never re-signs a document.
    // Skipping it here left fully signed contracts without any QR code.
    for (const entry of Object.values(this.signatureDocuments)) {
      if (!entry.signedSnapshot) {
        continue;
      }

      entry.signedSnapshot = {
        ...entry.signedSnapshot,
        qrCodeData,
        qrCodeImageUrl,
      };
    }
  }

  setExclusiveSelection(
    target: Record<string, unknown>,
    selectedKey: string,
    keys: string[],
    checked: boolean,
  ): void {
    if (!checked) {
      return;
    }

    for (const key of keys) {
      target[key] = key === selectedKey;
    }
  }

  copySignatureDraft(sourceKey: string, targetKey: string): void {
    const sourceEntry = this.signatureDocuments[sourceKey];
    const targetEntry = this.signatureDocuments[targetKey];
    if (!sourceEntry || !targetEntry || sourceKey === targetKey) {
      return;
    }

    targetEntry.signatureMode = sourceEntry.signatureMode;
    targetEntry.nicFiles = this.replaceFileEntries(targetEntry.nicFiles, sourceEntry.nicFiles);
    targetEntry.nicRectoVersoConfirmed = sourceEntry.nicRectoVersoConfirmed;
    targetEntry.signatureUploadFile = this.replaceStoredFileEntry(
      targetEntry.signatureUploadFile,
      sourceEntry.signatureUploadFile,
    );
    targetEntry.drawnSignatureDataUrl = sourceEntry.drawnSignatureDataUrl;
    targetEntry.signatureDate = sourceEntry.signatureDate;
    targetEntry.signedSnapshot = null;
    targetEntry.signedDocumentFile = null;
  }

  prefillSignatureDraftForDocuments(sourceKey: string, targetKeys: string[]): void {
    for (const targetKey of targetKeys) {
      this.copySignatureDraft(sourceKey, targetKey);
    }
  }

  isDocumentSigned(key: string): boolean {
    return !!this.signatureDocuments[key]?.signedSnapshot;
  }

  isSignatureDocumentAcknowledged(key: string): boolean {
    return this.acknowledgedSignatureDocuments[key] === true;
  }

  setSignatureDocumentAcknowledged(key: string, acknowledged: boolean): void {
    if (this.isContractFinalized) {
      return;
    }

    this.acknowledgedSignatureDocuments[key] = acknowledged;
  }

  setSignatureDocumentGroupVisibility(groupKey: string, showToClient: boolean): void {
    if (this.isContractFinalized) {
      return;
    }

    const normalizedGroupKey = this.toText(groupKey);
    if (normalizedGroupKey === '') {
      return;
    }

    this.persistedSignatureDocumentGroupVisibility[normalizedGroupKey] = showToClient;

    for (const definition of this.signatureDocumentDefinitions) {
      if (definition.group?.key === normalizedGroupKey) {
        definition.group.showToClient = showToClient;
      }
    }
  }

  hasAnySignedSignatureDocument(): boolean {
    return this.signatureDocumentDefinitions.some((definition) => this.isDocumentSigned(definition.key));
  }

  areAllSignatureDocumentsSigned(): boolean {
    return this.signatureDocumentDefinitions.every((definition) => this.isDocumentSigned(definition.key));
  }

  /**
   * Guards the "all documents signed" client email for the current session.
   * Lives in the store (not the component) so a signature reset from the header
   * can release it and let the next signing round notify again.
   */
  allDocumentsSignedNotified = false;

  resetSignatureDocuments(matchingKey?: string): void {
    if (this.isContractFinalized) {
      return;
    }

    this.allDocumentsSignedNotified = false;

    for (const definition of this.signatureDocumentDefinitions) {
      if (matchingKey !== undefined && definition.key !== matchingKey) {
        continue;
      }

      const entry = this.signatureDocuments[definition.key];
      if (!entry) {
        continue;
      }

      entry.signatureDate = '';
      entry.signedSnapshot = null;
      entry.signedDocumentFile = null;
      this.acknowledgedSignatureDocuments[definition.key] = false;
    }
  }

  private unwrapContractResponse(response: unknown): Record<string, any> | null {
    if (!this.isRecord(response)) {
      return null;
    }

    if (this.isRecord(response['data'])) {
      return response['data'];
    }

    return response;
  }

  private applyContract(contract: Record<string, any>): void {
    this.isContractFinalized = this.toText(contract['signed_at']) !== '' || this.toText(contract['signature_data']) !== '';

    const rawContractDetails = this.isRecord(contract['contract_details_data'])
      ? contract['contract_details_data']
      : {};
    const normalizedContractDetails = this.normalizeLegacyKeys(rawContractDetails);
    const contractDetails: Record<string, any> = this.isRecord(normalizedContractDetails)
      ? normalizedContractDetails
      : {};
    this.dismissedChecklistDefaultDocumentScopes = this.extractPersistedDismissedChecklistDefaultDocumentScopes(
      contractDetails['dismissedChecklistDefaultDocumentScopes'],
    );
    this.managerObservationSignatureDefaultsInitialized = this.extractPersistedManagerObservationSignatureDefaultsState(
      contractDetails['managerObservationSignatureDefaultsApplied'],
      contractDetails['signatures'],
    );

    const contractProduct = this.isRecord(contract['product']) ? contract['product'] : null;
    const contractUser = this.isRecord(contract['user']) ? contract['user'] : null;
    const contractCountry = this.isRecord(contract['country']) ? contract['country'] : null;
    const leadName = this.buildUserDisplayName(contractUser);
    const siteAddressFromContract = [
      this.toText(contract['street_number']),
      this.toText(contract['street']),
      this.toText(contract['city']),
      this.firstText(contract['country_name'], contractCountry?.['name']),
    ]
      .filter((value) => value !== '')
      .join(', ');

    this.projectProductReference = this.firstText(
      contractDetails['projectProductReference'],
      contractDetails['project_product_reference'],
    );
    this.projectProductGamme = this.firstText(
      contractDetails['projectProductGamme'],
      contractDetails['project_product_gamme'],
      this.resolveProjectProductGammeFromCategory(this.firstText(
        contractDetails['projectProductCategory'],
        contractDetails['project_product_category'],
      )),
    );

    const persistedPvModules = this.isRecord(contractDetails['pvModules'])
      ? contractDetails['pvModules']
      : null;
    this.persistedPvModuleCount = persistedPvModules
      && Object.prototype.hasOwnProperty.call(persistedPvModules, 'moduleCount')
      ? this.toText(persistedPvModules['moduleCount'])
      : null;

    this.form.contractType = this.firstText(
      this.projectProductReference,
      contractProduct?.['name'],
      contract['contract_type'],
      contract['product_type'],
      this.form.contractType,
    );

    if (this.projectProductReference === '') {
      this.normalizeSelectedContractType();
      this.loadCurrentSignatureDocumentTemplates();
    } else {
      this.loadSignatureDocumentTemplates(
        this.lastSignatureDocumentBaseUrl ?? '',
        this.lastSignatureDocumentToken,
        this.toText(contract['product_type']),
      );
    }

    this.form.latitude = this.toText(contract['latitude']);
    this.form.longitude = this.toText(contract['longitude']);
    this.form.address.number = this.toText(contract['street_number']);
    this.form.address.street = this.toText(contract['street']);
    this.form.address.city = this.toText(contract['city']);
    this.form.address.country = this.firstText(
      contract['country_name'],
      contractCountry?.['name'],
      this.form.address.country,
    );
    this.form.contractDetails.contractReference = this.firstText(
      contractDetails['contractReference'],
      contract['contract_name'],
      this.form.contractDetails.contractReference,
    );
    this.form.contractDetails.fileReference = this.firstText(
      contractDetails['fileReference'],
      contractDetails['contractReference'],
      contract['contract_name'],
      this.form.contractDetails.fileReference,
    );

    if (this.projectProductReference !== '') {
      this.form.contractDetails.fileReference = this.buildProjectProductDossierReference(
        this.form.contractDetails.fileReference,
        this.toText(contract['created_at']),
      );
    }

    this.form.contractDetails.tenantClient = this.firstText(
      contractDetails['tenantClient'],
      leadName,
      this.form.contractDetails.tenantClient,
    );
    this.form.contractDetails.signatureDate = this.firstText(
      contractDetails['signatureDate'],
      this.form.contractDetails.signatureDate,
    );
    this.form.contractDetails.installationDate = this.firstText(
      contractDetails['installationDate'],
      this.form.contractDetails.installationDate,
    );
    this.form.contractDetails.commissioningDate = this.firstText(
      contractDetails['commissioningDate'],
      this.form.contractDetails.commissioningDate,
    );
    this.form.contractDetails.dossierParticularities = this.firstText(
      contractDetails['dossierParticularities'],
      this.form.contractDetails.dossierParticularities,
    );

    const contractParties = this.isRecord(contractDetails['parties'])
      ? contractDetails['parties']
      : {};
    const contractTenant = this.isRecord(contractParties['tenant'])
      ? contractParties['tenant']
      : (this.isRecord(contractDetails['tenant']) ? contractDetails['tenant'] : null);

    this.mergeInto(this.form.contractDetails.parties, contractParties);
    this.mergeInto(
      this.form.contractDetails.parties.landlord,
      this.isRecord(contractParties['landlord'])
        ? contractParties['landlord']
        : contractDetails['landlord'],
    );
    this.mergeInto(
      this.form.contractDetails.parties.technician,
      this.isRecord(contractParties['technician'])
        ? contractParties['technician']
        : contractDetails['technician'],
    );
    this.mergeInto(this.form.contractDetails.referenceConfig, contractDetails['referenceConfig']);
    this.mergeInto(this.form.contractDetails.cetBis, contractDetails['cetBis']);
    this.mergeInto(this.form.contractDetails.cetBis, contractDetails['cet_bis']);
    this.mergeInto(
      this.form.contractDetails.cetBis,
      Object.fromEntries(
        Object.keys(this.form.contractDetails.cetBis)
          .filter((key) => key in contractDetails)
          .map((key) => [key, contractDetails[key]]),
      ),
    );
    this.mergeInto(this.form.contractDetails.energyFrameworkGrid, contractDetails['energyFrameworkGrid']);
    this.mergeInto(this.form.contractDetails.energyFrameworkGrid, contractDetails['energy_framework_grid']);
    this.mergeInto(
      this.form.contractDetails.energyFrameworkGrid,
      Object.fromEntries(
        Object.keys(this.form.contractDetails.energyFrameworkGrid)
          .filter((key) => key in contractDetails)
          .map((key) => [key, contractDetails[key]]),
      ),
    );
    this.mergeInto(this.form.contractDetails.documentStatus, contractDetails['documentStatus']);
    this.mergeInto(this.form.contractDetails.documentStatus, contractDetails['document_status']);
    this.mergeDocumentStatusEntries(contractDetails['documentStatus']);
    this.mergeDocumentStatusEntries(contractDetails['document_status']);
    this.mergeInto(
      this.form.contractDetails.documentStatus,
      Object.fromEntries(
        Object.keys(this.form.contractDetails.documentStatus)
          .filter((key) => key in contractDetails)
          .map((key) => [key, contractDetails[key]]),
      ),
    );
    this.mergeInto(this.form.contractDetails.installedConfig, contractDetails['installedConfig']);
    this.mergeInto(this.form.contractDetails.pvModules, contractDetails['pvModules']);
    this.mergeInto(this.form.contractDetails.inverter, contractDetails['inverter']);
    this.mergeInto(this.form.contractDetails.battery, contractDetails['battery']);
    this.mergeInto(this.form.contractDetails.protections, contractDetails['protections']);
    this.mergeInto(this.form.contractDetails.monitoring, contractDetails['monitoring']);
    this.mergeInto(this.form.contractDetails.siteLayout, contractDetails['siteLayout']);
    this.mergeInto(this.form.contractDetails.associatedTechnicalInfrastructure, contractDetails['associatedTechnicalInfrastructure']);
    this.mergeInto(this.form.contractDetails.associatedTechnicalInfrastructure, contractDetails['associated_technical_infrastructure']);
    this.mergeInto(this.form.contractDetails.deviations, contractDetails['deviations']);
    this.mergeInto(this.form.contractDetails.expressDerogations, contractDetails['expressDerogations']);
    this.mergeInto(this.form.contractDetails.reservations, contractDetails['reservations']);
    this.mergeInto(this.form.contractDetails.financialTerms, contractDetails['financialTerms']);
    this.mergeInto(this.form.contractDetails.cebInformation, contractDetails['cebInformation']);
    this.mergeInto(this.form.contractDetails.mandatesPayment, contractDetails['mandatesPayment']);
    this.mergeInto(this.form.contractDetails.warrantiesSla, contractDetails['warrantiesSla']);
    this.mergeInto(this.form.contractDetails.documents, contractDetails['documents']);
    this.mergeInto(this.form.contractDetails.commissioningChecks, contractDetails['commissioningChecks']);
    this.hydrateDocumentFilesFromContractDocuments(contractDetails['documents']);
    this.applyPersistedDocumentProductSelections(contractDetails['documentProductSelections']);
    this.persistedSignatureDocumentGroupVisibility = this.extractPersistedSignatureDocumentGroupVisibility(
      contractDetails['signatureDocumentGroupVisibility'],
    );
    this.persistedDocumentSignatureStates = this.extractPersistedDocumentSignatureStates(
      contractDetails['documentSignatureStates'],
    );
    this.applyPersistedSignatureDocumentGroupVisibility();
    this.applyPersistedSignatureDocumentStates();
    this.mergeInto(
      this.form.contractDetails.additionalObservations,
      contractDetails['additionalObservations'],
    );
    this.mergeInto(
      this.form.contractDetails.transmission,
      contractDetails['transmission'],
    );
    this.mergeInto(
      this.form.contractDetails.transmission,
      contractDetails['validationTransmission'],
    );
    this.mergeInto(
      this.form.contractDetails.transmission,
      contractDetails['validation_transmission'],
    );
    this.mergeInto(this.form.contractDetails.signatures, contractDetails['signatures']);
    this.form.contractDetails.signatures.tenant.name = this.firstText(
      this.form.contractDetails.signatures.tenant.name,
      this.form.contractDetails.parties.tenant.name,
      this.form.contractDetails.tenantClient,
    );
    this.emitObservationSignatureStateChange();

    const referenceConfig = this.isRecord(contractDetails['referenceConfig'])
      ? contractDetails['referenceConfig']
      : null;
    this.form.contractDetails.referenceConfig.range = this.resolveReferenceTextValue(
      referenceConfig ? referenceConfig['range'] : null,
      this.srhRangeOptions,
      this.firstText(this.form.contractDetails.referenceConfig.range, this.selectedProductData?.label),
    );
    this.form.contractDetails.referenceConfig.commercialReference = this.resolveReferenceTextValue(
      referenceConfig ? referenceConfig['commercialReference'] : null,
      this.srhCommercialReferenceOptions,
      this.firstText(
        this.form.contractDetails.referenceConfig.commercialReference,
        this.buildCommercialReferenceKey(this.selectedProductData, this.getSelectedEnvironmentCode()),
      ),
    );

    if (this.projectProductReference !== '' && this.projectProductGamme !== '') {
      this.form.contractDetails.referenceConfig.range = this.projectProductGamme;
      this.form.contractDetails.referenceConfig.commercialReference = this.projectProductReference;
    }

    const siteAddress = this.firstText(
      contractDetails['siteConcerned'],
      this.form.contractDetails.parties.site.fullAddress,
      siteAddressFromContract,
      [
        this.form.address.number,
        this.form.address.street,
        this.form.address.city,
        this.form.address.country,
      ]
        .filter((value) => value.trim() !== '')
        .join(', '),
    );

    this.form.contractDetails.parties.site.fullAddress = siteAddress;
    this.form.contractDetails.parties.tenant.address = this.firstText(
      this.form.contractDetails.parties.tenant.address,
      siteAddress,
    );
    this.form.contractDetails.parties.tenant.companyName = this.firstText(
      this.form.contractDetails.parties.tenant.companyName,
      this.isRecord(contractTenant) ? this.toText(contractTenant['companyName']) : '',
      this.isRecord(contractTenant) ? this.toText(contractTenant['company_name']) : '',
      this.isRecord(contractTenant) ? this.toText(contractTenant['legalName']) : '',
      this.isRecord(contractTenant) ? this.toText(contractTenant['legal_name']) : '',
      this.toText(contractUser?.['company_name']),
      this.toText(contractUser?.['companyName']),
      this.toText(contract['company_name']),
      this.toText(contract['companyName']),
    );
    this.form.contractDetails.parties.tenant.name = this.firstText(
      this.form.contractDetails.parties.tenant.name,
      leadName,
      this.form.contractDetails.tenantClient,
    );
    this.form.contractDetails.parties.tenant.phone = this.firstText(
      this.form.contractDetails.parties.tenant.phone,
      contractUser?.['phone_number'],
    );
    this.form.contractDetails.parties.tenant.email = this.firstText(
      this.form.contractDetails.parties.tenant.email,
      contractUser?.['email'],
    );
    const financedByBank = this.firstText(
      contractDetails['financedByBank'],
      this.form.contractDetails.parties.tenant.financedByBank,
    );
    this.form.contractDetails.parties.tenant.financedByBank =
      (financedByBank.trim().toLowerCase() === SOLAR_RENT_BINARY_CHOICE_VALUES.no
        ? SOLAR_RENT_BINARY_CHOICE_VALUES.no
        : SOLAR_RENT_BINARY_CHOICE_VALUES.yes) as SolarRentBinaryChoiceValue;
    this.form.contractDetails.parties.tenant.civility = normalizeSolarRentTenantCivilityValue(
      this.form.contractDetails.parties.tenant.civility,
    );
    this.form.contractDetails.parties.site.occupationStatus = normalizeSolarRentOccupationStatusValue(
      this.form.contractDetails.parties.site.occupationStatus,
    );
    const paymentFrequency = normalizeSolarRentPaymentFrequencyValue(
      this.form.contractDetails.financialTerms.paymentFrequency,
    );
    this.form.contractDetails.financialTerms.paymentFrequency =
      (SOLAR_RENT_PAYMENT_FREQUENCY_VALUES.includes(paymentFrequency as SolarRentPaymentFrequencyValue)
        ? paymentFrequency
        : SOLAR_RENT_PAYMENT_FREQUENCY_VALUES[0]) as SolarRentPaymentFrequencyValue;
    const financialTerms = this.form.contractDetails.financialTerms;
    const normalizedPeriodicPaymentMode = normalizeSolarRentPeriodicPaymentModeValue(
      this.toText(financialTerms.periodicPaymentMode),
    );
    if (SOLAR_RENT_PERIODIC_PAYMENT_MODE_VALUES.includes(normalizedPeriodicPaymentMode as (typeof SOLAR_RENT_PERIODIC_PAYMENT_MODE_VALUES)[number])) {
      financialTerms.periodicPaymentMode = normalizedPeriodicPaymentMode;
    } else if (financialTerms.periodicPaymentModeDirectDebit) {
      financialTerms.periodicPaymentMode = 'direct_debit';
    } else if (financialTerms.periodicPaymentModeTransfer) {
      financialTerms.periodicPaymentMode = 'bank_transfer';
    } else if (financialTerms.periodicPaymentModeStandingOrder) {
      financialTerms.periodicPaymentMode = 'standing_order';
    } else if (financialTerms.periodicPaymentModeOther) {
      financialTerms.periodicPaymentMode = 'other';
    } else {
      financialTerms.periodicPaymentMode = '';
    }
    financialTerms.periodicPaymentModeDirectDebit = financialTerms.periodicPaymentMode === 'direct_debit';
    financialTerms.periodicPaymentModeTransfer = financialTerms.periodicPaymentMode === 'bank_transfer';
    financialTerms.periodicPaymentModeStandingOrder = financialTerms.periodicPaymentMode === 'standing_order';
    financialTerms.periodicPaymentModeOther = financialTerms.periodicPaymentMode === 'other';
    this.documentsResetSinceTypeChange = false;
    this.documentsChanged = false;
    this.hasManualDocumentEdits = false;
    this.pendingDocumentSelectionScopeChange = false;
    this.syncDerivedConfiguration();
    this.syncDocumentProductSelections();
    this.loadCurrentSignatureDocumentTemplates();

    if (this.solarProjectLeadProductPayload) {
      this.applySolarProjectLeadProduct(this.solarProjectLeadProductPayload);
    }
  }

  private hydrateDocumentFilesFromContractDocuments(rawDocuments: unknown): void {
    const documents = this.isRecord(rawDocuments) ? rawDocuments : {};

    for (const definition of this.contractDocumentDefinitions) {
      const key = definition.key;
      const rawEntry = documents[key];

      const entries = Array.isArray(rawEntry) ? rawEntry : rawEntry ? [rawEntry] : [];
      const normalizedEntries: SolarRentStoredFile[] = [];

      for (const entry of entries) {
        if (!this.isRecord(entry)) {
          continue;
        }

        const url = this.normalizeRemoteUrl(this.firstText(entry['url'], entry['path']));
        if (url === '') {
          continue;
        }

        const name = this.resolveRemoteDocumentName(entry['name'], url, key);
        const mimeType =
          this.toText(entry['mime_type'])
          || this.guessMimeTypeFromName(name)
          || 'application/octet-stream';

        const file = new File([new Blob([], { type: mimeType })], name, { type: mimeType });

        normalizedEntries.push({ name, url, file });
      }

      this.documentFiles[key] = normalizedEntries;

      if (this.isProductLinkedDocumentKey(key)) {
        this.documentProductSelections[key] = this.resolveDocumentProductSelection(entries);
      }
    }
  }

  private normalizeRemoteUrl(value: string): string {
    const raw = this.toText(value);
    if (raw === '') {
      return '';
    }

    if (raw.startsWith('data:')) {
      return raw;
    }

    if (raw.startsWith('blob:')) {
      return raw;
    }

    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }

    if (this.apiBaseUrl === '') {
      return raw;
    }

    if (raw.startsWith('/')) {
      return `${this.apiBaseUrl}${raw}`;
    }

    return `${this.apiBaseUrl}/${raw}`;
  }

  private resolveRemoteDocumentName(value: unknown, url: string, key: string): string {
    const explicitName = this.toText(value);
    if (explicitName !== '') {
      return explicitName;
    }

    const extractedName = this.extractRemoteDocumentNameFromUrl(url);
    if (extractedName !== '') {
      return extractedName;
    }

    return `${key}.pdf`;
  }

  private extractRemoteDocumentNameFromUrl(value: string): string {
    const raw = this.toText(value);
    if (raw === '') {
      return '';
    }

    try {
      const parsed = new URL(raw, this.apiBaseUrl || 'http://localhost');
      const queryName =
        parsed.searchParams.get('name_file')
        ?? parsed.searchParams.get('filename')
        ?? parsed.searchParams.get('file_name')
        ?? parsed.searchParams.get('file');

      const normalizedQueryName = this.toText(queryName);
      if (normalizedQueryName !== '') {
        return decodeURIComponent(normalizedQueryName);
      }

      const pathName = decodeURIComponent(parsed.pathname || '');
      const pathSegments = pathName.split('/').filter(Boolean);
      const trailingSegment = pathSegments[pathSegments.length - 1] ?? '';

      return trailingSegment.includes('.') ? trailingSegment : '';
    } catch {
      const pathWithoutQuery = decodeURIComponent(raw.split('?')[0] ?? raw);
      const pathSegments = pathWithoutQuery.split('/').filter(Boolean);
      const trailingSegment = pathSegments[pathSegments.length - 1] ?? '';

      return trailingSegment.includes('.') ? trailingSegment : '';
    }
  }

  private guessMimeTypeFromName(fileName: string): string | null {
    const lower = String(fileName || '').toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    if (lower.endsWith('.gif')) return 'image/gif';
    if (lower.endsWith('.webp')) return 'image/webp';
    if (lower.endsWith('.xls')) return 'application/vnd.ms-excel';
    if (lower.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (lower.endsWith('.doc')) return 'application/msword';
    if (lower.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    return null;
  }

  private syncDerivedConfiguration(): void {
    const selectedContractType = this.findContractTypeDefinition(this.form.contractType);
    const resolvedProductTypeCode = this.resolveSelectedProductTypeCode(this.form.contractType)
      || this.resolveProductTypeCodeFromValue(this.form.contractType)
      || this.getDefaultProductTypeCode();
    const productData = selectedContractType?.productData
      ?? this.resolveProductTypeProductData(resolvedProductTypeCode);
    const environmentCode = this.getSelectedEnvironmentCode();
    const summary = this.form.contractDetails.installedConfig.summary;
    summary.plannedEnvironment = environmentCode;
    summary.installedEnvironment = environmentCode;

    if (!selectedContractType && !productData) {
      this.syncContractReferencePreview();
      return;
    }

    if (selectedContractType) {
      this.form.contractType = selectedContractType.name;
    }

    if (!productData) {
      this.syncContractReferencePreview();
      return;
    }

    const commercialReferenceKey = this.buildCommercialReferenceKey(productData, environmentCode);
    const referenceConfig = this.form.contractDetails.referenceConfig;
    const projectProductReference = this.toText(this.projectProductReference);

    if (projectProductReference !== '') {
      if (this.projectProductGamme !== '') {
        referenceConfig.range = this.projectProductGamme;
      }
      referenceConfig.commercialReference = projectProductReference;
    } else {
      if (productData.rangeKey) {
        referenceConfig.range = this.firstText(selectedContractType?.label, productData.label, referenceConfig.range);
      }

      if (commercialReferenceKey !== '') {
        referenceConfig.commercialReference = this.firstText(
          this.resolveCommercialReferenceLabel(commercialReferenceKey),
          commercialReferenceKey,
          referenceConfig.commercialReference,
        );
      }
    }

    referenceConfig.plannedPvPower = productData.totalPvPower;
    referenceConfig.plannedBatteryCapacity = productData.batteryCapacity;
    referenceConfig.plannedInverter = getSolarRentInverterLabel(productData);
    this.setExclusiveSelection(
      referenceConfig,
      productData.monitoringIncluded ? 'plannedMonitoringYes' : 'plannedMonitoringNo',
      ['plannedMonitoringYes', 'plannedMonitoringNo'],
      true,
    );

    summary.plannedPv = productData.totalPvPower;
    summary.installedPv = productData.totalPvPower;
    summary.plannedBattery = productData.batteryCapacity;
    summary.installedBattery = productData.batteryCapacity;
    summary.plannedModuleCount = productData.panelCount;
    summary.installedModuleCount = productData.panelCount;
    summary.plannedInverter = getSolarRentInverterLabel(productData);
    summary.installedInverter = getSolarRentInverterLabel(productData);
    summary.plannedMonitoring = getSolarRentMonitoringLabel(productData);
    summary.installedMonitoring = getSolarRentMonitoringLabel(productData);
    this.applyProductDataDefaults(productData);
    this.syncContractReferencePreview();
  }

  private applyProductDataDefaults(productData: SolarRentProductData): void {
    const packageObservations = getSolarRentPackageObservations(productData);

    this.form.contractDetails.pvModules.brand = 'Generic solar module';
    this.form.contractDetails.pvModules.model = productData.panelUnitPower ? `${productData.panelUnitPower} W` : '';
    this.form.contractDetails.pvModules.unitPower = productData.panelUnitPower;
    this.form.contractDetails.pvModules.moduleCount = productData.panelCount;
    this.form.contractDetails.pvModules.totalPower = productData.totalPvPower;
    this.form.contractDetails.pvModules.observations = packageObservations;

    this.form.contractDetails.inverter.brand = getSolarRentBatteryBrand();
    this.form.contractDetails.inverter.model = getSolarRentInverterLabel(productData);
    this.form.contractDetails.inverter.nominalPower = productData.inverterPower;
    this.form.contractDetails.inverter.observations = packageObservations;

    this.form.contractDetails.battery.brand = getSolarRentBatteryBrand();
    this.form.contractDetails.battery.model = getSolarRentBatteryModel();
    this.form.contractDetails.battery.nominalCapacity = productData.batteryCapacity;
    this.form.contractDetails.battery.usableCapacity = productData.batteryCapacity;
    this.form.contractDetails.battery.unitCount = productData.batteryUnitCount;
    this.form.contractDetails.battery.observations = packageObservations;

    this.form.contractDetails.monitoring.application = getSolarRentMonitoringLabel(productData);
    this.form.contractDetails.monitoring.observations = packageObservations;
  }

  private buildPurposeStepPayload(): Record<string, any> {
    const selectedProduct = this.findContractTypeDefinition(this.form.contractType);
    const contractDetailsData = JSON.parse(JSON.stringify(this.form.contractDetails)) as Record<string, any>;

    contractDetailsData['client_locataire'] = this.form.contractDetails.tenantClient;
    contractDetailsData['date_signature'] = this.form.contractDetails.signatureDate;
    contractDetailsData['date_installation'] = this.form.contractDetails.installationDate;
    contractDetailsData['date_mise_en_service'] = this.form.contractDetails.commissioningDate;
    contractDetailsData['financed_by_bank'] = this.form.contractDetails.parties.tenant.financedByBank;
    contractDetailsData['signatureDocumentGroupVisibility'] = this.buildPersistedSignatureDocumentGroupVisibilityPayload();
    contractDetailsData['documentSignatureStates'] = this.buildPersistedDocumentSignatureStatesPayload();
    contractDetailsData['documentStatus'] = this.buildDocumentStatusPayload();

    // The "client was notified" flag only stays meaningful while every document is
    // still signed. Resetting or partially un-signing a contract must clear it,
    // otherwise the next signing round is silently suppressed.
    // The API deep-merges contract_details_data, so the key has to be sent as an
    // explicit null: simply omitting it would keep the stale value server-side.
    if (!this.areAllSignatureDocumentsSigned()) {
      contractDetailsData['allDocumentsSignedNotificationSent'] = null;
      this.allDocumentsSignedNotified = false;
    }
    contractDetailsData['documentProductSelections'] = this.buildPersistedDocumentProductSelectionsPayload();
    contractDetailsData['dismissedChecklistDefaultDocumentScopes'] = this.buildPersistedDismissedChecklistDefaultDocumentScopesPayload();
    contractDetailsData['managerObservationSignatureDefaultsApplied'] = this.buildPersistedManagerObservationSignatureDefaultsStatePayload();
    contractDetailsData['projectProductReference'] = this.projectProductReference;
    contractDetailsData['projectProductGamme'] = this.projectProductGamme;

    const resolvedProductType =
      this.resolveExistingProductTypeCode(this.toText(selectedProduct?.productType))
      || this.selectedProductTypeCode;
    const resolvedElectricalEnvironment =
      this.normalizeElectricalEnvironmentCode(this.toText(selectedProduct?.electricalEnvironment))
      || this.getSelectedEnvironmentCode();

    if (this.documentsResetSinceTypeChange || this.documentsChanged) {
      contractDetailsData['documents'] = this.buildContractDocumentsPayloadFromState();
    }

    const locationType = this.toText(this.form.contractDetails.parties.site.propertyType);
    const occupationStatus = this.toText(this.form.contractDetails.parties.site.occupationStatus);
    const latitude = this.toText(this.form.latitude);
    const longitude = this.toText(this.form.longitude);
    const streetNumber = this.toText(this.form.address.number);
    const street = this.toText(this.form.address.street);
    const city = this.toText(this.form.address.city);

    return {
      ...(resolvedProductType ? { product_type: resolvedProductType } : {}),
      ...(resolvedElectricalEnvironment ? { electrical_environment: resolvedElectricalEnvironment } : {}),
      ...(locationType ? { location_type: locationType } : {}),
      ...(occupationStatus ? { occupation_status: occupationStatus } : {}),
      ...(latitude ? { latitude } : {}),
      ...(longitude ? { longitude } : {}),
      ...(streetNumber ? { street_number: streetNumber } : {}),
      ...(street ? { street } : {}),
      ...(city ? { city } : {}),
      contract_details_data: contractDetailsData,
    };
  }

  private captureCurrentPurposeStepPayload(): void {
    this.lastSavedPurposeStepPayload = this.serializePurposeStepPayload(this.buildPurposeStepPayload());
  }

  private serializePurposeStepPayload(payload: Record<string, any>): string {
    return JSON.stringify(payload);
  }

  private buildClearedContractDocumentsPayload(): Record<string, any> {
    const documents: Record<string, any> = {
      other_details: '',
    };

    for (const definition of this.contractDocumentDefinitions) {
      documents[definition.key] = null;
    }

    return documents;
  }

  private applyDefaultSettings(response: unknown): void {
    const partyDefaults: Record<SolarRentPartyKey, Record<string, any>> = {
      landlord: this.normalizeDefaultSettingValue(SOLAR_RENT_PARTY_DEFAULT_SETTINGS.landlord),
      technician: this.normalizeDefaultSettingValue(SOLAR_RENT_PARTY_DEFAULT_SETTINGS.technician),
    };

    let documentChecklistDefaults: Record<string, any> | null = null;
    let pvModulesDefaults: Record<string, any> | null = null;
    let batteryDefaults: Record<string, any> | null = null;
    let inverterDefaults: Record<string, any> | null = null;
    let customerServiceDefaults: Record<string, any> = {};
    let hasCustomerServiceSetting = false;

    for (const item of this.unwrapDefaultSettingsResponse(response)) {
      const section = this.firstText(item.section, item.section_name);

      if (section === 'application_documents_checklist') {
        documentChecklistDefaults = this.normalizeDefaultSettingValue(item.value);
      }

      if (section === SOLAR_RENT_TECHNICAL_DEFAULT_SETTING_SECTION_KEYS.pvModules) {
        pvModulesDefaults = this.normalizeDefaultSettingValue(item.value);
      }

      if (section === SOLAR_RENT_TECHNICAL_DEFAULT_SETTING_SECTION_KEYS.battery) {
        batteryDefaults = this.normalizeDefaultSettingValue(item.value);
      }

      if (section === SOLAR_RENT_TECHNICAL_DEFAULT_SETTING_SECTION_KEYS.inverter) {
        inverterDefaults = this.normalizeDefaultSettingValue(item.value);
      }

      if (section === 'customer_service') {
        customerServiceDefaults = this.normalizeDefaultSettingValue(item.value);
        hasCustomerServiceSetting = true;
      }

      const partyKey = this.resolveDefaultSettingPartyKey(item);
      if (!partyKey) {
        continue;
      }

      partyDefaults[partyKey] = {
        ...partyDefaults[partyKey],
        ...this.normalizeDefaultSettingValue(item.value),
      };
    }

    this.pvMaterialsCatalog = this.normalizePvMaterialsCatalog(pvModulesDefaults);
    this.batteryMaterialsCatalog = this.normalizeBatteryMaterialsCatalog(batteryDefaults);
    this.inverterMaterialsCatalog = this.normalizeInverterMaterialsCatalog(inverterDefaults);
    if (hasCustomerServiceSetting) {
      this.customerServiceContact = {
        email: this.toText(customerServiceDefaults['email']),
        phone: this.toText(customerServiceDefaults['phone']),
      };
    }

    const shouldOverwritePartyDefaults = this.shouldApplyManagerTechnicalDefaults();
    const technicianPlatformApplication = this.toText(
      partyDefaults.technician['platformApplication'] ?? partyDefaults.technician['platform_application'],
    );

    if (shouldOverwritePartyDefaults) {
      this.overwriteValues(this.form.contractDetails.parties.landlord, partyDefaults.landlord);
      this.overwriteValues(this.form.contractDetails.parties.technician, partyDefaults.technician);
      this.overwriteValues(this.form.contractDetails.referenceConfig, {
        incidentReportingPlatform: technicianPlatformApplication,
      });
    } else {
      this.fillMissingValues(this.form.contractDetails.parties.landlord, partyDefaults.landlord);
      this.fillMissingValues(this.form.contractDetails.parties.technician, partyDefaults.technician);
      this.fillMissingValues(this.form.contractDetails.referenceConfig, {
        incidentReportingPlatform: technicianPlatformApplication,
      });
    }

    if (this.applyMissingManagerObservationSignatureDefaults(partyDefaults)) {
      this.emitObservationSignatureStateChange();
    }

    if (this.shouldApplyManagerTechnicalDefaults()) {
      this.applyManagerPvModulesDefaults(pvModulesDefaults);
      this.applyManagerBatteryDefaults(batteryDefaults);
      this.applyManagerInverterDefaults(inverterDefaults);
    }

    this.documentChecklistDefaults = documentChecklistDefaults;

    if (documentChecklistDefaults) {
      this.applyChecklistDefaultDocuments(documentChecklistDefaults);
    }
  }

  private shouldApplyManagerTechnicalDefaults(): boolean {
    return this.loadedContractId === null && !this.isLoadingContract;
  }

  applyPvMaterialFromCatalog(index: number): void {
    if (this.isContractFinalized) {
      return;
    }

    const material = this.pvMaterialsCatalog[index];
    if (!material) {
      return;
    }

    const pvModules = this.form.contractDetails.pvModules;
    pvModules.brand = material.brand;
    pvModules.model = material.model;
    pvModules.unitPower = material.unitPower;
    pvModules.serialNumbers = material.serialNumber;
    pvModules.moduleTableRows = {
      1: {
        brand: material.brand,
        model: material.model,
        unitPower: material.unitPower,
        serialNumber: material.serialNumber,
      },
    };
    pvModules.moduleCount = '1';

    const parsedPower = this.parseDecimalValue(material.unitPower);
    if (parsedPower !== null && parsedPower > 0) {
      pvModules.totalPower = this.formatTrimmedDecimal(parsedPower / 1000);
    }
  }

  applyBatteryMaterialFromCatalog(index: number): void {
    if (this.isContractFinalized) {
      return;
    }

    const material = this.batteryMaterialsCatalog[index];
    if (!material) {
      return;
    }

    const battery = this.form.contractDetails.battery;
    battery.brand = material.brand;
    battery.model = material.model;
    battery.nominalCapacity = material.capacity;
    battery.usableCapacity = material.capacity;
    battery.serialNumbers = material.serialNumber;
    battery.batteryTableRows = {
      1: {
        brand: material.brand,
        model: material.model,
        capacity: material.capacity,
        serialNumber: material.serialNumber,
      },
    };
    battery.unitCount = '1';
  }

  applyInverterMaterialFromCatalog(index: number): void {
    if (this.isContractFinalized) {
      return;
    }

    const material = this.inverterMaterialsCatalog[index];
    if (!material) {
      return;
    }

    const inverter = this.form.contractDetails.inverter;
    inverter.brand = material.brand;
    inverter.model = material.model;
    inverter.nominalPower = material.nominalPower;
    inverter.serialNumber = material.serialNumber;
    inverter.location = material.location;
    inverter.hybridType = material.type === 'hybrid';
    inverter.otherType = material.type === 'other';
    inverter.otherTypeDetails = material.type === 'other' ? material.otherTypeDetails : '';
  }

  private normalizeMaterialsItems(value: Record<string, any> | null): Record<string, any>[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value['items'])) {
      return value['items'].filter((item): item is Record<string, any> => this.isRecord(item));
    }

    return this.isRecord(value) ? [value] : [];
  }

  private normalizePvMaterialsCatalog(value: Record<string, any> | null): SolarRentPvMaterialCatalogItem[] {
    return this.normalizeMaterialsItems(value)
      .map((item) => ({
        brand: this.firstText(item['brand']),
        model: this.firstText(item['model']),
        unitPower: this.firstText(item['unitPower'], item['unit_power']),
        serialNumber: this.firstText(item['serialNumber'], item['serial_number']),
      }))
      .filter((item) => [item.brand, item.model, item.unitPower, item.serialNumber].some((entry) => entry !== ''));
  }

  private normalizeBatteryMaterialsCatalog(value: Record<string, any> | null): SolarRentBatteryMaterialCatalogItem[] {
    return this.normalizeMaterialsItems(value)
      .map((item) => ({
        brand: this.firstText(item['brand']),
        model: this.firstText(item['model']),
        capacity: this.firstText(item['capacity']),
        serialNumber: this.firstText(item['serialNumber'], item['serial_number']),
      }))
      .filter((item) => [item.brand, item.model, item.capacity, item.serialNumber].some((entry) => entry !== ''));
  }

  private normalizeInverterMaterialsCatalog(value: Record<string, any> | null): SolarRentInverterMaterialCatalogItem[] {
    return this.normalizeMaterialsItems(value)
      .map((item) => {
        const type: 'hybrid' | 'other' = this.firstText(item['type']).toLowerCase() === 'other'
          ? 'other'
          : 'hybrid';

        return {
          brand: this.firstText(item['brand']),
          model: this.firstText(item['model']),
          nominalPower: this.firstText(item['nominalPower'], item['nominal_power']),
          serialNumber: this.firstText(item['serialNumber'], item['serial_number']),
          location: this.firstText(item['location']),
          type,
          otherTypeDetails: type === 'other'
            ? this.firstText(item['otherTypeDetails'], item['other_type_details'])
            : '',
        };
      })
      .filter((item) => [item.brand, item.model, item.nominalPower, item.serialNumber, item.location, item.otherTypeDetails].some((entry) => entry !== ''));
  }

  private primaryMaterialEntry(value: Record<string, any> | null): Record<string, any> | null {
    const items = this.normalizeMaterialsItems(value);
    return items[0] ?? null;
  }

  private applyManagerPvModulesDefaults(value: Record<string, any> | null): void {
    const source = this.primaryMaterialEntry(value);
    if (!source) {
      return;
    }

    const brand = this.firstText(source['brand']);
    const model = this.firstText(source['model']);
    const unitPower = this.firstText(source['unitPower'], source['unit_power']);
    const serialNumber = this.firstText(source['serialNumber'], source['serial_number']);

    const hasAnyValue = [brand, model, unitPower, serialNumber].some((entry) => entry !== '');
    if (!hasAnyValue) {
      return;
    }

    const pvModules = this.form.contractDetails.pvModules;

    if (brand !== '') {
      pvModules.brand = brand;
    }

    if (model !== '') {
      pvModules.model = model;
    }

    if (unitPower !== '') {
      pvModules.unitPower = unitPower;
    }

    if (serialNumber !== '') {
      pvModules.serialNumbers = serialNumber;
    }

    const rowUnitPower = unitPower !== '' ? unitPower : pvModules.unitPower;
    pvModules.moduleTableRows = {
      1: {
        brand: brand !== '' ? brand : pvModules.brand,
        model: model !== '' ? model : pvModules.model,
        unitPower: rowUnitPower,
        serialNumber,
      },
    };
    pvModules.moduleCount = '1';

    const parsedPower = this.parseDecimalValue(rowUnitPower);
    if (parsedPower !== null && parsedPower > 0) {
      pvModules.totalPower = this.formatTrimmedDecimal(parsedPower / 1000);
    }
  }

  private applyManagerBatteryDefaults(value: Record<string, any> | null): void {
    const source = this.primaryMaterialEntry(value);
    if (!source) {
      return;
    }

    const brand = this.firstText(source['brand']);
    const model = this.firstText(source['model']);
    const capacity = this.firstText(source['capacity']);
    const serialNumber = this.firstText(source['serialNumber'], source['serial_number']);

    const hasAnyValue = [brand, model, capacity, serialNumber].some((entry) => entry !== '');
    if (!hasAnyValue) {
      return;
    }

    const battery = this.form.contractDetails.battery;

    if (brand !== '') {
      battery.brand = brand;
    }

    if (model !== '') {
      battery.model = model;
    }

    if (capacity !== '') {
      battery.nominalCapacity = capacity;
      battery.usableCapacity = capacity;
    }

    if (serialNumber !== '') {
      battery.serialNumbers = serialNumber;
    }

    battery.batteryTableRows = {
      1: {
        brand: brand !== '' ? brand : battery.brand,
        model: model !== '' ? model : battery.model,
        capacity: capacity !== '' ? capacity : battery.nominalCapacity,
        serialNumber,
      },
    };
    battery.unitCount = '1';
  }

  private applyManagerInverterDefaults(value: Record<string, any> | null): void {
    const source = this.primaryMaterialEntry(value);
    if (!source) {
      return;
    }

    const brand = this.firstText(source['brand']);
    const model = this.firstText(source['model']);
    const nominalPower = this.firstText(source['nominalPower'], source['nominal_power']);
    const serialNumber = this.firstText(source['serialNumber'], source['serial_number']);
    const location = this.firstText(source['location']);
    const type = this.firstText(source['type']).toLowerCase() === 'other' ? 'other' : 'hybrid';
    const otherTypeDetails = this.firstText(source['otherTypeDetails'], source['other_type_details']);

    const hasAnyValue = [brand, model, nominalPower, serialNumber, location, otherTypeDetails].some((entry) => entry !== '')
      || Object.prototype.hasOwnProperty.call(source, 'type');

    if (!hasAnyValue) {
      return;
    }

    const inverter = this.form.contractDetails.inverter;

    if (brand !== '') {
      inverter.brand = brand;
    }

    if (model !== '') {
      inverter.model = model;
    }

    if (nominalPower !== '') {
      inverter.nominalPower = nominalPower;
    }

    if (serialNumber !== '') {
      inverter.serialNumber = serialNumber;
    }

    if (location !== '') {
      inverter.location = location;
    }

    inverter.hybridType = type === 'hybrid';
    inverter.otherType = type === 'other';
    inverter.otherTypeDetails = inverter.otherType ? otherTypeDetails : '';
  }

  private parseDecimalValue(value: string): number | null {
    const normalizedValue = value.replace(/\s+/g, '').replace(',', '.');
    const matchedValue = normalizedValue.match(/-?\d+(?:\.\d+)?/);

    if (!matchedValue) {
      return null;
    }

    const parsedValue = Number.parseFloat(matchedValue[0]);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private formatTrimmedDecimal(value: number): string {
    return value.toFixed(3).replace(/\.0+$|(?<=\.\d*[1-9])0+$/g, '');
  }

  private applyChecklistDefaultDocuments(value: Record<string, any>): void {
    const scope = this.findChecklistDocumentDefaultsScope(value);

    if (!scope) {
      return;
    }

    const scoped = scope.value;

    console.log('[SolarRentWizard] Checklist defaults resolved', {
      keys: Object.keys(scoped),
    });

    for (const key of PRODUCT_LINKED_DOCUMENT_KEYS) {
      if ((this.documentFiles[key] ?? []).length > 0) {
        continue;
      }

      const rawEntries = scoped[key];
      const entries = Array.isArray(rawEntries) ? rawEntries : rawEntries ? [rawEntries] : [];
      const normalized = this.normalizeChecklistDefaultDocumentEntries(entries, key);
      const resolvedSelection = this.resolveDocumentProductSelection(entries);

      if (this.isChecklistDefaultDocumentDismissed(key, scope.signature)) {
        if (this.toText(this.documentProductSelections[key]) === '') {
          this.documentProductSelections[key] = resolvedSelection;
        }
        continue;
      }

      if (normalized.length > 0) {
        this.documentFiles[key] = this.replaceFileEntries(this.documentFiles[key], normalized);
        this.documentProductSelections[key] = resolvedSelection;
      }
    }

    console.log('[SolarRentWizard] Checklist defaults applied', {
      singleLineDiagramCount: (this.documentFiles['single_line_diagram'] ?? []).length,
      equipmentDatasheetsCount: (this.documentFiles['equipment_datasheets'] ?? []).length,
    });
    this.syncDocumentProductSelections();
    this.hasManualDocumentEdits = false;
  }

  private resolveChecklistDocumentDefaultsScope(
    value: Record<string, any>,
    selectedProduct: SolarRentContractTypeDefinition | null = null,
    selectedProductValue = '',
  ): Record<string, any> | null {
    return this.findChecklistDocumentDefaultsScope(value, selectedProduct, selectedProductValue)?.value ?? null;
  }

  private findChecklistDocumentDefaultsScope(
    value: Record<string, any>,
    selectedProduct: SolarRentContractTypeDefinition | null = null,
    selectedProductValue = '',
  ): SolarRentChecklistDocumentDefaultsScope | null {
    const directScope = this.extractChecklistDocumentDefaultsBlock(value);
    if (directScope) {
      return {
        value: directScope,
        signature: 'direct',
      };
    }

    const scopeKeys = this.buildChecklistDefaultScopeKeys(selectedProduct, selectedProductValue);
    const containers: Array<{ key: string; value: Record<string, any> }> = [
      { key: 'root', value },
      { key: 'contract_type_defaults', value: this.isRecord(value['contract_type_defaults']) ? value['contract_type_defaults'] : {} },
      { key: 'product_type_defaults', value: this.isRecord(value['product_type_defaults']) ? value['product_type_defaults'] : {} },
      { key: 'product_defaults', value: this.isRecord(value['product_defaults']) ? value['product_defaults'] : {} },
      { key: 'defaults_by_product', value: this.isRecord(value['defaults_by_product']) ? value['defaults_by_product'] : {} },
      { key: 'products', value: this.isRecord(value['products']) ? value['products'] : {} },
    ];

    for (const container of containers) {
      if (!this.isRecord(container.value)) {
        continue;
      }

      for (const scopeKey of scopeKeys) {
        const scopedCandidate = container.value[scopeKey];
        if (!this.isRecord(scopedCandidate)) {
          continue;
        }

        const scoped = this.extractChecklistDocumentDefaultsBlock(scopedCandidate);
        if (scoped) {
          return {
            value: scoped,
            signature: `${container.key}:${scopeKey}`,
          };
        }
      }
    }

    return null;
  }

  private extractChecklistDocumentDefaultsBlock(value: unknown): Record<string, any> | null {
    if (!this.isRecord(value)) {
      return null;
    }

    return PRODUCT_LINKED_DOCUMENT_KEYS.some((key) => key in value)
      ? value
      : null;
  }

  private buildChecklistDefaultScopeKeys(
    selectedProduct: SolarRentContractTypeDefinition | null = null,
    selectedProductValue = '',
  ): string[] {
    const keys: string[] = [];
    const resolvedProductDefinition = selectedProduct
      ?? this.findContractTypeDefinition(this.form.contractType);
    const productTypeCode = this.normalizeProductTypeCode(
      resolvedProductDefinition?.productType ?? this.selectedProductTypeCode,
    );
    const environmentCode = this.normalizeElectricalEnvironmentCode(
      resolvedProductDefinition?.electricalEnvironment ?? this.getSelectedEnvironmentCode(),
    );
    const environmentLower = environmentCode.toLowerCase();
    const productTypeAliases = this.buildProductTypeAliases(productTypeCode);
    const contractTypeKey = resolvedProductDefinition?.productType
      ? this.resolveContractTypeKeyFromValue(resolvedProductDefinition.productType)
      : this.resolveChecklistContractTypeKey();
    const contractTypeName = this.toText(this.form.contractType);
    const selectedProductName = this.toText(resolvedProductDefinition?.name) || this.toText(selectedProductValue);
    const selectedProductId = resolvedProductDefinition?.id;
    const normalizedContractSelection = this.normalizeProductSelectionKey(contractTypeName);
    const normalizedSelectedProduct = this.normalizeProductSelectionKey(selectedProductName);

    const addKey = (candidate: string) => {
      const normalized = this.toText(candidate);
      if (normalized === '' || keys.includes(normalized)) {
        return;
      }
      keys.push(normalized);
    };

    addKey(contractTypeKey);
    addKey(contractTypeName);
    addKey(selectedProductName);
    addKey(selectedProductId !== null && selectedProductId !== undefined ? String(selectedProductId) : '');
    addKey(normalizedContractSelection);
    addKey(normalizedSelectedProduct);

    for (const alias of productTypeAliases) {
      addKey(alias);
      addKey(alias.toLowerCase());
      addKey(alias.toUpperCase());
      addKey(this.normalizeProductSelectionKey(alias));

      if (environmentCode !== '') {
        addKey(`${alias}-${environmentLower}`);
        addKey(`${alias}_${environmentLower}`);
        addKey(`${alias}${environmentLower}`);
        addKey(`${alias}-${environmentCode}`);
        addKey(`${alias}_${environmentCode}`);
        addKey(`${alias}${environmentCode}`);
      }
    }

    return keys;
  }

  private normalizeChecklistDefaultDocumentEntries(entries: unknown[], key: string): SolarRentStoredFile[] {
    const normalized: SolarRentStoredFile[] = [];

    for (const entry of entries) {
      if (!this.isRecord(entry)) {
        continue;
      }

      const url = this.normalizeRemoteUrl(this.toText(entry['link'] ?? entry['url'] ?? ''));
      if (url === '') {
        continue;
      }

      const name = this.resolveRemoteDocumentName(entry['name'], url, key);
      const mimeType =
        this.toText(entry['mime_type'])
        || this.guessMimeTypeFromName(name)
        || 'application/octet-stream';
      const file = new File([new Blob([], { type: mimeType })], name, { type: mimeType });
      normalized.push({ name, url, file });
    }

    return normalized;
  }

  private buildProductTypeAliases(productTypeCode: string): string[] {
    const normalized = this.normalizeProductTypeCode(productTypeCode);
    if (normalized === '') {
      return [];
    }

    const compact = normalized.replace(/_KWH$/i, '').replace(/_/g, '');
    const numeric = compact.replace(/[^0-9]/g, '');
    const aliases = [
      normalized,
      normalized.replace(/_/g, ' '),
      compact,
      numeric,
      normalized.replace(/_KWH$/i, ''),
    ];

    return aliases
      .map((value) => this.toText(value))
      .filter((value) => value !== '')
      .filter((value, index, values) => values.indexOf(value) === index);
  }

  private resolveChecklistContractTypeKey(): string {
    const selected = this.getSelectedRangeOption();
    if (!selected) {
      return '';
    }

    return this.resolveContractReferencePrefixFromRangeKey(selected.key);
  }

  private getSelectedContractTypeCode(): string {
    const fromRange = this.resolveChecklistContractTypeKey();
    if (fromRange !== '') {
      return fromRange;
    }

    return this.resolveContractReferencePrefixFromProductType(this.selectedProductTypeCode);
  }

  private buildAvailableProductTypeEntries(): Array<{ code: string; name: string }> {
    if (this.productTypeOptions.length > 0) {
      const entries = this.productTypeOptions
        .filter((option) => option.code !== '')
        .map((option) => ({
          code: option.code,
          name: option.name,
        }));

      const selectedCode = this.selectedProductTypeCode;
      if (selectedCode !== '' && !entries.some((entry) => entry.code === selectedCode)) {
        entries.unshift({
          code: selectedCode,
          name: this.getProductTypeLabel(selectedCode),
        });
      }

      return entries;
    }

    return this.availableProductTypeCodes.map((code) => ({
      code,
      name: this.getProductTypeLabel(code),
    }));
  }

  private buildAvailableElectricalEnvironmentEntries(): Array<{ code: string; name: string }> {
    return this.electricalEnvironmentOptions
      .filter((option) => option.code !== '' && option.active !== false)
      .map((option) => ({
        code: option.code,
        name: option.name || option.code,
      }));
  }

  private buildProductTypeSelectOptionsCacheKey(): string {
    return [
      this.productTypeLoadError,
      this.selectedProductTypeCode,
      this.productTypeOptions
        .map((option) => `${option.id}:${option.code}:${option.name}:${option.active !== false}`)
        .join('|'),
      this.contractTypeDefinitions
        .map((definition) => `${definition.id ?? ''}:${definition.name}:${definition.productType}`)
        .join('|'),
    ].join('||');
  }

  private buildElectricalEnvironmentSelectOptionsCacheKey(): string {
    return this.electricalEnvironmentOptions
      .map((option) => `${option.id}:${option.code}:${option.name}:${option.active !== false}`)
      .join('|');
  }

  private buildSharedDocumentProductSelectOptionsCacheKey(): string {
    return [
      this.form.contractType,
      this.selectedProductTypeCode,
      this.getSelectedEnvironmentCode(),
      this.contractTypeDefinitions
        .map(
          (definition) =>
            `${definition.id ?? ''}:${definition.name}:${definition.productType}:${definition.electricalEnvironment}:${definition.label}`,
        )
        .join('|'),
    ].join('||');
  }

  private resolveContractTypeKeyFromValue(value: string): string {
    return this.resolveContractReferencePrefixFromValue(value);
  }

  private applyCurrentContractTypeDefaultDocuments(): void {
    if (!this.documentChecklistDefaults) {
      return;
    }

    this.applyChecklistDefaultDocuments(this.documentChecklistDefaults);
  }

  private syncContractReferencePreview(): void {
    const nextReference = this.buildContractReferencePreview();

    if (nextReference !== '') {
      this.form.contractDetails.contractReference = nextReference;
    }
  }

  private buildContractReferencePreview(): string {
    const referencePrefix = this.resolveContractReferencePreviewPrefix();
    if (referencePrefix === '') {
      return this.toText(this.form.contractDetails.contractReference);
    }

    const currentReference = this.toText(this.form.contractDetails.contractReference);
    const currentMatch = currentReference.match(/^[A-Z0-9-]+-(\d{4})(?:-(\d{2}))?-(\d{4})$/i);
    const fileReference = this.toText(this.form.contractDetails.fileReference);
    const dossierMatch = fileReference.match(/^[A-Z0-9]+(?:-[A-Z0-9]+)*-(\d{4})(?:-(\d{2}))?-(\d{4})$/i);
    const sequence = currentMatch?.[3]
      ?? dossierMatch?.[3]
      ?? this.resolveContractReferenceSequence();
    const year = currentMatch?.[1]
      ?? dossierMatch?.[1]
      ?? String(new Date().getFullYear());

    if (sequence === '') {
      return currentReference;
    }

    return `${referencePrefix}-${year}-${sequence}`;
  }

  private buildProjectProductDossierReference(currentReference: string, createdAt: string): string {
    const prefix = this.normalizeContractReferencePrefix(this.projectProductReference);
    if (prefix === '') {
      return currentReference;
    }

    const currentMatch = this.toText(currentReference).match(/^[A-Z0-9-]+-(\d{4})(?:-(\d{2}))?-(\d{4})$/i);
    const createdAtDate = createdAt !== '' ? new Date(createdAt) : null;
    const year = currentMatch?.[1]
      ?? (createdAtDate && !Number.isNaN(createdAtDate.getTime()) ? String(createdAtDate.getFullYear()) : String(new Date().getFullYear()));
    const month = currentMatch?.[2]
      ?? (createdAtDate && !Number.isNaN(createdAtDate.getTime()) ? String(createdAtDate.getMonth() + 1).padStart(2, '0') : String(new Date().getMonth() + 1).padStart(2, '0'));
    const sequence = currentMatch?.[3] ?? this.resolveContractReferenceSequence();

    if (sequence === '') {
      return currentReference;
    }

    return `${prefix}-${year}-${month}-${sequence}`;
  }

  private resolveContractReferencePreviewPrefix(): string {
    const projectProductReference = this.toText(this.projectProductReference);

    if (projectProductReference !== '') {
      return this.normalizeContractReferencePrefix(projectProductReference);
    }

    return this.getSelectedContractTypeCode();
  }

  private resolveProjectProductGammeFromCategory(category: string): string {
    switch (this.toText(category).toLowerCase()) {
      case 'residential':
        return 'Residential';
      case 'commercial':
      case 'industrial':
        return 'Business';
      default:
        return '';
    }
  }

  private buildContractDocumentsPayloadFromState(): Record<string, any> {
    const documents = this.buildClearedContractDocumentsPayload();

    for (const definition of this.contractDocumentDefinitions) {
      const key = definition.key;
      const entries = (this.documentFiles[key] ?? [])
        .map((entry) => this.buildContractDocumentPayloadEntry(key, entry))
        .filter((entry): entry is Record<string, any> => entry !== null);

      if (entries.length === 0) {
        continue;
      }

      documents[key] = entries;
    }

    return documents;
  }

  private buildContractDocumentPayloadEntry(key: string, entry: SolarRentStoredFile | null | undefined): Record<string, any> | null {
    if (!entry) {
      return null;
    }

    const normalizedUrl = this.toText(entry.url);
    if (normalizedUrl === '' || normalizedUrl.startsWith('blob:')) {
      return null;
    }

    const payload: Record<string, any> = {
      url: normalizedUrl,
      name: this.toText(entry.name) || null,
    };

    if (!this.isProductLinkedDocumentKey(key)) {
      return payload;
    }

    const selectedProduct = this.findDocumentProductDefinition(
      this.toNumber(this.documentProductSelections[key]),
      this.toText(this.documentProductSelections[key]),
    );

    return {
      ...payload,
      ...(selectedProduct?.id ? { product_id: selectedProduct.id } : {}),
      ...(selectedProduct?.name ? { product_name: selectedProduct.name } : {}),
      ...(selectedProduct?.productType ? { product_type: selectedProduct.productType } : {}),
      ...(selectedProduct?.electricalEnvironment
        ? { electrical_environment: selectedProduct.electricalEnvironment }
        : {}),
    };
  }

  private resolveContractReferenceSequence(): string {
    const contractId = Number.parseInt(this.toText(this.loadedContractId), 10);

    if (Number.isFinite(contractId) && contractId > 0) {
      return String(contractId).padStart(4, '0');
    }

    return '';
  }

  private resetContractDocuments(): void {
    for (const definition of this.contractDocumentDefinitions) {
      this.documentFiles[definition.key] = this.replaceFileEntries(this.documentFiles[definition.key], []);
    }
    for (const key of PRODUCT_LINKED_DOCUMENT_KEYS) {
      this.documentProductSelections[key] = '';
    }

    this.form.contractDetails.documents.otherDetails = '';
    this.documentsResetSinceTypeChange = true;
    this.documentsChanged = true;
    this.hasManualDocumentEdits = false;
  }

  private setContractTypeDefinitions(definitions: SolarRentContractTypeDefinition[]): void {
    const normalizedDefinitions = definitions.filter(
      (definition, index, values) => definition.name !== '' && values.findIndex((entry) => entry.name === definition.name) === index,
    ).map((definition) => this.enrichContractTypeDefinition(definition));
    const currentType = this.toText(this.form.contractType);

    this.contractTypeDefinitions = normalizedDefinitions;
    this.contractTypeOptions = normalizedDefinitions.map(({ name }) => name);
    this.srhRangeOptions = normalizedDefinitions
      .filter((definition) => !!definition.productData?.rangeKey)
      .map((definition) => ({
        key: definition.productData!.rangeKey,
        label: definition.label,
      }))
      .filter((option, index, values) => values.findIndex((entry) => entry.key === option.key) === index);
    this.srhCommercialReferenceOptions = normalizedDefinitions.reduce<SolarRentOption[]>(
      (options, definition) => options.concat(this.buildCommercialReferenceOptions(definition)),
      [],
    );

    const normalizedCurrentType = this.normalizeContractTypeValue(currentType);

    if (normalizedCurrentType !== '' && !this.contractTypeOptions.includes(normalizedCurrentType)) {
      this.contractTypeDefinitions.unshift({
        id: null,
        name: normalizedCurrentType,
        code: normalizedCurrentType,
        label: currentType,
        productType: this.resolveProductTypeCodeFromValue(currentType || normalizedCurrentType),
        electricalEnvironment: this.getSelectedEnvironmentCode(),
        productData: this.resolveProductTypeProductData(
          this.resolveProductTypeCodeFromValue(currentType || normalizedCurrentType),
        ),
      });
      this.contractTypeOptions.unshift(normalizedCurrentType);
    }

    if (normalizedCurrentType === '') {
      const defaultProductType = this.getDefaultProductTypeCode();
      const environmentCode = this.getSelectedEnvironmentCode();
      const defaultDefinition = this.findContractTypeByProductTypeAndEnvironment(defaultProductType, environmentCode)
        ?? this.contractTypeDefinitions.find(
          (definition) => this.toText(definition.productType).toUpperCase() === defaultProductType,
        )
        ?? null;

      this.form.contractType = defaultDefinition
        ? this.normalizeContractTypeValue(defaultDefinition.name)
        : defaultProductType;
    }

    this.normalizeSelectedContractType();
    this.syncDerivedConfiguration();
    this.syncDocumentProductSelections();
  }

  private getDefaultProductTypeCode(): string {
    if (this.productTypeLoadError !== '') {
      return '';
    }

    const available = this.availableProductTypeCodes;

    if (available.length === 0) {
      return '';
    }

    return [...available].sort((left, right) => this.compareProductTypeCodes(left, right))[0] ?? '';
  }

  private setSignatureDocumentDefinitions(definitions: SolarRentSignatureDocumentConfig[]): void {
    const normalizedDefinitions = definitions.filter(
      (definition, index, values) => definition.key !== '' && values.findIndex((entry) => entry.key === definition.key) === index,
    );
    const allDefinitions = SOLAR_RENT_SINGLE_SIGNATURE_FOR_ALL
      ? normalizedDefinitions
      : [
          ...normalizedDefinitions,
          createSolarRentFinalSignatureDocumentConfig(normalizedDefinitions.length + 1),
        ];
    const existingEntries = { ...this.signatureDocuments };
    const existingAcknowledgedEntries = { ...this.acknowledgedSignatureDocuments };

    this.signatureDocumentDefinitions = allDefinitions;

    for (const key of Object.keys(this.signatureDocuments)) {
      delete this.signatureDocuments[key];
    }
    for (const key of Object.keys(this.acknowledgedSignatureDocuments)) {
      delete this.acknowledgedSignatureDocuments[key];
    }

    for (const definition of allDefinitions) {
      this.signatureDocuments[definition.key] = existingEntries[definition.key] ?? this.createEmptySignatureDocumentState();
      this.acknowledgedSignatureDocuments[definition.key] = existingAcknowledgedEntries[definition.key] === true;
    }

    this.applyPersistedSignatureDocumentGroupVisibility();
    this.applyPersistedSignatureDocumentStates();
  }

  private buildPersistedSignatureDocumentGroupVisibilityPayload(): Record<string, boolean> {
    const payload: Record<string, boolean> = {};

    for (const definition of this.signatureDocumentDefinitions) {
      const groupKey = this.toText(definition.group?.key);
      if (groupKey === '') {
        continue;
      }

      payload[groupKey] = definition.group?.showToClient !== false;
    }

    return payload;
  }

  private buildPersistedDocumentProductSelectionsPayload(): Record<string, string> {
    return PRODUCT_LINKED_DOCUMENT_KEYS.reduce<Record<string, string>>((payload, key) => {
      payload[key] = this.toText(this.documentProductSelections[key]);
      return payload;
    }, {});
  }

  private applyPersistedDocumentProductSelections(value: unknown): void {
    if (!this.isRecord(value)) {
      return;
    }

    for (const key of PRODUCT_LINKED_DOCUMENT_KEYS) {
      const persistedSelection = this.toText(value[key]);
      if (persistedSelection === '') {
        continue;
      }

      this.documentProductSelections[key] = persistedSelection;
    }

    this.syncDocumentProductSelections();
  }

  private extractPersistedSignatureDocumentGroupVisibility(value: unknown): Record<string, boolean> {
    if (!this.isRecord(value)) {
      return {};
    }

    return Object.entries(value).reduce<Record<string, boolean>>((result, [key, rawValue]) => {
      const normalizedKey = this.toText(key);
      if (normalizedKey !== '') {
        result[normalizedKey] = rawValue !== false;
      }

      return result;
    }, {});
  }

  private applyPersistedSignatureDocumentGroupVisibility(): void {
    for (const definition of this.signatureDocumentDefinitions) {
      const groupKey = this.toText(definition.group?.key);
      if (!definition.group || groupKey === '') {
        continue;
      }

      if (Object.prototype.hasOwnProperty.call(this.persistedSignatureDocumentGroupVisibility, groupKey)) {
        definition.group.showToClient = this.persistedSignatureDocumentGroupVisibility[groupKey] !== false;
      }
    }
  }

  private buildPersistedDocumentSignatureStatesPayload(): Record<string, SolarRentPersistedDocumentSignatureState> {
    const payload: Record<string, SolarRentPersistedDocumentSignatureState> = {};

    for (const definition of this.signatureDocumentDefinitions) {
      const entry = this.signatureDocuments[definition.key];
      const acknowledged = this.acknowledgedSignatureDocuments[definition.key] === true;
      const signatureDate = this.toText(entry?.signatureDate);
      const signedSnapshot = entry?.signedSnapshot ?? null;
      const signatureMode = entry?.signatureMode ?? 'draw';
      const drawnSignatureDataUrl = this.toText(entry?.drawnSignatureDataUrl);
      const nicRectoVersoConfirmed = entry?.nicRectoVersoConfirmed === true;
      const hasDrawnSignature = drawnSignatureDataUrl !== '';
      const hasUploadedSignatureFile = !!entry?.signatureUploadFile;
      const hasUploadedNicFiles = (entry?.nicFiles?.length ?? 0) > 0;
      const hasSavedSignedDocumentFile = !!entry?.signedDocumentFile;
      const hadPersistedState = Object.prototype.hasOwnProperty.call(this.persistedDocumentSignatureStates, definition.key);

      const isCompletelyEmpty = !acknowledged && signatureDate === '' && !signedSnapshot && !hasDrawnSignature && !hasUploadedSignatureFile && !hasUploadedNicFiles && !hasSavedSignedDocumentFile;
      if (isCompletelyEmpty && !hadPersistedState) {
        continue;
      }

      payload[definition.key] = {
        acknowledged,
        completed: !!entry?.signedSnapshot,
        signatureDate,
        signedSnapshot,
        signatureMode,
        drawnSignatureDataUrl,
        signatureUploadFile: this.serializeStoredFileEntry(entry?.signatureUploadFile ?? null),
        nicFiles: (entry?.nicFiles ?? [])
          .map((storedFile) => this.serializeStoredFileEntry(storedFile))
          .filter((storedFile): storedFile is SolarRentPersistedStoredFile => storedFile !== null),
        nicRectoVersoConfirmed,
        signedDocumentFile: this.serializeStoredFileEntry(entry?.signedDocumentFile ?? null),
      };
    }

    return payload;
  }

  private extractPersistedDocumentSignatureStates(
    value: unknown,
  ): Record<string, SolarRentPersistedDocumentSignatureState> {
    if (!this.isRecord(value)) {
      return {};
    }

    const states: Record<string, SolarRentPersistedDocumentSignatureState> = {};

    for (const [key, rawState] of Object.entries(value)) {
      if (!this.isRecord(rawState)) {
        continue;
      }

      states[key] = {
        acknowledged: rawState['acknowledged'] === true,
        completed: rawState['completed'] === true,
        signatureDate: this.toText(rawState['signatureDate']),
        signedSnapshot: this.extractPersistedSignedSnapshot(rawState['signedSnapshot']),
        signatureMode: rawState['signatureMode'] === 'upload' ? 'upload' : 'draw',
        drawnSignatureDataUrl: this.toText(rawState['drawnSignatureDataUrl']),
        signatureUploadFile: this.extractPersistedStoredFile(rawState['signatureUploadFile']),
        nicFiles: this.extractPersistedStoredFiles(rawState['nicFiles']),
        nicRectoVersoConfirmed: rawState['nicRectoVersoConfirmed'] === true,
        signedDocumentFile: this.extractPersistedStoredFile(rawState['signedDocumentFile']),
      };
    }

    return states;
  }

  private extractPersistedSignedSnapshot(value: unknown): SolarRentSignedDocumentSnapshot | null {
    if (!this.isRecord(value)) {
      return null;
    }

    return {
      title: this.toText(value['title']),
      fileName: this.toText(value['fileName']),
      contractId: this.toText(value['contractId']),
      fileReference: this.toText(value['fileReference']),
      tenantName: this.toText(value['tenantName']),
      tenantIdentityNumber: this.toText(value['tenantIdentityNumber']) || this.toText(value['tenantNicBrn']),
      tenantAddress: this.toText(value['tenantAddress']),
      siteAddress: this.toText(value['siteAddress']),
      generatedAt: this.toText(value['generatedAt']),
      signedDate: this.toText(value['signedDate']),
      qrCodeData: this.toText(value['qrCodeData']),
      qrCodeImageUrl: this.toText(value['qrCodeImageUrl']),
      nicImageUrls: Array.isArray(value['nicImageUrls'])
        ? value['nicImageUrls']
            .map((entry) => this.normalizeRemoteUrl(this.toText(entry)))
            .filter((entry) => entry !== '')
        : [],
      signatureImageUrl: this.normalizeRemoteUrl(this.toText(value['signatureImageUrl'])),
    };
  }

  private extractPersistedStoredFile(value: unknown): SolarRentPersistedStoredFile | null {
    if (!this.isRecord(value)) {
      return null;
    }

    const url = this.normalizeRemoteUrl(this.toText(value['url']));
    if (url === '') {
      return null;
    }

    return {
      name: this.toText(value['name']) || this.resolveRemoteDocumentName('', url, 'signature-asset'),
      url,
      mime_type: this.toText(value['mime_type']) || null,
    };
  }

  private extractPersistedStoredFiles(value: unknown): SolarRentPersistedStoredFile[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((entry) => this.extractPersistedStoredFile(entry))
      .filter((entry): entry is SolarRentPersistedStoredFile => entry !== null);
  }

  private applyPersistedSignatureDocumentStates(): void {
    for (const [key, persistedState] of Object.entries(this.persistedDocumentSignatureStates)) {
      const entry = this.signatureDocuments[key];
      const definition = this.signatureDocumentDefinitions.find((item) => item.key === key);
      if (!entry) {
        continue;
      }

      this.acknowledgedSignatureDocuments[key] = persistedState.acknowledged;
      entry.signatureMode = persistedState.signatureMode === 'upload' ? 'upload' : 'draw';
      entry.drawnSignatureDataUrl = this.toText(persistedState.drawnSignatureDataUrl);
      entry.signatureDate = persistedState.signatureDate;
      entry.signatureUploadFile = this.createStoredFileEntryFromPersisted(persistedState.signatureUploadFile);
      entry.nicFiles = persistedState.nicFiles
        ? persistedState.nicFiles
            .map((storedFile) => this.createStoredFileEntryFromPersisted(storedFile))
            .filter((storedFile): storedFile is SolarRentStoredFile => storedFile !== null)
        : [];
      entry.nicRectoVersoConfirmed = persistedState.nicRectoVersoConfirmed === true && entry.nicFiles.length > 0;
      entry.signedSnapshot = persistedState.signedSnapshot;
      entry.signedDocumentFile = this.createStoredFileEntryFromPersisted(persistedState.signedDocumentFile);

      if (!entry.signedSnapshot && persistedState.completed && definition && definition.type === 'document' && !definition.needUserSignature) {
        entry.signedSnapshot = {
          title: definition.label,
          fileName: definition.fileName,
          contractId: this.toText(this.loadedContractId),
          fileReference: this.form.contractDetails.fileReference,
          tenantName: this.form.contractDetails.parties.tenant.name,
          tenantIdentityNumber: this.form.contractDetails.parties.tenant.identityNumber,
          tenantAddress: this.form.contractDetails.parties.tenant.address,
          siteAddress: this.siteCompleteAddress,
          generatedAt: persistedState.signatureDate,
          signedDate: persistedState.signatureDate,
          qrCodeData: '',
          qrCodeImageUrl: '',
          nicImageUrls: [],
          signatureImageUrl: '',
        };
      }
    }
  }

  private getSelectedRangeOption(): SolarRentOption | null {
    const storedRange = this.toText(this.form.contractDetails.referenceConfig.range);
    const selectedByStoredText = this.srhRangeOptions.find(({ key, label }) => {
      return storedRange !== '' && (storedRange === key || storedRange === label);
    });

    if (selectedByStoredText) {
      return selectedByStoredText;
    }

    const normalizedContractType = this.resolveContractTypeKeyFromValue(this.form.contractType);
    const selectedByStoredValue = this.srhRangeOptions.find(({ key }) => {
      return normalizedContractType !== ''
        && normalizedContractType === this.resolveContractReferencePrefixFromRangeKey(key);
    });

    if (selectedByStoredValue) {
      return selectedByStoredValue;
    }

    return null;
  }

  private getSelectedCommercialReferenceOption(): SolarRentOption | null {
    const storedReference = this.toText(this.form.contractDetails.referenceConfig.commercialReference).toUpperCase();
    return this.srhCommercialReferenceOptions.find(({ key, label }) => {
      return storedReference !== '' && (storedReference === key.toUpperCase() || storedReference === label.toUpperCase());
    }) ?? null;
  }

  private resolveCommercialReferenceLabel(key: string): string {
    return this.srhCommercialReferenceOptions.find((option) => option.key === key)?.label ?? '';
  }

  private resolveReferenceTextValue(
    rawValue: unknown,
    options: SolarRentOption[],
    fallbackValue = '',
  ): string {
    const directValue = this.toText(rawValue);
    if (directValue !== '') {
      return directValue;
    }

    if (this.isRecord(rawValue)) {
      const selectedOption = options.find((option) => rawValue[option.key] === true);
      if (selectedOption) {
        return selectedOption.label;
      }
    }

    return fallbackValue;
  }

  private extractSignatureDocumentDefinition(
    value: unknown,
    index: number,
  ): SolarRentSignatureDocumentConfig | null {
    if (!this.isRecord(value)) {
      return null;
    }

    const code = this.firstText(value['code']);
    const label = this.firstText(value['name']);
    const description = this.firstText(value['description']);
    const semanticKey = this.firstText(value['key']);
    const group = this.isRecord(value['group']) ? value['group'] : null;
    const activeVersion = this.isRecord(value['active_version']) ? value['active_version'] : null;
    const pdfTemplateKey = this.firstText(activeVersion?.['pdf_template_key']);
    const fileName = this.firstText(activeVersion?.['pdf_template_name']);
    const idSource = this.firstText(code, fileName, label);

    if (idSource === '' || label === '') {
      return null;
    }

    const key = buildSolarRentSignatureDocumentId(idSource);

    return {
      index,
      group: group ? {
        key: this.firstText(group['code'], group['label'], key).toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        label: this.firstText(group['label']),
        title: this.firstText(group['title']),
        code: this.firstText(group['code']),
        showToClient: group['show_to_client'] !== false,
      } : null,
      key,
      groupKey: this.firstText(group?.['code'], value['slug'], key),
      semanticKey,
      code,
      pdfTemplateKey,
      label,
      needUserSignature: value['need_user_signature'] === true,
      fileName,
      description,
      step: 200 + index,
      title: label,
      subSelected: key,
      previewUrl: fileName ? buildSolarRentSignatureDocumentPreviewUrl(fileName) : '',
      type: 'document',
    };
  }

  private createEmptySignatureDocumentState(): SolarRentSignatureDocumentState {
    return {
      signatureMode: 'draw',
      nicFiles: [],
      nicRectoVersoConfirmed: false,
      signatureUploadFile: null,
      drawnSignatureDataUrl: '',
      signatureDate: '',
      signedSnapshot: null,
      signedDocumentFile: null,
    };
  }

  loadCurrentSignatureDocumentTemplates(): void {
    if (!this.lastSignatureDocumentBaseUrl) {
      return;
    }

    this.loadSignatureDocumentTemplates(
      this.lastSignatureDocumentBaseUrl,
      this.lastSignatureDocumentToken,
      this.form.contractType,
    );
  }

  private extractContractTypeDefinition(value: unknown): SolarRentContractTypeDefinition | null {
    if (typeof value === 'string') {
      const name = this.toText(value);
      return name === '' ? null : {
        id: null,
        name,
        code: name,
        label: name,
        productType: this.resolveProductTypeCodeFromValue(name),
        electricalEnvironment: '',
        productData: null,
      };
    }

    if (!this.isRecord(value)) {
      return null;
    }

    const name = this.firstText(
      value['name'],
      value['designation'],
      value['product_name'],
      value['code'],
    );

    if (name === '') {
      return null;
    }

    const productTypeSource = this.isRecord(value['product_type'])
      ? value['product_type']
      : (this.isRecord(value['product_type_item']) ? value['product_type_item'] : null);
    const productData = this.extractProductData(value['product_data'])
      ?? this.extractProductData(productTypeSource?.['product_data']);
    const productType = this.resolveExistingProductTypeCode(
      this.firstText(
        productTypeSource?.['code'],
        productTypeSource?.['name'],
        value['product_type_code'],
        value['product_type_name'],
        value['product_type'],
        this.resolveProductTypeCodeFromValue(name),
      ),
    );
    const electricalEnvironment = this.normalizeElectricalEnvironmentCode(
      this.firstText(
        value['electrical_environment_code'],
        value['electrical_environment'],
        value['electrical_environnement'],
        value['environment'],
      ),
    );

    return {
      id: this.toNumber(value['id']),
      name,
      code: this.firstText(value['code'], value['designation'], name),
      label: this.firstText(productData?.label, value['label'], name),
      productType,
      electricalEnvironment,
      productData,
    };
  }

  private normalizeApiEndpoint(value: unknown): string {
    const endpoint = this.toText(value);

    if (endpoint === '') {
      return '';
    }

    return `/${endpoint.replace(/^\/+/, '').replace(/\/+$/, '')}`;
  }

  private resolveContractsApiBaseUrl(baseUrl: string): string {
    const normalizedBaseUrl = String(baseUrl).trim().replace(/\/+$/, '');
    const configuredApiUrl = String(environment.apiUrl ?? '').trim().replace(/\/+$/, '');
    const configuredApiEndpoint = this.normalizeApiEndpoint(environment.apiEndpoint);
    const configuredRequestBaseUrl = `${configuredApiUrl}${configuredApiEndpoint}`;

    if (configuredRequestBaseUrl !== '') {
      return configuredRequestBaseUrl;
    }

    if (normalizedBaseUrl === '') {
      return '';
    }

    if (/\/api(?:\/v\d+)?$/i.test(normalizedBaseUrl)) {
      return normalizedBaseUrl;
    }

    return `${normalizedBaseUrl}/api`;
  }

  private resolveCoreApiBaseUrl(baseUrl: string): string {
    const requestBaseUrl = String(baseUrl);

    if (requestBaseUrl === '') {
      return '';
    }

    if (/\/api$/i.test(requestBaseUrl)) {
      return requestBaseUrl;
    }

    return `${requestBaseUrl}/api`;
  }

  private serializeStoredFileEntry(file: SolarRentStoredFile | null): SolarRentPersistedStoredFile | null {
    if (!file) {
      return null;
    }

    const url = this.toText(file.url);
    if (url === '') {
      return null;
    }

    return {
      name: this.toText(file.name),
      url,
      mime_type: file.file?.type || this.guessMimeTypeFromName(file.name),
    };
  }

  private createStoredFileEntryFromPersisted(file: SolarRentPersistedStoredFile | null | undefined): SolarRentStoredFile | null {
    if (!file) {
      return null;
    }

    const url = this.normalizeRemoteUrl(file.url);
    if (url === '') {
      return null;
    }

    const name = this.toText(file.name) || this.resolveRemoteDocumentName('', url, 'signature-asset');
    const mimeType = this.toText(file.mime_type) || this.guessMimeTypeFromName(name) || 'application/octet-stream';

    return {
      name,
      url,
      file: new File([new Blob([], { type: mimeType })], name, { type: mimeType }),
    };
  }

  private createRemoteStoredFileEntry(value: unknown, fallbackFile: File): SolarRentStoredFile | null {
    if (!this.isRecord(value)) {
      return null;
    }

    const url = this.normalizeRemoteUrl(this.firstText(value['url'], value['path']));
    if (url === '') {
      return null;
    }

    const name = this.firstText(value['name'], fallbackFile.name, this.resolveRemoteDocumentName('', url, 'signature-asset'));
    const mimeType = this.firstText(value['mime_type'], fallbackFile.type) || this.guessMimeTypeFromName(name) || 'application/octet-stream';

    return {
      name,
      url,
      file: new File([new Blob([], { type: mimeType })], name, { type: mimeType }),
    };
  }

  private createLatestRemoteStoredFileEntry(value: unknown, fallbackFile: File): SolarRentStoredFile | null {
    const entries = Array.isArray(value) ? value : value ? [value] : [];

    for (let index = entries.length - 1; index >= 0; index -= 1) {
      const entry = this.createRemoteStoredFileEntry(entries[index], fallbackFile);
      if (entry) {
        return entry;
      }
    }

    return null;
  }

  private extractProductData(value: unknown): SolarRentProductData | null {
    if (!this.isRecord(value)) {
      return null;
    }

    const label = this.firstText(
      value['label'],
      value['name'],
      value['product_label'],
    );
    const rangeKey = this.firstText(
      value['rangeKey'],
      value['range_key'],
    );
    const commercialReferenceKey = this.firstText(
      value['commercialReferenceKey'],
      value['commercial_reference_key'],
    );
    const batteryCapacity = this.firstText(
      value['batteryCapacity'],
      value['battery_capacity'],
    );
    const batteryUnitCount = this.firstText(
      value['batteryUnitCount'],
      value['battery_unit_count'],
    );
    const panelCount = this.firstText(
      value['panelCount'],
      value['panel_count'],
    );
    const panelUnitPower = this.firstText(
      value['panelUnitPower'],
      value['panel_unit_power'],
    );
    const totalPvPower = this.firstText(
      value['totalPvPower'],
      value['total_pv_power'],
    );
    const inverterPower = this.firstText(
      value['inverterPower'],
      value['inverter_power'],
    );
    const contractYears = this.firstText(
      value['contractYears'],
      value['contract_years'],
    );

    if (
      label === ''
      && rangeKey === ''
      && commercialReferenceKey === ''
      && batteryCapacity === ''
      && panelCount === ''
      && totalPvPower === ''
      && inverterPower === ''
    ) {
      return null;
    }

    return {
      label,
      rangeKey,
      commercialReferenceKey,
      batteryCapacity,
      batteryUnitCount,
      panelCount,
      panelUnitPower,
      totalPvPower,
      inverterPower,
      maintenanceSemiAnnual: value['maintenanceSemiAnnual'] === true || value['maintenance_semi_annual'] === true,
      monitoringIncluded: value['monitoringIncluded'] === true || value['monitoring_included'] === true,
      hotlineIncluded: value['hotlineIncluded'] === true || value['hotline_included'] === true,
      batteryReplacementGuaranteed: value['batteryReplacementGuaranteed'] === true || value['battery_replacement_guaranteed'] === true,
      contractYears,
      cebNetMeteringIncluded: value['cebNetMeteringIncluded'] === true || value['ceb_net_metering_included'] === true,
      annualAuditIncluded: value['annualAuditIncluded'] === true || value['annual_audit_included'] === true,
    };
  }

  private buildCommercialReferenceOptions(
    definition: SolarRentContractTypeDefinition,
  ): SolarRentOption[] {
    const productData = definition.productData;

    if (!productData?.rangeKey) {
      return [];
    }

    return (['SP', 'TP'] as const).map((environmentCode) => ({
      key: this.buildCommercialReferenceKey(productData, environmentCode),
      label: `${definition.label} ${environmentCode}`,
    }));
  }

  private buildCommercialReferenceKey(
    productData: SolarRentProductData | null | undefined,
    environmentCode: 'SP' | 'TP',
  ): string {
    if (!productData?.rangeKey) {
      return '';
    }

    if (environmentCode === 'SP' && productData.commercialReferenceKey) {
      return productData.commercialReferenceKey;
    }

    return `${productData.rangeKey}_${environmentCode.toLowerCase()}`;
  }

  private findProductTypeOption(productTypeCode: string): SolarRentProductTypeOption | null {
    const normalizedProductTypeCode = this.normalizeProductTypeCode(productTypeCode);

    if (normalizedProductTypeCode === '') {
      return null;
    }

    return this.productTypeOptions.find(
      (option) => this.normalizeProductTypeCode(option.code) === normalizedProductTypeCode,
    ) ?? null;
  }

  private findProductTypeOptionByValue(value: string): SolarRentProductTypeOption | null {
    const normalizedValue = this.normalizeProductSelectionKey(value);

    if (normalizedValue === '') {
      return null;
    }

    return this.productTypeOptions.find((option) => {
      return this.normalizeProductSelectionKey(option.code) === normalizedValue
        || this.normalizeProductSelectionKey(option.name) === normalizedValue;
    }) ?? null;
  }

  private resolveProductTypeProductData(productTypeCode: string): SolarRentProductData | null {
    return this.findProductTypeOption(productTypeCode)?.productData ?? null;
  }

  private enrichContractTypeDefinition(
    definition: SolarRentContractTypeDefinition,
  ): SolarRentContractTypeDefinition {
    const fallbackProductTypeOption = this.findProductTypeOption(definition.productType);
    const productData = definition.productData ?? fallbackProductTypeOption?.productData ?? null;
    const label = this.firstText(
      productData?.label,
      definition.label,
      fallbackProductTypeOption?.name,
      definition.name,
    );

    if (productData === definition.productData && label === definition.label) {
      return definition;
    }

    return {
      ...definition,
      label,
      productData,
    };
  }

  private findContractTypeDefinition(contractType: string): SolarRentContractTypeDefinition | null {
    const normalizedContractType = this.toText(contractType);
    const normalizedSelectionKey = this.normalizeProductSelectionKey(normalizedContractType);
    const resolvedProductTypeCode = this.resolveProductTypeCodeFromValue(normalizedContractType);

    const definition = this.contractTypeDefinitions.find(
      ({ name, code, label, productType }) => (
        name === normalizedContractType
        || code === normalizedContractType
        || label === normalizedContractType
        || productType === normalizedContractType
        || this.normalizeProductSelectionKey(name) === normalizedSelectionKey
        || this.normalizeProductSelectionKey(code) === normalizedSelectionKey
        || this.normalizeProductSelectionKey(label) === normalizedSelectionKey
        || this.normalizeProductSelectionKey(productType) === normalizedSelectionKey
        || (resolvedProductTypeCode !== '' && productType === resolvedProductTypeCode)
      ),
    ) ?? null;

    return definition ? this.enrichContractTypeDefinition(definition) : null;
  }

  private normalizeContractTypeValue(contractType: string): string {
    const normalizedContractType = this.toText(contractType);
    return this.findContractTypeDefinition(normalizedContractType)?.name ?? normalizedContractType;
  }

  private normalizeSelectedContractType(): void {
    const normalizedCurrentType = this.normalizeContractTypeValue(this.form.contractType);

    this.form.contractType = this.contractTypeOptions.includes(normalizedCurrentType)
      ? normalizedCurrentType
      : (this.contractTypeOptions[0] ?? normalizedCurrentType);
  }

  private resolveSelectedProductName(contractType: string): string {
    return this.findContractTypeDefinition(contractType)?.name ?? this.toText(contractType);
  }

  private resolveSelectedProductTypeCode(contractType: string): string {
    const definitionProductType = this.findContractTypeDefinition(contractType)?.productType;
    if (definitionProductType) {
      return this.resolveExistingProductTypeCode(definitionProductType);
    }

    return this.resolveExistingProductTypeCode(contractType);
  }

  private resolveExistingProductTypeCode(value: string): string {
    const productTypeOption = this.findProductTypeOptionByValue(value);
    if (productTypeOption) {
      return this.toText(productTypeOption.code).toUpperCase();
    }

    const canonicalProductTypeOption = this.findProductTypeOptionByCanonicalParts(value);
    if (canonicalProductTypeOption) {
      return this.toText(canonicalProductTypeOption.code).toUpperCase();
    }

    return this.normalizeProductTypeCode(value);
  }

  private findProductTypeOptionByCanonicalParts(value: string): SolarRentProductTypeOption | null {
    const targetParts = this.extractProductCodeParts(value);
    if (!targetParts) {
      return null;
    }

    return this.productTypeOptions.find((option) => {
      const optionParts = this.extractProductCodeParts(option.code);

      return !!optionParts
        && optionParts.code === targetParts.code
        && optionParts.unit === targetParts.unit;
    }) ?? null;
  }

  private findContractTypeByProductTypeAndEnvironment(
    productTypeCode: string,
    environmentCode: 'SP' | 'TP',
  ): SolarRentContractTypeDefinition | null {
    const normalizedProductType = this.normalizeProductTypeCode(productTypeCode);
    const normalizedEnvironment = this.normalizeElectricalEnvironmentCode(environmentCode);

    if (normalizedProductType === '' || normalizedEnvironment === '') {
      return null;
    }

    return this.contractTypeDefinitions.find(
      (definition) =>
        this.normalizeProductTypeCode(definition.productType) === normalizedProductType
        && this.normalizeElectricalEnvironmentCode(definition.electricalEnvironment) === normalizedEnvironment,
    ) ?? null;
  }

  private normalizeProductSelectionKey(value: string): string {
    return this.toText(value).toUpperCase().replace(/[^A-Z0-9]+/g, '');
  }

  private normalizeContractReferencePrefix(value: string): string {
    return this.toText(value)
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private resolveContractReferencePrefixFromProductType(productTypeCode: string): string {
    return this.buildBrandedContractReferencePrefix(productTypeCode);
  }

  private resolveContractReferencePrefixFromRangeKey(rangeKey: string): string {
    return this.buildBrandedContractReferencePrefix(rangeKey);
  }

  private resolveContractReferencePrefixFromValue(value: string): string {
    return this.buildBrandedContractReferencePrefix(value);
  }

  private resolveProductTypeCodeFromValue(value: string): string {
    const parts = this.extractProductCodeParts(value);
    if (!parts) {
      return '';
    }

    return this.buildCanonicalProductTypeCode(parts.code, parts.unit);
  }

  private normalizeProductTypeCode(value: string): string {
    const resolvedCode = this.resolveProductTypeCodeFromValue(value);
    if (resolvedCode !== '') {
      return resolvedCode;
    }

    return this.toText(value).toUpperCase().replace(/[^A-Z0-9]+/g, '_');
  }

  private normalizeElectricalEnvironmentCode(value: string): 'SP' | 'TP' | '' {
    const normalized = this.normalizeProductSelectionKey(value);
    if (normalized === '') {
      return '';
    }

    if (
      normalized.includes('TP')
      || normalized.includes('TRIPLEPHASE')
      || normalized.includes('THREEPHASE')
    ) {
      return 'TP';
    }

    if (
      normalized.includes('SP')
      || normalized.includes('SINGLEPHASE')
      || normalized.includes('MONOPHASE')
    ) {
      return 'SP';
    }

    return '';
  }

  private extractProductCodeParts(value: string): { code: string; unit: string } | null {
    const normalized = this.normalizeProductSelectionKey(value);
    if (normalized === '') {
      return null;
    }

    const withoutPrefix = normalized.replace(/^(SOLARRENT|SRH)/, '');
    const match = withoutPrefix.match(/^0*(\d+)([A-Z]*)$/);
    if (!match) {
      return null;
    }

    const numericCode = this.toText(match[1]).replace(/^0+(?=\d)/, '');
    if (numericCode === '') {
      return null;
    }

    return {
      code: numericCode,
      unit: this.normalizeProductCodeUnit(match[2] ?? ''),
    };
  }

  private normalizeProductCodeUnit(unit: string): string {
    const normalized = this.toText(unit).toUpperCase();

    if (normalized === '' || normalized === 'KW' || normalized === 'KWH') {
      return 'KWH';
    }

    return normalized;
  }

  private buildCanonicalProductTypeCode(code: string, unit: string): string {
    const normalizedCode = this.toText(code).replace(/^0+(?=\d)/, '');
    if (normalizedCode === '') {
      return '';
    }

    const paddedCode = normalizedCode.length === 1 ? `0${normalizedCode}` : normalizedCode;

    return `${paddedCode}_${this.normalizeProductCodeUnit(unit)}`;
  }

  private detectContractReferenceBrand(normalized: string): string {
    if (normalized.startsWith('SRC')) {
      return 'SRC';
    }

    if (normalized.startsWith('SIGEN')) {
      return 'SIGEN';
    }

    return 'SRH';
  }

  private isContractReferenceUnitToken(token: string): boolean {
    return /^(SP|TP|KW|KWH)$/.test(this.normalizeProductCodeUnit(token));
  }

  private buildContractReferenceBrandPrefix(brandToken: string): string {
    if (brandToken === '' || brandToken === 'SOLARRENT') {
      return 'SRH';
    }

    return brandToken;
  }

  private buildBrandedContractReferencePrefix(value: string): string {
    const normalized = this.toText(value)
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    if (normalized === '') {
      return '';
    }

    let brandToken = '';
    let unitToken = '';
    let numericCode = '';
    const series: string[] = [];

    for (const token of normalized.split('_')) {
      if (/^\d+$/.test(token)) {
        numericCode += token.replace(/^0+(?=\d)/, '');
        continue;
      }

      const inlineMatch = token.match(/^(\d+)([A-Z]*)$/);
      if (inlineMatch) {
        numericCode += inlineMatch[1].replace(/^0+(?=\d)/, '');
        if (unitToken === '') {
          unitToken = inlineMatch[2];
        }
        continue;
      }

      if (!/^[A-Z]+$/.test(token)) {
        return '';
      }

      if (brandToken === '' && !this.isContractReferenceUnitToken(token)) {
        brandToken = token;
        continue;
      }

      if (unitToken === '' && this.isContractReferenceUnitToken(token)) {
        unitToken = token;
        continue;
      }

      series.push(token);
    }

    if (numericCode === '') {
      return '';
    }

    return [
      this.buildContractReferenceBrandPrefix(brandToken),
      ...series,
      `${numericCode}${this.normalizeProductCodeUnit(unitToken)}`,
    ]
      .filter((part) => part !== '')
      .join('-');
  }

  private compareProductTypeCodes(left: string, right: string): number {
    const leftParts = this.extractProductCodeParts(left);
    const rightParts = this.extractProductCodeParts(right);

    if (leftParts && rightParts) {
      const leftNumericCode = Number(leftParts.code);
      const rightNumericCode = Number(rightParts.code);

      if (leftNumericCode !== rightNumericCode) {
        return leftNumericCode - rightNumericCode;
      }

      return leftParts.unit.localeCompare(rightParts.unit);
    }

    if (leftParts) {
      return -1;
    }

    if (rightParts) {
      return 1;
    }

    return left.localeCompare(right);
  }

  private toNumber(value: unknown): number | null {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private syncBooleanOptionGroupKeys(target: Record<string, boolean>, keys: string[]): void {
    const nextValues = new Set(keys);

    for (const key of Object.keys(target)) {
      if (!nextValues.has(key)) {
        delete target[key];
      }
    }

    for (const key of keys) {
      target[key] = target[key] === true;
    }
  }

  private getSelectedEnvironmentCode(): 'SP' | 'TP' {
    return this.form.contractDetails.parties.site.threePhaseEnvironment ? 'TP' : 'SP';
  }

  private getAvailableDocumentProductDefinitions(): SolarRentContractTypeDefinition[] {
    const selectedProductType = this.normalizeProductTypeCode(this.selectedProductTypeCode);
    const selectedEnvironment = this.normalizeElectricalEnvironmentCode(this.getSelectedEnvironmentCode());

    const strictMatches = this.contractTypeDefinitions.filter(
      (definition) =>
        this.normalizeProductTypeCode(definition.productType) === selectedProductType
        && this.normalizeElectricalEnvironmentCode(definition.electricalEnvironment) === selectedEnvironment,
    );

    if (strictMatches.length > 0) {
      return strictMatches;
    }

    const productTypeMatches = this.contractTypeDefinitions.filter(
      (definition) =>
        this.normalizeProductTypeCode(definition.productType) === selectedProductType,
    );

    if (productTypeMatches.length > 0) {
      return productTypeMatches;
    }

    if (this.contractTypeDefinitions.length > 0) {
      return this.contractTypeDefinitions;
    }

    const fallbackProductName = this.toText(this.form.contractType);
    if (fallbackProductName === '') {
      return [];
    }

    return [
      {
        id: null,
        name: fallbackProductName,
        code: fallbackProductName,
        label: fallbackProductName,
        productType: this.normalizeProductTypeCode(this.selectedProductTypeCode),
        electricalEnvironment: this.normalizeElectricalEnvironmentCode(this.getSelectedEnvironmentCode()),
        productData: null,
      },
    ];
  }

  private buildDocumentSelectionScopeSignature(): string {
    const selectedProductDefinition = this.findContractTypeDefinition(this.form.contractType);
    const selectedProductId = selectedProductDefinition?.id;
    const selectedProductName = this.normalizeProductSelectionKey(
      selectedProductDefinition?.name ?? this.toText(this.form.contractType),
    );

    return [
      this.normalizeProductTypeCode(
        selectedProductDefinition?.productType ?? this.selectedProductTypeCode,
      ),
      this.normalizeElectricalEnvironmentCode(
        selectedProductDefinition?.electricalEnvironment ?? this.getSelectedEnvironmentCode(),
      ),
      selectedProductId !== null && selectedProductId !== undefined ? String(selectedProductId) : '',
      selectedProductName,
    ].join('|');
  }

  private updateDocumentSelectionScope(mutator: () => void): void {
    const previousScopeSignature = this.buildDocumentSelectionScopeSignature();

    mutator();

    const nextScopeSignature = this.buildDocumentSelectionScopeSignature();
    if (previousScopeSignature !== nextScopeSignature) {
      this.pendingDocumentSelectionScopeChange = true;
    }

    this.syncDocumentProductSelections();
  }

  private applyPendingDocumentSelectionScopeChange(): SolarRentDeferredDocumentScopeSnapshot | null {
    if (!this.pendingDocumentSelectionScopeChange) {
      return null;
    }

    this.pendingDocumentSelectionScopeChange = false;

    if (!this.shouldReapplyDocumentDefaultsOnSelectionChange()) {
      return null;
    }

    const snapshot = this.captureDeferredDocumentSelectionScopeSnapshot();

    this.resetContractDocuments();
    this.applyCurrentContractTypeDefaultDocuments();
    this.hasManualDocumentEdits = false;

    return snapshot;
  }

  private captureDeferredDocumentSelectionScopeSnapshot(): SolarRentDeferredDocumentScopeSnapshot {
    const documentFiles: Record<string, SolarRentStoredFile[]> = {};

    for (const definition of this.contractDocumentDefinitions) {
      documentFiles[definition.key] = (this.documentFiles[definition.key] ?? []).map((entry) => ({
        ...entry,
      }));
    }

    const documentProductSelections = PRODUCT_LINKED_DOCUMENT_KEYS.reduce<
      Partial<Record<ProductLinkedDocumentKey, string>>
    >((carry, key) => {
      carry[key] = this.toText(this.documentProductSelections[key]);
      return carry;
    }, {});

    return {
      documentFiles,
      documentProductSelections,
      otherDetails: this.toText(this.form.contractDetails.documents.otherDetails),
      documentsResetSinceTypeChange: this.documentsResetSinceTypeChange,
      documentsChanged: this.documentsChanged,
      hasManualDocumentEdits: this.hasManualDocumentEdits,
    };
  }

  private restoreDeferredDocumentSelectionScopeChange(
    snapshot: SolarRentDeferredDocumentScopeSnapshot | null,
  ): void {
    if (!snapshot) {
      return;
    }

    for (const definition of this.contractDocumentDefinitions) {
      this.documentFiles[definition.key] = this.replaceFileEntries(
        this.documentFiles[definition.key],
        snapshot.documentFiles[definition.key] ?? [],
      );
    }

    for (const key of PRODUCT_LINKED_DOCUMENT_KEYS) {
      this.documentProductSelections[key] = this.toText(snapshot.documentProductSelections[key]);
    }

    this.form.contractDetails.documents.otherDetails = snapshot.otherDetails;
    this.documentsResetSinceTypeChange = snapshot.documentsResetSinceTypeChange;
    this.documentsChanged = snapshot.documentsChanged;
    this.hasManualDocumentEdits = snapshot.hasManualDocumentEdits;
    this.pendingDocumentSelectionScopeChange = true;
  }

  private applySelectedChecklistDefaultDocument(
    key: ProductLinkedDocumentKey,
    selectedValue: string,
  ): void {
    if (!this.documentChecklistDefaults) {
      return;
    }

    const selectedProductDefinition = this.findDocumentProductDefinition(
      this.toNumber(selectedValue),
      selectedValue,
    );
    const scope = this.findChecklistDocumentDefaultsScope(
      this.documentChecklistDefaults,
      selectedProductDefinition,
      selectedValue,
    );
    const scoped = scope?.value;
    const rawEntries = scoped?.[key];
    const entries = Array.isArray(rawEntries) ? rawEntries : rawEntries ? [rawEntries] : [];
    const normalized = this.normalizeChecklistDefaultDocumentEntries(entries, key);

    if (scope && this.isChecklistDefaultDocumentDismissed(key, scope.signature)) {
      this.documentFiles[key] = this.replaceFileEntries(this.documentFiles[key], []);
      this.documentProductSelections[key] = selectedValue;
      return;
    }

    this.documentFiles[key] = this.replaceFileEntries(this.documentFiles[key], normalized);
    this.documentProductSelections[key] = selectedValue;
  }

  private rememberDismissedChecklistDefaultDocument(key: ProductLinkedDocumentKey): void {
    const scopeSignature = this.resolveCurrentChecklistDefaultDocumentScopeSignature(key);
    if (scopeSignature === '') {
      return;
    }

    const existing = this.dismissedChecklistDefaultDocumentScopes[key] ?? [];
    if (existing.includes(scopeSignature)) {
      return;
    }

    this.dismissedChecklistDefaultDocumentScopes = {
      ...this.dismissedChecklistDefaultDocumentScopes,
      [key]: existing.concat(scopeSignature),
    };
  }

  private resolveCurrentChecklistDefaultDocumentScopeSignature(key: ProductLinkedDocumentKey): string {
    if (!this.documentChecklistDefaults) {
      return '';
    }

    const selectedValue = this.toText(this.documentProductSelections[key]);
    const selectedProductDefinition = this.findDocumentProductDefinition(
      this.toNumber(selectedValue),
      selectedValue,
    );

    return this.findChecklistDocumentDefaultsScope(
      this.documentChecklistDefaults,
      selectedProductDefinition,
      selectedValue,
    )?.signature ?? '';
  }

  private isChecklistDefaultDocumentDismissed(
    key: ProductLinkedDocumentKey,
    scopeSignature: string,
  ): boolean {
    return scopeSignature !== ''
      && (this.dismissedChecklistDefaultDocumentScopes[key] ?? []).includes(scopeSignature);
  }

  private buildPersistedDismissedChecklistDefaultDocumentScopesPayload(): Record<string, string[]> {
    return PRODUCT_LINKED_DOCUMENT_KEYS.reduce<Record<string, string[]>>((carry, key) => {
      const scopes = (this.dismissedChecklistDefaultDocumentScopes[key] ?? [])
        .map((scope) => this.toText(scope))
        .filter((scope, index, values) => scope !== '' && values.indexOf(scope) === index);

      carry[key] = scopes;
      return carry;
    }, {});
  }

  private extractPersistedDismissedChecklistDefaultDocumentScopes(
    value: unknown,
  ): Partial<Record<ProductLinkedDocumentKey, string[]>> {
    const source = this.isRecord(value) ? value : {};
    const extracted: Partial<Record<ProductLinkedDocumentKey, string[]>> = {};

    for (const key of PRODUCT_LINKED_DOCUMENT_KEYS) {
      const rawScopes = Array.isArray(source[key]) ? source[key] : [];
      extracted[key] = rawScopes
        .map((scope) => this.toText(scope))
        .filter((scope, index, values) => scope !== '' && values.indexOf(scope) === index);
    }

    return extracted;
  }

  private shouldReapplyDocumentDefaultsOnSelectionChange(): boolean {
    return !this.hasManualDocumentEdits;
  }

  private syncDocumentProductSelections(): void {
    const optionKeys = this.buildSharedDocumentProductOptions().map((option) => option.key);

    for (const key of PRODUCT_LINKED_DOCUMENT_KEYS) {
      const selectedValue = this.toText(this.documentProductSelections[key]);
      const hasFiles = (this.documentFiles[key] ?? []).length > 0;

      if (selectedValue === '') {
        if (hasFiles) {
          this.documentProductSelections[key] = this.resolveDefaultDocumentProductSelection();
        }

        continue;
      }

      if (!optionKeys.includes(selectedValue)) {
        if (hasFiles) {
          this.documentProductSelections[key] = this.resolveDefaultDocumentProductSelection();
        }
      }
    }
  }

  private getSharedDocumentProductAnchorKey(): string | null {
    return this.contractDocumentDefinitions.find(({ key }) => this.isProductLinkedDocumentKey(key))?.key ?? null;
  }

  private resolveDocumentProductSelection(entries: unknown[]): string {
    for (const entry of entries) {
      if (!this.isRecord(entry)) {
        continue;
      }

      const productId = this.toNumber(entry['product_id']);
      const productName = this.firstText(entry['product_name'], entry['product']);
      const productType = this.firstText(entry['product_type']);
      const electricalEnvironment = this.firstText(entry['electrical_environment']);

      const definition = this.findDocumentProductDefinition(
        productId,
        productName,
        productType,
        electricalEnvironment,
      );

      if (definition?.name) {
        return definition.name;
      }

      if (productName !== '') {
        return productName;
      }
    }

    return this.resolveDefaultDocumentProductSelection();
  }

  private resolveDefaultDocumentProductSelection(): string {
    const currentDefinition = this.findContractTypeDefinition(this.form.contractType);
    if (currentDefinition?.name) {
      const hasExactOption = this.getAvailableDocumentProductDefinitions().some(
        (definition) => definition.name === currentDefinition.name,
      );

      if (hasExactOption) {
        return currentDefinition.name;
      }
    }

    const selectedProductType = this.normalizeProductTypeCode(this.selectedProductTypeCode);
    const selectedEnvironment = this.normalizeElectricalEnvironmentCode(this.getSelectedEnvironmentCode());
    const strictMatches = this.contractTypeDefinitions.filter(
      (definition) =>
        this.normalizeProductTypeCode(definition.productType) === selectedProductType
        && this.normalizeElectricalEnvironmentCode(definition.electricalEnvironment) === selectedEnvironment,
    );

    if (strictMatches.length === 1) {
      return strictMatches[0]?.name ?? '';
    }

    const availableDefinitions = this.getAvailableDocumentProductDefinitions();
    if (availableDefinitions.length === 1) {
      return availableDefinitions[0]?.name ?? '';
    }

    return '';
  }

  private findDocumentProductDefinition(
    productId: number | null,
    productName: string,
    productType?: string,
    electricalEnvironment?: string,
  ): SolarRentContractTypeDefinition | null {
    const normalizedProductName = this.toText(productName);
    const normalizedProductType = this.normalizeProductTypeCode(productType ?? '');
    const normalizedEnvironment = this.normalizeElectricalEnvironmentCode(electricalEnvironment ?? '');

    return this.contractTypeDefinitions.find((definition) => {
      if (productId !== null && definition.id === productId) {
        return true;
      }

      if (normalizedProductName !== '' && definition.name !== normalizedProductName) {
        return false;
      }

      if (
        normalizedProductType !== ''
        && this.normalizeProductTypeCode(definition.productType) !== normalizedProductType
      ) {
        return false;
      }

      if (
        normalizedEnvironment !== ''
        && this.normalizeElectricalEnvironmentCode(definition.electricalEnvironment) !== normalizedEnvironment
      ) {
        return false;
      }

      return normalizedProductName !== '';
    }) ?? null;
  }

  private isProductLinkedDocumentKey(key: string): key is ProductLinkedDocumentKey {
    return (PRODUCT_LINKED_DOCUMENT_KEYS as readonly string[]).includes(key);
  }

  private normalizeLegacyKeys(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((entry) => this.normalizeLegacyKeys(entry));
    }

    if (!this.isRecord(value)) {
      return value;
    }

    const normalized: Record<string, unknown> = {};

    for (const [key, entryValue] of Object.entries(value)) {
      const normalizedKey = LEGACY_FORM_KEY_MAP[key] ?? key;
      normalized[normalizedKey] = this.normalizeLegacyKeys(entryValue);
    }

    return normalized;
  }

  private buildAuthorizationHeaders(token?: string | null): HttpHeaders | undefined {
    return token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;
  }

  private unwrapCollectionResponse(response: unknown): unknown[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (!this.isRecord(response)) {
      return [];
    }

    if (Array.isArray(response['data'])) {
      return response['data'];
    }

    if (this.isRecord(response['data']) && Array.isArray(response['data']['data'])) {
      return response['data']['data'];
    }

    if (Array.isArray(response['results'])) {
      return response['results'];
    }

    if (Array.isArray(response['items'])) {
      return response['items'];
    }

    return [];
  }

  private mapUniqueCodeOptions<T extends { code: string }>(
    response: unknown,
    mapper: (value: Record<string, unknown>) => T,
  ): T[] {
    return this.unwrapCollectionResponse(response)
      .filter((value): value is Record<string, unknown> => this.isRecord(value))
      .map((value) => mapper(value))
      .filter((option) => option.code !== '')
      .filter(
        (option, index, values) =>
          values.findIndex((entry) => entry.code === option.code) === index,
      );
  }

  private finalizePendingPurposeStepSnapshotAfterTemplateLoad(): void {
    if (!this.pendingInitialPurposeStepSnapshotAfterTemplateLoad) {
      return;
    }

    this.captureCurrentPurposeStepPayload();
    this.pendingInitialPurposeStepSnapshotAfterTemplateLoad = false;
  }

  private unwrapDefaultSettingsResponse(response: unknown): SolarRentDefaultSettingItem[] {
    if (Array.isArray(response)) {
      return response.filter((item): item is SolarRentDefaultSettingItem => this.isRecord(item));
    }

    if (!this.isRecord(response)) {
      return [];
    }

    const candidate = Array.isArray(response['data'])
      ? response['data']
      : Array.isArray(response['settings'])
        ? response['settings']
        : Array.isArray(response['result'])
          ? response['result']
          : ('section' in response || 'section_name' in response)
            ? [response]
            : [];

    return candidate.filter((item): item is SolarRentDefaultSettingItem => this.isRecord(item));
  }

  private resolveDefaultSettingPartyKey(item: SolarRentDefaultSettingItem): SolarRentPartyKey | null {
    const section = this.firstText(item.section, item.section_name);
    return SOLAR_RENT_PARTY_SECTION_KEYS[section] ?? null;
  }

  private normalizeDefaultSettingValue(value: unknown): Record<string, any> {
    const normalized = this.normalizeLegacyKeys(this.isRecord(value) ? value : {});
    return this.isRecord(normalized) ? normalized : {};
  }

  private applyMissingManagerObservationSignatureDefaults(
    partyDefaults: Record<SolarRentPartyKey, Record<string, any>>,
  ): boolean {
    let insertedAny = false;

    (Object.keys(partyDefaults) as SolarRentPartyKey[]).forEach((partyKey) => {
      if (this.managerObservationSignatureDefaultsInitialized[partyKey] === true) {
        return;
      }

      const signatureTarget = this.form.contractDetails.signatures[partyKey];
      const defaultValues = partyDefaults[partyKey];
      const defaultSignatureValue = this.resolveDefaultSettingSignatureValue(defaultValues);
      let inserted = false;

      if (this.toText(signatureTarget.signature) === '' && defaultSignatureValue !== '') {
        signatureTarget.signature = defaultSignatureValue;
        inserted = true;
      }

      if (this.shouldApplyManagerObservationSignatureTextDefault(partyKey, 'name', signatureTarget.name)) {
        const defaultName = this.toText(defaultValues['representedBy']);
        if (defaultName !== '') {
          signatureTarget.name = defaultName;
          inserted = true;
        }
      }

      if (this.shouldApplyManagerObservationSignatureTextDefault(partyKey, 'title', signatureTarget.title)) {
        const defaultTitle = this.toText(defaultValues['title']);
        if (defaultTitle !== '') {
          signatureTarget.title = defaultTitle;
          inserted = true;
        }
      }

      if (inserted) {
        insertedAny = true;
        this.managerObservationSignatureDefaultsInitialized = {
          ...this.managerObservationSignatureDefaultsInitialized,
          [partyKey]: true,
        };
      }
    });

    return insertedAny;
  }

  private shouldApplyManagerObservationSignatureTextDefault(
    partyKey: SolarRentPartyKey,
    field: 'name' | 'title',
    currentValue: string,
  ): boolean {
    const normalizedCurrentValue = this.toText(currentValue);
    if (normalizedCurrentValue === '') {
      return true;
    }

    return normalizedCurrentValue === this.toText(SOLAR_RENT_SIGNATURE_TEXT_DEFAULT_SETTINGS[partyKey][field]);
  }

  private resolveDefaultSettingSignatureValue(value: Record<string, any>): string {
    const normalizedMode = this.toText(value['signatureMode'] ?? value['signature_mode']).toLowerCase();
    const drawnSignatureValue = this.normalizeRemoteUrl(
      this.toText(value['signatureDrawnDataUrl'] ?? value['signature_drawn_data_url']),
    );
    const uploadedSignatureValue = this.extractDefaultSettingStoredFileReference(
      value['signatureUploadFile'] ?? value['signature_upload_file'],
    );

    if (normalizedMode === 'upload') {
      return uploadedSignatureValue || drawnSignatureValue;
    }

    if (normalizedMode === 'draw') {
      return drawnSignatureValue || uploadedSignatureValue;
    }

    return drawnSignatureValue || uploadedSignatureValue;
  }

  private extractDefaultSettingStoredFileReference(value: unknown): string {
    const entries = Array.isArray(value) ? value : value ? [value] : [];

    for (const entry of entries) {
      if (this.isRecord(entry)) {
        const resolvedUrl = this.normalizeRemoteUrl(
          this.firstText(entry['link'], entry['url'], entry['path']),
        );

        if (resolvedUrl !== '') {
          return resolvedUrl;
        }

        continue;
      }

      const resolvedUrl = this.normalizeRemoteUrl(this.toText(entry));
      if (resolvedUrl !== '') {
        return resolvedUrl;
      }
    }

    return '';
  }

  private buildPersistedManagerObservationSignatureDefaultsStatePayload(): Record<string, boolean> {
    return (Object.keys(SOLAR_RENT_PARTY_DEFAULT_SETTINGS) as SolarRentPartyKey[]).reduce<Record<string, boolean>>(
      (payload, partyKey) => {
        payload[partyKey] = this.managerObservationSignatureDefaultsInitialized[partyKey] === true;
        return payload;
      },
      {},
    );
  }

  private extractPersistedManagerObservationSignatureDefaultsState(
    value: unknown,
    persistedSignatures: unknown,
  ): Partial<Record<SolarRentPartyKey, boolean>> {
    const source = this.isRecord(value) ? value : {};
    const signatures = this.isRecord(persistedSignatures) ? persistedSignatures : {};
    const extracted: Partial<Record<SolarRentPartyKey, boolean>> = {};

    for (const partyKey of Object.keys(SOLAR_RENT_PARTY_DEFAULT_SETTINGS) as SolarRentPartyKey[]) {
      if (source[partyKey] === true) {
        extracted[partyKey] = true;
        continue;
      }

      const persistedSignature = this.isRecord(signatures[partyKey]) ? signatures[partyKey] : null;
      if (!persistedSignature) {
        extracted[partyKey] = false;
        continue;
      }

      const hasPersistedSignatureValue = this.toText(persistedSignature['signature']) !== '';
      const usesCustomName = !this.shouldApplyManagerObservationSignatureTextDefault(
        partyKey,
        'name',
        this.toText(persistedSignature['name']),
      );
      const usesCustomTitle = !this.shouldApplyManagerObservationSignatureTextDefault(
        partyKey,
        'title',
        this.toText(persistedSignature['title']),
      );

      extracted[partyKey] = hasPersistedSignatureValue || usesCustomName || usesCustomTitle;
    }

    return extracted;
  }

  private emitObservationSignatureStateChange(): void {
    this.observationSignatureStateChangesSubject.next();
  }

  private applyBooleanOptionGroup(target: Record<string, boolean>, source: unknown): void {
    for (const key of Object.keys(target)) {
      target[key] = false;
    }

    if (Array.isArray(source)) {
      for (const value of source) {
        const key = String(value ?? '').trim();
        if (key in target) {
          target[key] = true;
        }
      }
      return;
    }

    if (!this.isRecord(source)) {
      return;
    }

    for (const key of Object.keys(target)) {
      target[key] = source[key] === true;
    }
  }

  private mergeDocumentStatusEntries(value: unknown): void {
    if (!this.isRecord(value)) {
      return;
    }

    const target = this.form.contractDetails.documentStatus as unknown as Record<string, unknown>;

    for (const [key, entry] of Object.entries(value)) {
      if (SOLAR_RENT_DOCUMENT_STATUS_META_KEYS.includes(key) || !this.isRecord(entry)) {
        continue;
      }

      if (key === 'other') {
        const inputValue = this.toText(entry['input_value']);
        const details = this.form.contractDetails.documentStatus;
        if (inputValue !== '' && details.otherDetails === '') {
          details.otherDetails = inputValue;
          details.otherValue = inputValue;
        }
      }

      target[key] = this.normalizeDocumentStatusEntry(entry);
    }
  }

  private normalizeDocumentStatusEntry(value: Record<string, unknown>): SolarRentDocumentStatusEntry {
    const entry: SolarRentDocumentStatusEntry = {
      available: value['available'] === true,
      applicable: value['applicable'] === true,
      signed: value['signed'] === true,
      transmitted: value['transmitted'] === true,
    };

    if (typeof value['input_value'] === 'string') {
      entry.input_value = value['input_value'];
    }

    return entry;
  }

  private buildDocumentStatusPayload(): Record<string, unknown> {
    const source = JSON.parse(
      JSON.stringify(this.form.contractDetails.documentStatus),
    ) as Record<string, unknown>;
    const payload: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(source)) {
      if (SOLAR_RENT_DOCUMENT_STATUS_META_KEYS.includes(key) || !this.isRecord(value)) {
        continue;
      }

      payload[key] = this.normalizeDocumentStatusEntry(value);
    }

    const stateDefinitions = this.signatureDocumentDefinitions.filter(({ type }) => type === 'document');
    const definitions = stateDefinitions.length > 0 ? stateDefinitions : this.documentStatusFallbackTemplates;

    for (const row of SOLAR_RENT_DOCUMENT_STATUS_BASELINE_ROWS) {
      
      const definition = definitions.find((candidate) =>
        matchesSolarRentDocumentStatusBaselineRow(row, candidate),
      );
      const storageKey = definition?.key ?? row.id;

      if (this.isRecord(payload[storageKey])) {
        continue;
      }

      payload[storageKey] = {
        available: false,
        applicable: false,
        signed: false,
        transmitted: false,
      };
    }
    for (const definition of definitions) {
      const existing = this.isRecord(payload[definition.key])
        ? payload[definition.key] as Record<string, unknown>
        : null;

      payload[definition.key] = {
        available: true,
        applicable: existing?.['applicable'] === true,
        signed: existing?.['signed'] === true || this.isDocumentSigned(definition.key),
        transmitted: existing?.['transmitted'] === true,
      };
    }

    const otherEntry = this.isRecord(payload['other'])
      ? payload['other'] as Record<string, unknown>
      : null;

    payload['other'] = {
      available: otherEntry?.['available'] === true,
      applicable: otherEntry?.['applicable'] === true,
      signed: otherEntry?.['signed'] === true,
      transmitted: otherEntry?.['transmitted'] === true,
      input_value:
        this.toText(source['otherDetails'])
        || this.toText(source['otherValue'])
        || this.toText(otherEntry?.['input_value']),
    };

    return payload;
  }

  private mergeInto(target: Record<string, any>, source: unknown): void {
    if (!this.isRecord(source)) {
      return;
    }

    for (const [key, value] of Object.entries(source)) {
      if (!(key in target) || value === undefined || Array.isArray(value)) {
        continue;
      }

      if (this.isRecord(value) && this.isRecord(target[key])) {
        if (Object.keys(target[key]).length === 0) {
          target[key] = { ...value };
          continue;
        }

        this.mergeInto(target[key], value);
        continue;
      }

      if (typeof target[key] === 'boolean') {
        target[key] = value === true;
        continue;
      }

      if (value === null) {
        continue;
      }

      target[key] = typeof target[key] === 'string' ? this.toText(value) : value;
    }
  }

  private fillMissingValues(target: Record<string, any>, source: unknown): void {
    if (!this.isRecord(source)) {
      return;
    }

    for (const [key, value] of Object.entries(source)) {
      if (!(key in target) || value === undefined || value === null || Array.isArray(value)) {
        continue;
      }

      if (this.isRecord(value) && this.isRecord(target[key])) {
        this.fillMissingValues(target[key], value);
        continue;
      }

      if (typeof target[key] === 'boolean') {
        if (target[key] !== true) {
          target[key] = value === true;
        }
        continue;
      }

      if (typeof target[key] === 'string') {
        const normalizedValue = this.toText(value);
        if (target[key].trim() === '' && normalizedValue !== '') {
          target[key] = normalizedValue;
        }
        continue;
      }

      if (target[key] === '' || target[key] === null || target[key] === undefined) {
        target[key] = value;
      }
    }
  }

  private overwriteValues(target: Record<string, any>, source: unknown): void {
    if (!this.isRecord(source)) {
      return;
    }

    for (const [key, value] of Object.entries(source)) {
      if (!(key in target) || value === undefined || value === null || Array.isArray(value)) {
        continue;
      }

      if (this.isRecord(value) && this.isRecord(target[key])) {
        this.overwriteValues(target[key], value);
        continue;
      }

      if (typeof target[key] === 'boolean') {
        target[key] = value === true;
        continue;
      }

      if (typeof target[key] === 'string') {
        target[key] = this.toText(value);
        continue;
      }

      target[key] = value;
    }
  }

  private firstText(...values: unknown[]): string {
    for (const value of values) {
      const normalized = this.toText(value);
      if (normalized !== '') {
        return normalized;
      }
    }

    return '';
  }

  private toText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private buildUserDisplayName(user: Record<string, any> | null): string {
    if (!user) {
      return '';
    }

    return [this.toText(user['first_name']), this.toText(user['last_name'])]
      .filter((value) => value !== '')
      .join(' ');
  }

  private isRecord(value: unknown): value is Record<string, any> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private replaceFileEntry(existing: SolarRentStoredFile | null, file: File | null): SolarRentStoredFile | null {
    if (existing?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(existing.url);
    }

    if (!file) {
      return null;
    }

    return {
      file,
      name: file.name,
      url: URL.createObjectURL(file),
    };
  }

  private appendFileEntry(existing: SolarRentStoredFile[] | null | undefined, file: File | null): SolarRentStoredFile[] {
    if (!file) {
      return existing ? [...existing] : [];
    }

    return [
      ...(existing ?? []),
      {
        file,
        name: file.name,
        url: URL.createObjectURL(file),
      },
    ];
  }

  private removeFileEntry(existing: SolarRentStoredFile[] | null | undefined, index = 0): SolarRentStoredFile[] {
    const entries = existing ?? [];
    const target = entries[index];
    if (target?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(target.url);
    }

    return entries.filter((_, entryIndex) => entryIndex !== index);
  }

  private replaceFileEntries(existing: SolarRentStoredFile[] | null | undefined, files: SolarRentStoredFile[]): SolarRentStoredFile[] {
    for (const entry of existing ?? []) {
      if (entry.url?.startsWith('blob:')) {
        URL.revokeObjectURL(entry.url);
      }
    }

    return files.map((entry) => this.cloneStoredFileEntry(entry));
  }

  private replacePendingDocumentUpload(
    existing: SolarRentStoredFile[] | null | undefined,
    uploadedFile: SolarRentStoredFile,
  ): SolarRentStoredFile[] {
    const entries = [...(existing ?? [])];
    const pendingIndex = entries.findIndex(
      (entry) => entry.url?.startsWith('blob:') && this.toText(entry.name) === this.toText(uploadedFile.name),
    );

    if (pendingIndex >= 0) {
      entries.splice(pendingIndex, 1, uploadedFile);

      return this.replaceFileEntries(existing, entries);
    }

    return this.replaceFileEntries(existing, [...entries, uploadedFile]);
  }

  private replaceStoredFileEntry(
    existing: SolarRentStoredFile | null,
    entry: SolarRentStoredFile | null | undefined,
  ): SolarRentStoredFile | null {
    if (existing?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(existing.url);
    }

    if (!entry) {
      return null;
    }

    return this.cloneStoredFileEntry(entry);
  }

  private cloneStoredFileEntry(entry: SolarRentStoredFile): SolarRentStoredFile {
    const url = entry.url?.startsWith('blob:') ? URL.createObjectURL(entry.file) : entry.url;

    return {
      file: entry.file,
      name: entry.name,
      url,
    };
  }
}
